import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Users,
  Wrench,
  LogOut,
  Plus,
  Search,
  Package,
  History,
  CheckCircle,
  Clock,
  XCircle,
  Trash2,
  Briefcase,
  Settings,
  Edit2,
  Save,
  ListPlus,
  Menu,
  UserCircle,
  Loader2,
  Upload,
  Phone,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Building2,
  FolderOpen,
  Calendar,
  MapPin,
  UserPlus,
  Euro,
  FileText,
  Download,
  RotateCcw,
  BarChart3,
  Sparkles,
} from "lucide-react";
import * as XLSX from "xlsx";
import { Modal, StatusBadge, AddressInput, LowStockAlert, PulseBadge, ProfileBubble, ProductTour } from "../ui";
import type { AddressInputHandle } from "../ui/AddressInput";
import type { ProductTourStep } from "../ui/ProductTour";
import { TeamManager } from "../TeamManager";
import { SettingsPage } from "../SettingsPage";
import { DossierNotes } from "../DossierNotes";
import { SignedQuoteUpload } from "../commercial/SignedQuoteUpload";
import { JobsCategoryHub } from "../JobsCategoryHub";
import { JobsDossierTypeHub } from "../JobsDossierTypeHub";
import { AdminOverview } from "./AdminOverview";
import { AdminPlanningPanel } from "./AdminPlanningPanel";
import type { Job, StockItem, StockRegion, Log, User, TeamPermissions } from "../../types";
import { resolveJobStatusFromScheduling } from "../../utils/jobStatus";
import {
  categoryRequiresDossierType,
  countJobsByCategory,
  DOSSIER_TYPE_FILTER_LABELS,
  JOB_CATEGORY_LABELS,
  jobMatchesCategory,
  jobMatchesDossierTypeFilter,
} from "../../utils/jobCategories";
import type { DossierTypeFilter, JobCategory } from "../../utils/jobCategories";
import {
  hasPermission,
  canSeeJobsTab,
  canSeeStockTab,
  canSeeAccessTab,
  canSeeTeamTab,
} from "../../utils/permissions";
import { useBudgetPrices } from "../../hooks/useBudgetPrices";
import { useProfile } from "../../hooks/useProfile";
import { getCurrentLocale } from "../../i18n";
import { invokeAiSuggestJob, invokeAiSuggestStockPrepare, invokeAiNaturalSearch } from "../../utils/ai";
import { pdf } from "@react-pdf/renderer";
import { DevisFactureDoc } from "../pdf/DevisFactureDoc";
import {
  buildClientNameForDossierType,
  isPacDossierType,
  showDehumidifierTechnicalFields,
  showLedTechnicalFields,
  showTimerTechnicalFields,
  usesRenovationQuestions,
  usesTechnicalQuestions,
} from "../../utils/dossierLabels";
import { sortBudgetStockItems } from "../../utils/budgetCatalog";

type DossierType = NonNullable<Job["dossierType"]>;

const DOSSIER_TYPE_OPTIONS: Array<{ value: DossierType; label: string }> = [
  { value: "led_exterieur", label: "LED extérieur" },
  { value: "led_interieur", label: "LED intérieur" },
  { value: "deshumidificateur", label: "Déshumidificateur" },
  { value: "vmc_agricoles", label: "VMC Agricoles" },
  { value: "th_122", label: "TH 122" },
  { value: "renovation", label: "Rénovation" },
  { value: "hp_bp_flottante", label: "HP/BP flottante" },
  { value: "pac", label: "PAC" },
];

const isValidDossierType = (value: unknown): value is DossierType =>
  value === "led_exterieur" ||
  value === "led_interieur" ||
  value === "deshumidificateur" ||
  value === "vmc_agricoles" ||
  value === "th_122" ||
  value === "renovation" ||
  value === "hp_bp_flottante" ||
  value === "pac" ||
  value === "led";

export interface AdminDashboardProps {
  currentUser: User;
  stock: StockItem[];
  setStock: React.Dispatch<React.SetStateAction<StockItem[]>>;
  stockRegions?: StockRegion[];
  stockRegionsLoading?: boolean;
  onAddRegion?: (name: string) => Promise<{ data: StockRegion | null; error: string | null }>;
  onUpdateRegion?: (id: string, name: string) => Promise<{ data: StockRegion | null; error: string | null }>;
  onRemoveRegion?: (id: string) => Promise<{ error: string | null }>;
  /** Initialise les références de tous les types de dossier pour une région × partenaire (Budget). */
  onEnsureBudgetTypeCatalog?: (
    regionId: string,
    partnerId?: string | null
  ) => Promise<{ created: number; error: string | null }>;
  refetchStockRegions?: () => void;
  logs: Log[];
  jobs: Job[];
  installers: User[];
  partners: User[];
  commercials?: User[];
  onUpdateJob: (job: Job) => void | Promise<{ error: string | null } | void>;
  onDeleteJob: (jobId: string) => void;
  onLogout: () => void;
  onManageAccess: (type: "partner" | "installer" | "commercial") => void;
  onAdminAddJob: (jobData: any) => void;
  onAdminImportJobs: (jobsData: Array<Record<string, unknown>>) => Promise<void>;
  onClearLogs: () => void;
  onDeleteLog: (logId: string) => void;
  onAddNewStockItem: (name: string, quantity: number, lowStockThreshold?: number, regionId?: string, partnerId?: string | null) => Promise<{ error: string | null }>;
  onDeleteStockItem: (id: string) => void;
  onUpdateStockItem: (id: string, name: string, quantity: number, lowStockThreshold?: number) => void;
  isOwner?: boolean;
  teamMembers: User[];
  teamLoading: boolean;
  teamError: string | null;
  teamCanAddMore: boolean;
  onTeamInvite: (data: {
    name: string;
    email: string;
    team_role: "owner" | "admin" | "member";
    permissions?: TeamPermissions;
  }) => Promise<{ error: string | null }>;
  onTeamUpdatePermissions: (
    memberId: string,
    permissions: TeamPermissions
  ) => Promise<{ error: string | null }>;
  onTeamRemoveMember: (memberId: string) => Promise<{ error: string | null }>;
  /** ID du tenant (owner) pour Réglages — abonnement et infos entreprise */
  ownerId?: string;
  /** Ouvrir la gestion d'abonnement (Stripe) */
  onManageSubscription?: () => void;
  /** Message d'erreur Stripe à afficher sous le bouton Gérer l'abonnement */
  subscriptionError?: string | null;
  /** Recalculer les factures partenaires pour tous les dossiers terminés avec les prix actuels */
  onRecalculatePartnerInvoices?: () => Promise<{ updated: number; error: string | null }>;
  /** Navigation demandée depuis le bot (mini secrétaire) : ouvrir l'onglet et optionnellement sélectionner région/partenaire */
  pendingNavigation?: { tab: string; regionId?: string; partnerId?: string } | null;
  onConsumePendingNavigation?: () => void;
  /** Supprimer définitivement le compte (tenant) et toutes les données. Réservé au propriétaire. */
  onDeleteAccount?: () => Promise<{ error: string | null }>;
  /** Clôturer un chantier (saisie quantités posées + notes, déduction stock). Même flux que le poseur. */
  onValidateJob?: (
    jobId: string,
    usageNeuf: Record<string, number>,
    usageRemplacement: Record<string, number>,
    notes: string
  ) => Promise<void>;
  /** Modifier le rapport d'un chantier déjà terminé (quantités + notes, ajustement stock). */
  onModifyJob?: (
    jobId: string,
    usageNeuf: Record<string, number>,
    usageRemplacement: Record<string, number>,
    notes: string
  ) => Promise<void>;
  /** Export complet des données du tenant (portabilité RGPD). */
  onExportAllData?: () => Promise<{ error: string | null }>;
}

export const AdminDashboard = ({
  currentUser,
  stock,
  setStock,
  stockRegions = [],
  stockRegionsLoading = false,
  onAddRegion,
  onUpdateRegion,
  onRemoveRegion,
  onEnsureBudgetTypeCatalog,
  refetchStockRegions,
  logs,
  jobs,
  onUpdateJob,
  onDeleteJob,
  onLogout,
  onManageAccess,
  installers,
  onAdminAddJob,
  onAdminImportJobs,
  onClearLogs,
  onDeleteLog,
  onAddNewStockItem,
  onDeleteStockItem,
  onUpdateStockItem,
  isOwner = false,
  teamMembers,
  teamLoading,
  teamError,
  teamCanAddMore,
  onTeamInvite,
  onTeamUpdatePermissions,
  onTeamRemoveMember,
  ownerId,
  onManageSubscription,
  subscriptionError,
  onRecalculatePartnerInvoices,
  pendingNavigation,
  onConsumePendingNavigation,
  onDeleteAccount,
  onValidateJob,
  onModifyJob,
  onExportAllData,
  partners: partnersProp,
  commercials: commercialsProp,
}: AdminDashboardProps) => {
  const partners = partnersProp ?? [];
  const commercials = commercialsProp ?? [];
  const { profile, loading: profileLoading, updatePreferences: updateCurrentUserPreferences } = useProfile(currentUser?.id ?? "");
  const { profile: ownerProfile, updatePreferences: updateOwnerPreferences } = useProfile(ownerId ?? "");
  const { i18n, t } = useTranslation();
  const [activeTab, setActiveTab] = useState("overview");

  // Resynchroniser l'UI avec la langue sauvegardée (owner) dès qu'elle diffère (évite que l'UI reste en anglais si un autre onglet/détecteur a changé la langue)
  useEffect(() => {
    if (!ownerId) return;
    const locale = ownerProfile?.preferences?.locale;
    if (locale !== "fr" && locale !== "en" && locale !== "es") return;
    if (getCurrentLocale() !== locale) i18n.changeLanguage(locale);
  }, [ownerId, ownerProfile?.preferences?.locale, i18n]);
  const [showSettings, setShowSettings] = useState(false);
  const [updatedUser, setUpdatedUser] = useState<User>(currentUser);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddJobModalOpen, setIsAddJobModalOpen] = useState(false);
  const [addJobSubmitting, setAddJobSubmitting] = useState(false);
  const addJobAddressRef = useRef<AddressInputHandle>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  /** Évite que le reset hub (changement de folder) n’efface une navigation Overview → catégorie. */
  const skipJobsFolderNavResetRef = useRef(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [editingJobInitial, setEditingJobInitial] = useState<Job | null>(null);
  /** true = consultation seule (pas le droit canEditJobs / pas créateur) */
  const [editingJobReadOnly, setEditingJobReadOnly] = useState(false);

  const cloneJobSnapshot = useCallback((job: Job): Job => {
    return {
      ...job,
      stockToPrepare: job.stockToPrepare ? { ...job.stockToPrepare } : undefined,
      stockUsed: job.stockUsed ? { ...job.stockUsed } : undefined,
      stockUsedNeuf: job.stockUsedNeuf ? { ...job.stockUsedNeuf } : undefined,
      stockUsedRemplacement: job.stockUsedRemplacement ? { ...job.stockUsedRemplacement } : undefined,
    };
  }, []);

  const openEditJobModal = useCallback(
    (job: Job, opts?: { readOnly?: boolean }) => {
      const snapshot = cloneJobSnapshot(job);
      const mayEdit =
        hasPermission(currentUser, "canEditJobs") || currentUser?.id === job.partnerId;
      setEditingJobReadOnly(opts?.readOnly ?? !mayEdit);
      setEditingJob(snapshot);
      setEditingJobInitial(snapshot);
    },
    [cloneJobSnapshot, currentUser]
  );

  const closeEditJobModal = useCallback(() => {
    setEditingJob(null);
    setEditingJobInitial(null);
    setEditingJobReadOnly(false);
  }, []);
  const [validatingJob, setValidatingJob] = useState<Job | null>(null);
  const [validatingUsageNeuf, setValidatingUsageNeuf] = useState<Record<string, number>>({});
  const [validatingUsageRemplacement, setValidatingUsageRemplacement] = useState<Record<string, number>>({});
  const [validatingNotes, setValidatingNotes] = useState("");
  const [validatingSubmitting, setValidatingSubmitting] = useState(false);
  const [validatingIsEditing, setValidatingIsEditing] = useState(false);
  const [newJob, setNewJob] = useState<{
    dossierType?: DossierType;
    clientName: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhone: string;
    surface: string;
    lightingType: string;
    ceilingHeight: string;
    estimatedLedCount: string;
    dehumidifierCount: string;
    timerCount: string;
    needsCounter: boolean;
    needsGenerator: boolean;
    notes: string;
    partnerId: string;
    stockRegionId: string;
  }>({
    dossierType: undefined,
    clientName: "",
    clientFirstName: "",
    clientLastName: "",
    clientPhone: "",
    surface: "",
    lightingType: "",
    ceilingHeight: "",
    estimatedLedCount: "",
    dehumidifierCount: "",
    timerCount: "",
    needsCounter: false,
    needsGenerator: false,
    notes: "",
    partnerId: "" as string,
    stockRegionId: "" as string,
  });
  const [isAddJobDossierTypeOpen, setIsAddJobDossierTypeOpen] = useState(false);
  const selectedAddJobDossierType = DOSSIER_TYPE_OPTIONS.find(
    (option) => option.value === newJob.dossierType
  );
  const [assignImmediately, setAssignImmediately] = useState(false);
  const [immediateAssignment, setImmediateAssignment] = useState<{
    installerId: string;
    date: string;
    time: string;
    stockToPrepare: Record<string, number>;
  }>({
    installerId: "",
    date: "",
    time: "",
    stockToPrepare: {},
  });

  /** IA : suggestion notes / estimation LED (create ou edit) */
  const [aiSuggestLoading, setAiSuggestLoading] = useState<null | "create_notes" | "create_led" | "edit_notes" | "edit_led">(null);
  const [aiSuggestError, setAiSuggestError] = useState<string | null>(null);
  /** IA : suggestion stock à préparer */
  const [aiStockPrepareLoading, setAiStockPrepareLoading] = useState<false | "edit" | "create">(false);
  const [aiStockPrepareError, setAiStockPrepareError] = useState<string | null>(null);

  const [isEditingStock, setIsEditingStock] = useState(false);
  const [tempStock, setTempStock] = useState(stock);
  const [newStockItem, setNewStockItem] = useState({ 
    name: "", 
    quantity: "", 
    lowStockThreshold: "10" 
  });
  const [editingStockItem, setEditingStockItem] = useState<StockItem | null>(
    null
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lowStockAlertDismissed, setLowStockAlertDismissed] = useState(false);
  const [stockAddError, setStockAddError] = useState<string | null>(null);
  const [stockAddLoading, setStockAddLoading] = useState(false);
  /** Région de stock sélectionnée (onglet Stock). */
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  /** Partenaire pour le filtre stock : "__internal__" = stock interne, ou id partenaire */
  const [selectedPartnerIdForStock, setSelectedPartnerIdForStock] = useState<string>("__internal__");
  const [newRegionName, setNewRegionName] = useState("");
  const [editingRegion, setEditingRegion] = useState<StockRegion | null>(null);
  const [isAddRegionModalOpen, setIsAddRegionModalOpen] = useState(false);
  const [addRegionLoading, setAddRegionLoading] = useState(false);
  const [addRegionError, setAddRegionError] = useState<string | null>(null);

  /** Modal "Ajouter une unité de stock" depuis Ajuster le stock (vue d’ensemble, par région) */
  const [isAddStockUnitOverviewOpen, setIsAddStockUnitOverviewOpen] = useState(false);
  const [newStockUnitOverview, setNewStockUnitOverview] = useState({
    name: "",
    quantity: "",
    lowStockThreshold: "10",
  });
  const [addStockUnitOverviewError, setAddStockUnitOverviewError] = useState<string | null>(null);
  const [addStockUnitOverviewLoading, setAddStockUnitOverviewLoading] = useState(false);

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfJob, setPdfJob] = useState<Job | null>(null);
  const [pdfType, setPdfType] = useState<"devis" | "facture">("devis");
  const [pdfAmount, setPdfAmount] = useState("");
  const [pdfGenerating, setPdfGenerating] = useState(false);

  /** Mise à jour optimiste : disparition immédiate du guide au clic "Terminer", sans attendre le serveur. */
  const [onboardingDismissedLocal, setOnboardingDismissedLocal] = useState(false);
  const onboardingDonePref = profile?.preferences?.onboardingDone ?? false;
  /** N'afficher l'onboarding que quand le profil est chargé, pour éviter un flash si onboarding déjà terminé. */
  const showOnboarding =
    isOwner && !profileLoading && !onboardingDonePref && !onboardingDismissedLocal;
  const onboardingStep1Done = stockRegions.length > 0;
  const onboardingStep2Done = stock.length > 0;
  const onboardingStep3Done = jobs.length > 0;
  const onboardingAllDone = onboardingStep1Done && onboardingStep2Done && onboardingStep3Done;

  /** Onglet Stock : null = liste des régions de stock, sinon id de la région affichée */
  const [stockRegionFolderId, setStockRegionFolderId] = useState<string | null>(null);
  /** Stock : vue par région ou par partenaire */
  const [stockViewMode, setStockViewMode] = useState<"by_region" | "by_partner">("by_region");
  /** Stock vue partenaire : null = liste des partenaires, "__internal__" ou id = détail stock de ce partenaire par région */
  const [stockPartnerFolderId, setStockPartnerFolderId] = useState<string | null>(null);
  /** Dans la vue détail d'une région (Stock > Région) : filtre partenaire pour visualiser le stock. "" = tous, "__internal__" = interne, ou id partenaire */
  const [stockRegionDetailPartnerFilter, setStockRegionDetailPartnerFilter] = useState<string>("");

  /** Budget : recalcul des factures en cours / message */
  const [recalculateInvoicesLoading, setRecalculateInvoicesLoading] = useState(false);
  const [recalculateInvoicesMessage, setRecalculateInvoicesMessage] = useState<string | null>(null);
  /** Budget : tri résultats par partenaire */
  const [partnersResultsSortBy, setPartnersResultsSortBy] = useState<"name" | "region" | "totalAsc" | "totalDesc">("totalDesc");

  /** Onglet Dossiers : "__all__" = tous, "__admin__" = admin/équipe, ou id partenaire */
  const [jobsFolderId, setJobsFolderId] = useState<string | null>(() => {
    try {
      const s = localStorage.getItem("crm-dossiers-folderId");
      if (s === "__all__" || s === "__admin__") return s;
      if (s && s.length > 0) return s;
    } catch (_) {}
    return "__all__";
  });
  /** Filtre optionnel par région de stock dans l'onglet Dossiers */
  const [jobsRegionFilterId, setJobsRegionFilterId] = useState<string>(() => {
    try {
      return localStorage.getItem("crm-dossiers-regionId") ?? "";
    } catch (_) { return ""; }
  });
  /** Filtre par statut dans l'onglet Dossiers */
  const [jobsStatusFilter, setJobsStatusFilter] = useState<"" | Job["status"]>(() => {
    try {
      const s = localStorage.getItem("crm-dossiers-status") as "" | Job["status"] | null;
      if (s === "pending" || s === "scheduled" || s === "in_progress" || s === "completed" || s === "cancelled") return s;
    } catch (_) {}
    return "";
  });
  /** Filtre par poseur (recherche intelligente) */
  const [jobsInstallerId, setJobsInstallerId] = useState<string>(() => {
    try { return localStorage.getItem("crm-dossiers-installerId") ?? ""; } catch (_) { return ""; }
  });
  /** Plage de dates (recherche intelligente) */
  const [jobsDateFrom, setJobsDateFrom] = useState<string>(() => {
    try { return localStorage.getItem("crm-dossiers-dateFrom") ?? ""; } catch (_) { return ""; }
  });
  const [jobsDateTo, setJobsDateTo] = useState<string>(() => {
    try { return localStorage.getItem("crm-dossiers-dateTo") ?? ""; } catch (_) { return ""; }
  });
  const [aiNaturalSearchLoading, setAiNaturalSearchLoading] = useState(false);
  const [aiNaturalSearchError, setAiNaturalSearchError] = useState<string | null>(null);
  /** Tri : colonne + direction */
  const jobsListSortKeyOptions = ["clientName", "createdAt", "status", "region", "installer", "lightingType"] as const;
  type JobsListSortKey = (typeof jobsListSortKeyOptions)[number];
  const [jobsListSortKey, setJobsListSortKey] = useState<JobsListSortKey>(() => {
    try {
      const s = localStorage.getItem("crm-dossiers-sortKey");
      if (jobsListSortKeyOptions.includes(s as JobsListSortKey)) return s as JobsListSortKey;
    } catch (_) {}
    return "createdAt";
  });
  const [jobsListSortDir, setJobsListSortDir] = useState<"asc" | "desc">(() => {
    try {
      return (localStorage.getItem("crm-dossiers-sortDir") === "asc") ? "asc" : "desc";
    } catch (_) { return "desc"; }
  });
  /** Pagination */
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsPerPage, setJobsPerPage] = useState<25 | 50 | 100>(() => {
    try {
      const n = parseInt(localStorage.getItem("crm-dossiers-perPage") ?? "25", 10);
      if (n === 25 || n === 50 || n === 100) return n;
    } catch (_) {}
    return 25;
  });
  /** Vue Liste ou Résumé (point 10) */
  const [jobsViewMode, setJobsViewMode] = useState<"list" | "summary">("list");
  /** Navigation guidée : catégories → types (si À programmer) → liste */
  const [jobsNavScreen, setJobsNavScreen] = useState<"hub" | "types" | "list">("hub");
  const [jobsBrowseCategory, setJobsBrowseCategory] = useState<JobCategory | null>(null);
  const [jobsBrowseDossierType, setJobsBrowseDossierType] = useState<DossierTypeFilter | null>(null);

  /** Onglet Régions : null = liste des régions, sinon id région sélectionnée */
  const [selectedRegionsTabRegionId, setSelectedRegionsTabRegionId] = useState<string | null>(null);
  /** Sous-onglet dans Régions : Poseurs | Stocks | Dossiers */
  const [regionsSubTab, setRegionsSubTab] = useState<"poseurs" | "stocks" | "dossiers">("dossiers");
  /** Onglet Partenaires : null = liste des partenaires, sinon id partenaire sélectionné */
  const [selectedPartnersTabPartnerId, setSelectedPartnersTabPartnerId] = useState<string | null>(null);
  /** Onglet Poseurs : null = liste poseurs par région, sinon id poseur sélectionné */
  const [selectedInstallersTabInstallerId, setSelectedInstallersTabInstallerId] = useState<string | null>(null);
  /** Tri dans l'onglet Poseurs : par région (sections) ou par nombre de chantiers */
  const [installersSortBy, setInstallersSortBy] = useState<"region" | "count">("region");
  /** Détail factures (Budget) : id du dossier dont le détail matériel est déplié, ou null */
  const [expandedInvoiceJobId, setExpandedInvoiceJobId] = useState<string | null>(null);

  /** Filtres Planning : région, partenaire, poseur */
  const [planningRegionId, setPlanningRegionId] = useState<string>("");
  const [planningPartnerId, setPlanningPartnerId] = useState<string>("");
  const [planningInstallerId, setPlanningInstallerId] = useState<string>("");

  /** Chantiers en cours pour l'onglet Planning (même filtres que le calendrier) */
  const planningInProgressJobs = useMemo(() => {
    return jobs
      .filter((j) => {
        if (j.status !== "in_progress") return false;
        if (planningRegionId && j.stockRegionId !== planningRegionId) return false;
        if (planningPartnerId && j.partnerId !== planningPartnerId) return false;
        if (planningInstallerId && j.installerId !== planningInstallerId) return false;
        return true;
      })
      .sort((a, b) => {
        const d = (a.date || "").localeCompare(b.date || "");
        if (d !== 0) return d;
        return (a.time || "").localeCompare(b.time || "");
      });
  }, [jobs, planningRegionId, planningPartnerId, planningInstallerId]);

  /** Filtre/tri liste partenaires (onglets Dossiers et Partenaires) : par statut de dossier ou par région */
  const [partnersListStatusFilter, setPartnersListStatusFilter] = useState<"" | Job["status"]>("");
  const [partnersListRegionFilter, setPartnersListRegionFilter] = useState<string>("");
  const [partnersListSortBy, setPartnersListSortBy] = useState<"name" | "count">("name");

  /** Budget : parcours Partenaire → Région → types */
  const [budgetPartnerId, setBudgetPartnerId] = useState<string | null>(null);
  const [budgetRegionId, setBudgetRegionId] = useState<string>("");
  const [budgetRefId, setBudgetRefId] = useState<string>("");
  const [budgetTariffType, setBudgetTariffType] = useState<"neuf" | "remplacement">("neuf");
  /** Prix en cours d'édition (centimes) ; null = afficher la valeur enregistrée */
  const [budgetEditedCents, setBudgetEditedCents] = useState<number | null>(null);
  /** Modifications non enregistrées : bloquer la sortie de l'onglet Budget */
  const [budgetPriceDirty, setBudgetPriceDirty] = useState(false);
  /** Modal "quitter sans enregistrer" : onglet demandé */
  const [budgetPendingTab, setBudgetPendingTab] = useState<string | null>(null);
  /** Sous-vue Budget : vue d'ensemble | tarifs | factures */
  const [budgetSubTab, setBudgetSubTab] = useState<"overview" | "tariffs" | "invoices">("overview");

  const budgetPrices = useBudgetPrices(ownerId);
  const resolvedBudgetPartnerId =
    budgetPartnerId === null || budgetPartnerId === "__internal__" || budgetPartnerId === ""
      ? null
      : budgetPartnerId;

  // Stock filtré par région et partenaire sélectionnés (onglet Stock)
  const resolvedPartnerIdForStock = selectedPartnerIdForStock === "__internal__" || selectedPartnerIdForStock === "" ? null : selectedPartnerIdForStock;
  const stockForCurrentRegion = useMemo(
    () =>
      selectedRegionId != null
        ? stock.filter(
            (s) =>
              s.regionId === selectedRegionId &&
              (s.partnerId ?? null) === resolvedPartnerIdForStock
          )
        : [],
    [stock, selectedRegionId, resolvedPartnerIdForStock]
  );
  const getStockForRegion = (regionId: string | null | undefined) =>
    regionId ? stock.filter((s) => s.regionId === regionId) : stock;
  /** Stock pour une région et un partenaire donnés (dossiers : stock à préparer, etc.) */
  const getStockForRegionAndPartner = (regionId: string | null | undefined, partnerId: string | null | undefined) => {
    if (!regionId) return [];
    const pid = (partnerId === undefined || partnerId === "") ? null : partnerId ?? null;
    return stock.filter((s) => s.regionId === regionId && (s.partnerId ?? null) === pid);
  };

  const stockForValidatingJob = useMemo(
    () => (validatingJob ? getStockForRegionAndPartner(validatingJob.stockRegionId, validatingJob.partnerId) : []),
    [validatingJob?.id, validatingJob?.stockRegionId, validatingJob?.partnerId, stock]
  );

  useEffect(() => {
    if (!validatingJob) return;
    // Un dossier qui n'est pas encore "completed" doit toujours passer par validateJob
    // (qui force status → completed), même s'il contient des données de rapport
    // héritées d'un ancien appel à modifyJob (bug historique).
    const isAlreadyCompleted = validatingJob.status === "completed";
    if (
      (validatingJob.stockUsedNeuf != null && Object.keys(validatingJob.stockUsedNeuf).length > 0) ||
      (validatingJob.stockUsedRemplacement != null && Object.keys(validatingJob.stockUsedRemplacement).length > 0)
    ) {
      const neuf: Record<string, number> = {};
      const rem: Record<string, number> = {};
      stockForValidatingJob.forEach((item: StockItem) => {
        neuf[item.id] = validatingJob.stockUsedNeuf?.[item.id] ?? 0;
        rem[item.id] = validatingJob.stockUsedRemplacement?.[item.id] ?? 0;
      });
      setValidatingUsageNeuf(neuf);
      setValidatingUsageRemplacement(rem);
      setValidatingNotes(validatingJob.installerNotes || "");
      setValidatingIsEditing(isAlreadyCompleted);
    } else if (validatingJob.stockUsed && Object.keys(validatingJob.stockUsed).length > 0) {
      const neuf: Record<string, number> = {};
      const rem: Record<string, number> = {};
      stockForValidatingJob.forEach((item: StockItem) => {
        const total = validatingJob.stockUsed![item.id] ?? 0;
        neuf[item.id] = total;
        rem[item.id] = 0;
      });
      setValidatingUsageNeuf(neuf);
      setValidatingUsageRemplacement(rem);
      setValidatingNotes(validatingJob.installerNotes || "");
      setValidatingIsEditing(isAlreadyCompleted);
    } else {
      const initial: Record<string, number> = {};
      stockForValidatingJob.forEach((item: StockItem) => (initial[item.id] = 0));
      setValidatingUsageNeuf(initial);
      setValidatingUsageRemplacement(
        stockForValidatingJob.reduce((acc, item) => ({ ...acc, [item.id]: 0 }), {})
      );
      setValidatingNotes("");
      setValidatingIsEditing(false);
    }
  }, [validatingJob?.id, validatingJob?.stockUsed, validatingJob?.stockUsedNeuf, validatingJob?.stockUsedRemplacement, validatingJob?.installerNotes, validatingJob?.status, stockForValidatingJob]);

  const handleValidateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatingJob) return;
    if (validatingIsEditing && !onModifyJob) return;
    if (!validatingIsEditing && !onValidateJob) return;
    const neufToSend: Record<string, number> = {};
    const remToSend: Record<string, number> = {};
    // Ne pas dépendre uniquement du filtre visuel (région/partenaire) :
    // on envoie toutes les clés présentes dans l'état du formulaire.
    const allItemIds = new Set<string>([
      ...stockForValidatingJob.map((item: StockItem) => item.id),
      ...Object.keys(validatingUsageNeuf),
      ...Object.keys(validatingUsageRemplacement),
    ]);
    allItemIds.forEach((itemId) => {
      neufToSend[itemId] = validatingUsageNeuf[itemId] ?? 0;
      remToSend[itemId] = validatingUsageRemplacement[itemId] ?? 0;
    });
    setValidatingSubmitting(true);
    try {
      if (validatingIsEditing) await onModifyJob!(validatingJob.id, neufToSend, remToSend, validatingNotes);
      else await onValidateJob!(validatingJob.id, neufToSend, remToSend, validatingNotes);
      setValidatingJob(null);
      closeEditJobModal();
    } catch (err) {
      // Ne pas fermer la modale : l'utilisateur doit voir que ça a échoué
      alert(`Erreur lors de la clôture du chantier.\n\n${err instanceof Error ? err.message : "Erreur inconnue"}`);
    } finally {
      setValidatingSubmitting(false);
    }
  };

  /** Stock pour un partenaire (toutes régions). partnerId null = interne, "__internal__" = interne. */
  const getStockForPartner = (partnerId: string | null) => {
    const pid = (partnerId === "__internal__" || partnerId === "" ? null : partnerId) ?? null;
    return stock.filter((s) => (s.partnerId ?? null) === pid);
  };
  /** Nombre d’articles en stock bas pour un partenaire (toutes régions). */
  const getLowStockCountForPartner = (partnerId: string | null) => {
    return getStockForPartner(partnerId).filter((item) => (item.quantity <= (item.lowStockThreshold ?? 10))).length;
  };
  // Sélectionner la première région par défaut quand les régions sont chargées
  useEffect(() => {
    if (stockRegions.length > 0 && selectedRegionId === null) {
      setSelectedRegionId(stockRegions[0].id);
    }
  }, [stockRegions, selectedRegionId]);
  // À l'ouverture du modal "Nouveau dossier", initialiser région et partenaire selon le contexte
  const defaultRegionForNewJob = selectedRegionsTabRegionId ?? selectedRegionId;
  useEffect(() => {
    if (!isAddJobModalOpen) return;
    setNewJob((prev) => {
      const next = { ...prev };
      if (defaultRegionForNewJob) next.stockRegionId = defaultRegionForNewJob;
      if (selectedPartnersTabPartnerId) next.partnerId = selectedPartnersTabPartnerId;
      return next;
    });
  }, [isAddJobModalOpen, defaultRegionForNewJob, selectedPartnersTabPartnerId]);

  useEffect(() => {
    if (!isAddJobModalOpen) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      const hasUnsaved =
        !!newJob.dossierType ||
        !!newJob.clientName.trim() ||
        !!newJob.clientPhone.trim() ||
        !!newJob.surface.trim() ||
        !!newJob.dehumidifierCount.trim() ||
        !!newJob.timerCount.trim() ||
        !!newJob.notes.trim();
      if (!hasUnsaved) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isAddJobModalOpen, newJob]);


  // Budget : initialiser le catalogue types pour le couple partenaire × région
  useEffect(() => {
    if (activeTab !== "budget" || budgetSubTab !== "tariffs") return;
    if (!budgetPartnerId || !budgetRegionId || !onEnsureBudgetTypeCatalog) return;
    let cancelled = false;
    (async () => {
      await onEnsureBudgetTypeCatalog(budgetRegionId, resolvedBudgetPartnerId);
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    budgetSubTab,
    budgetPartnerId,
    budgetRegionId,
    resolvedBudgetPartnerId,
    onEnsureBudgetTypeCatalog,
  ]);

  // Après création des types manquants, sélectionner le premier si besoin
  useEffect(() => {
    if (activeTab !== "budget" || !budgetPartnerId || !budgetRegionId || budgetRefId) return;
    const refs = sortBudgetStockItems(
      getStockForRegionAndPartner(budgetRegionId, resolvedBudgetPartnerId)
    );
    if (refs[0]) setBudgetRefId(refs[0].id);
  }, [activeTab, budgetPartnerId, budgetRegionId, budgetRefId, resolvedBudgetPartnerId, stock]);

  /** Changement d'onglet : bloque si on quitte Budget avec des modifications non enregistrées */
  const requestTabChange = (tab: string) => {
    if (tab !== activeTab) {
      // Évite qu'une modale de l'onglet Dossiers masque la vue cible (notamment sur mobile).
      closeEditJobModal();
      setValidatingJob(null);
      setPdfModalOpen(false);
      setPdfJob(null);
      setIsAddJobModalOpen(false);
    }
    if (activeTab === "budget" && budgetPriceDirty && tab !== "budget") {
      setBudgetPendingTab(tab);
      // Mobile: fermer le menu pour laisser apparaître la modale de confirmation.
      setIsMobileMenuOpen(false);
      return;
    }
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
  };

  // Redirection de sécurité : si un membre force un onglet sans permission, retour à l'accueil (overview)
  useEffect(() => {
    if (activeTab === "jobs" && !canSeeJobsTab(currentUser)) setActiveTab("overview");
    else if (activeTab === "regions" && !canSeeJobsTab(currentUser)) setActiveTab("overview");
    else if (activeTab === "partners" && !canSeeJobsTab(currentUser)) setActiveTab("overview");
    else if (activeTab === "installers" && !canSeeJobsTab(currentUser)) setActiveTab("overview");
    else if (activeTab === "planning" && !canSeeJobsTab(currentUser)) setActiveTab("overview");
    else if (activeTab === "budget" && !canSeeJobsTab(currentUser)) setActiveTab("overview");
    else if (activeTab === "stock" && !canSeeStockTab(currentUser)) setActiveTab("overview");
    else if (activeTab === "access" && !canSeeAccessTab(currentUser)) setActiveTab("overview");
    else if (activeTab === "team" && !canSeeTeamTab(currentUser)) setActiveTab("overview");
  }, [activeTab, currentUser]);

  // Navigation demandée depuis le bot (mini secrétaire)
  useEffect(() => {
    if (!pendingNavigation || !onConsumePendingNavigation) return;
    setActiveTab(pendingNavigation.tab);
    if (pendingNavigation.regionId) setSelectedRegionId(pendingNavigation.regionId);
    if (pendingNavigation.partnerId) setSelectedPartnersTabPartnerId(pendingNavigation.partnerId);
    onConsumePendingNavigation();
  }, [pendingNavigation, onConsumePendingNavigation]);

  // Calculer les articles en stock bas (région courante ou tout)
  const lowStockItems = stockForCurrentRegion.filter((item) => {
    const threshold = item.lowStockThreshold ?? 10;
    return item.quantity <= threshold;
  });
  // Nombre total d'articles en "peu de stock" (toutes régions) pour la bulle de l'onglet
  const totalLowStockCount = stock.filter((item) => {
    const threshold = item.lowStockThreshold ?? 10;
    return item.quantity <= threshold;
  }).length;
  // Pour une région donnée, compter les articles en peu de stock
  const getLowStockCountForRegion = (regionId: string) =>
    stock
      .filter((s) => s.regionId === regionId)
      .filter((item) => {
        const threshold = item.lowStockThreshold ?? 10;
        return item.quantity <= threshold;
      }).length;

  // Ne pas écraser la liste par [] pendant le chargement des régions (évite le clignotement des dossiers/stock)
  useEffect(() => {
    if (isEditingStock) return;
    if (stockRegionsLoading && stockForCurrentRegion.length === 0) return;
    setTempStock(stockForCurrentRegion);
  }, [stockForCurrentRegion, isEditingStock, stockRegionsLoading]);

  // Quand on ajoute une unité depuis le modal, la région peut avoir un item de plus : garder tempStock à jour
  useEffect(() => {
    if (isEditingStock && stockForCurrentRegion.length > tempStock.length) {
      setTempStock(stockForCurrentRegion);
    }
  }, [isEditingStock, stockForCurrentRegion, tempStock.length]);

  const handleSaveStock = async () => {
    // {t("admin.save")} chaque modification en base de données
    for (const item of tempStock) {
      const originalItem = stock.find(s => s.id === item.id);
      if (originalItem && originalItem.quantity !== item.quantity) {
        await onUpdateStockItem(item.id, item.name, item.quantity, item.lowStockThreshold);
      }
    }
    setIsEditingStock(false);
  };
  const handleCreateStockItem = async (e: React.FormEvent) => {
    e.preventDefault();
    setStockAddError(null);

    const qty = parseInt(newStockItem.quantity);
    const threshold = parseInt(newStockItem.lowStockThreshold);
    if (!newStockItem.name.trim()) return;

    setStockAddLoading(true);
    const regionId = stockRegionFolderId ?? selectedRegionId ?? stockRegions[0]?.id;
    const partnerId = resolvedPartnerIdForStock;
    const { error } = await onAddNewStockItem(
      newStockItem.name,
      isNaN(qty) ? 0 : qty,
      isNaN(threshold) ? 10 : threshold,
      regionId ?? undefined,
      partnerId ?? undefined,
    );
    setStockAddLoading(false);

    if (error) {
      setStockAddError(error);
      return;
    }
    setNewStockItem({ name: "", quantity: "", lowStockThreshold: "10" });
  };
  const handleSaveItemEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingStockItem) {
      onUpdateStockItem(
        editingStockItem.id,
        editingStockItem.name,
        editingStockItem.quantity,
        editingStockItem.lowStockThreshold
      );
      setEditingStockItem(null);
    }
  };
  /** Persistance des préférences onglet Dossiers */
  useEffect(() => {
    try {
      if (jobsFolderId) localStorage.setItem("crm-dossiers-folderId", jobsFolderId);
      localStorage.setItem("crm-dossiers-regionId", jobsRegionFilterId);
      localStorage.setItem("crm-dossiers-status", jobsStatusFilter);
      localStorage.setItem("crm-dossiers-installerId", jobsInstallerId);
      localStorage.setItem("crm-dossiers-dateFrom", jobsDateFrom);
      localStorage.setItem("crm-dossiers-dateTo", jobsDateTo);
      localStorage.setItem("crm-dossiers-sortKey", jobsListSortKey);
      localStorage.setItem("crm-dossiers-sortDir", jobsListSortDir);
      localStorage.setItem("crm-dossiers-perPage", String(jobsPerPage));
    } catch (_) {}
  }, [jobsFolderId, jobsRegionFilterId, jobsStatusFilter, jobsInstallerId, jobsDateFrom, jobsDateTo, jobsListSortKey, jobsListSortDir, jobsPerPage]);

  /** Dossiers affichés : folder + recherche (nom, id, adresse, tél, poseur) + statut + région */
  const getJobsForCurrentFolder = (): Job[] => {
    if (!jobsFolderId) return [...jobs];
    let list: Job[];
    if (jobsFolderId === "__admin__") {
      list = jobs.filter((j: Job) => !j.partnerId);
    } else if (jobsFolderId === "__all__") {
      list = [...jobs];
    } else {
      list = jobs.filter((j: Job) => j.partnerId === jobsFolderId);
    }
    if (jobsStatusFilter) {
      list = list.filter((j: Job) => j.status === jobsStatusFilter);
    }
    if (jobsInstallerId) {
      list = list.filter((j: Job) => j.installerId === jobsInstallerId);
    }
    if (jobsDateFrom) {
      list = list.filter((j: Job) => (j.date ?? "") >= jobsDateFrom);
    }
    if (jobsDateTo) {
      list = list.filter((j: Job) => (j.date ?? "") <= jobsDateTo);
    }
    if (!searchTerm.trim()) return list;
    const term = searchTerm.toLowerCase().trim();
    return list.filter((j: Job) => {
      if ((j.clientName ?? "").toLowerCase().includes(term)) return true;
      if ((j.id ?? "").toLowerCase().includes(term)) return true;
      if ((j.clientAddress ?? "").toLowerCase().includes(term)) return true;
      if ((j.clientPhone ?? "").toLowerCase().includes(term)) return true;
      const installerName = j.installerId ? installers.find((i: User) => i.id === j.installerId)?.name ?? "" : "";
      if (installerName.toLowerCase().includes(term)) return true;
      return false;
    });
  };
  const folderJobs = getJobsForCurrentFolder();
  const folderJobsRegionFiltered = jobsRegionFilterId
    ? folderJobs.filter((j: Job) => j.stockRegionId === jobsRegionFilterId)
    : folderJobs;
  const folderJobsForHub = folderJobsRegionFiltered;
  const folderJobsCategoryFiltered =
    jobsBrowseCategory && jobsBrowseCategory !== "all"
      ? folderJobsForHub.filter((j: Job) => jobMatchesCategory(j, jobsBrowseCategory))
      : folderJobsForHub;
  const folderJobsBrowseFiltered = jobsBrowseDossierType
    ? folderJobsCategoryFiltered.filter((j: Job) =>
        jobMatchesDossierTypeFilter(j, jobsBrowseDossierType)
      )
    : folderJobsCategoryFiltered;
  const toScheduleJobsForTypes = useMemo(
    () => folderJobsForHub.filter((j: Job) => jobMatchesCategory(j, "to_schedule")),
    [folderJobsForHub]
  );
  /** Tri par colonne */
  const folderJobsSorted = useMemo(() => {
    const sorted = [...folderJobsBrowseFiltered].sort((a, b) => {
      let cmp = 0;
      if (jobsListSortKey === "clientName") {
        cmp = (a.clientName ?? "").localeCompare(b.clientName ?? "", "fr");
      } else if (jobsListSortKey === "createdAt") {
        cmp = new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime();
      } else if (jobsListSortKey === "status") {
        const order: Record<Job["status"], number> = { pending: 0, scheduled: 1, in_progress: 2, completed: 3, cancelled: 4 };
        cmp = (order[a.status] ?? 0) - (order[b.status] ?? 0);
      } else if (jobsListSortKey === "region") {
        const nameA = (a.stockRegionId && stockRegions.find((r) => r.id === a.stockRegionId)?.name) ?? "";
        const nameB = (b.stockRegionId && stockRegions.find((r) => r.id === b.stockRegionId)?.name) ?? "";
        cmp = nameA.localeCompare(nameB, "fr");
      } else if (jobsListSortKey === "lightingType") {
        cmp = (a.lightingType ?? "").localeCompare(b.lightingType ?? "", "fr");
      } else {
        const nameA = a.installerId ? installers.find((i: User) => i.id === a.installerId)?.name ?? "" : "";
        const nameB = b.installerId ? installers.find((i: User) => i.id === b.installerId)?.name ?? "" : "";
        cmp = nameA.localeCompare(nameB, "fr");
      }
      return jobsListSortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [folderJobsBrowseFiltered, jobsListSortKey, jobsListSortDir, stockRegions, installers]);
  const jobsTotalFiltered = folderJobsSorted.length;
  /** Total dans la vue courante sans aucun filtre (pour "X sur Y") */
  const jobsTotalInFolder = useMemo(() => {
    if (!jobsFolderId) return 0;
    if (jobsFolderId === "__admin__") return jobs.filter((j: Job) => !j.partnerId).length;
    if (jobsFolderId === "__all__") return jobs.length;
    return jobs.filter((j: Job) => j.partnerId === jobsFolderId).length;
  }, [jobs, jobsFolderId]);
  const hasActiveFilters = searchTerm.trim() !== "" || jobsStatusFilter !== "" || jobsRegionFilterId !== "" || jobsInstallerId !== "" || jobsDateFrom !== "" || jobsDateTo !== "" || !!jobsBrowseCategory || !!jobsBrowseDossierType;
  /** Réinitialiser la page quand les filtres changent */
  useEffect(() => {
    setJobsPage(1);
  }, [searchTerm, jobsRegionFilterId, jobsStatusFilter, jobsFolderId, jobsInstallerId, jobsDateFrom, jobsDateTo, jobsBrowseCategory, jobsBrowseDossierType]);

  useEffect(() => {
    if (skipJobsFolderNavResetRef.current) {
      skipJobsFolderNavResetRef.current = false;
      return;
    }
    setJobsBrowseCategory(null);
    setJobsBrowseDossierType(null);
    setJobsNavScreen("hub");
  }, [jobsFolderId]);

  const jobsPaginated = folderJobsSorted.slice((jobsPage - 1) * jobsPerPage, jobsPage * jobsPerPage);
  const jobsToShow = jobsPaginated;
  const jobsTotalPages = Math.max(1, Math.ceil(jobsTotalFiltered / jobsPerPage));

  const getJobsFolderTitle = (): string => {
    if (!jobsFolderId) return "";
    if (jobsFolderId === "__admin__") return t("admin.adminAndTeam");
    if (jobsFolderId === "__all__") return t("admin.allJobsFolder");
    return partners.find((p: User) => p.id === jobsFolderId)?.name ?? t("admin.partnerLabel");
  };

  const handleJobsSort = (key: JobsListSortKey) => {
    if (jobsListSortKey === key) {
      setJobsListSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setJobsListSortKey(key);
      setJobsListSortDir(key === "createdAt" ? "desc" : "asc");
    }
    setJobsPage(1);
  };

  /** Réinitialiser filtres et préférences onglet Dossiers (point 9) */
  const handleResetDossiersPrefs = () => {
    setSearchTerm("");
    setJobsRegionFilterId("");
    setJobsStatusFilter("");
    setJobsInstallerId("");
    setJobsDateFrom("");
    setJobsDateTo("");
    setJobsFolderId("__all__");
    setJobsListSortKey("createdAt");
    setJobsListSortDir("desc");
    setJobsPerPage(25);
    setJobsPage(1);
    setJobsBrowseCategory(null);
    setJobsBrowseDossierType(null);
    setJobsNavScreen("hub");
    try {
      localStorage.setItem("crm-dossiers-folderId", "__all__");
      localStorage.setItem("crm-dossiers-regionId", "");
      localStorage.setItem("crm-dossiers-status", "");
      localStorage.setItem("crm-dossiers-installerId", "");
      localStorage.setItem("crm-dossiers-dateFrom", "");
      localStorage.setItem("crm-dossiers-dateTo", "");
      localStorage.setItem("crm-dossiers-sortKey", "createdAt");
      localStorage.setItem("crm-dossiers-sortDir", "desc");
      localStorage.setItem("crm-dossiers-perPage", "25");
    } catch (_) {}
  };

  const goToJobsCategoryHub = () => {
    setJobsBrowseCategory(null);
    setJobsBrowseDossierType(null);
    setJobsStatusFilter("");
    setJobsNavScreen("hub");
  };

  const goToJobsDossierTypes = () => {
    setJobsBrowseDossierType(null);
    setJobsNavScreen("types");
  };

  const handleAdminSelectCategory = (category: JobCategory) => {
    setJobsBrowseCategory(category);
    setJobsBrowseDossierType(null);
    setJobsStatusFilter("");
    if (categoryRequiresDossierType(category)) {
      setJobsNavScreen("types");
    } else {
      setJobsNavScreen("list");
    }
  };

  const handleAdminSelectDossierType = (type: DossierTypeFilter) => {
    setJobsBrowseDossierType(type);
    setJobsNavScreen("list");
  };

  const openOverviewJobsCategory = (category: JobCategory) => {
    if (!canSeeJobsTab(currentUser)) return;
    // Aligner la liste sur les counts Overview (tous les dossiers, sans filtres résiduels).
    setJobsStatusFilter("");
    setJobsRegionFilterId("");
    setSearchTerm("");
    setAiNaturalSearchError(null);
    setJobsInstallerId("");
    setJobsDateFrom("");
    setJobsDateTo("");
    setJobsViewMode("list");
    if (jobsFolderId !== "__all__") {
      skipJobsFolderNavResetRef.current = true;
      setJobsFolderId("__all__");
    }
    setJobsBrowseCategory(category);
    setJobsBrowseDossierType(null);
    if (categoryRequiresDossierType(category)) {
      setJobsNavScreen("types");
    } else {
      setJobsNavScreen("list");
    }
    requestTabChange("jobs");
  };

  const handleSmartSearch = async () => {
    const q = searchTerm.trim();
    if (!q) return;
    setAiNaturalSearchLoading(true);
    setAiNaturalSearchError(null);
    try {
      const result = await invokeAiNaturalSearch({
        query: q,
        context: {
          partnerNames: partners.map((p: User) => p.name),
          installerNames: installers.map((i: User) => i.name),
          regionNames: stockRegions.map((r) => r.name),
        },
      });
      setJobsStatusFilter((result.status as "" | Job["status"]) || "");
      setSearchTerm(result.freeText ?? "");
      const partnerId = result.partnerName
        ? partners.find((p: User) => p.name === result.partnerName)?.id ?? ""
        : "";
      setJobsFolderId(partnerId || "__all__");
      const installerId = result.installerName
        ? installers.find((i: User) => i.name === result.installerName)?.id ?? ""
        : "";
      setJobsInstallerId(installerId);
      const regionId = result.regionName
        ? stockRegions.find((r) => r.name === result.regionName)?.id ?? ""
        : "";
      setJobsRegionFilterId(regionId);
      setJobsDateFrom(result.dateFrom ?? "");
      setJobsDateTo(result.dateTo ?? "");
      setJobsBrowseCategory(null);
      setJobsBrowseDossierType(null);
      setJobsNavScreen("list");
      setJobsPage(1);
    } catch (err) {
      setAiNaturalSearchError(err instanceof Error ? err.message : "Erreur recherche IA");
    } finally {
      setAiNaturalSearchLoading(false);
    }
  };

  /** Export des dossiers filtrés en CSV puis téléchargement (point 10) */
  const handleExportDossiers = (format: "csv" | "xlsx") => {
    const headers = [t("admin.client"), t("admin.address"), t("admin.phone"), t("settings.csvSurface"), t("admin.region"), t("admin.typeLabel"), t("admin.statusLabel"), t("admin.installer"), t("admin.dateCreated"), t("admin.invoice")];
    if (jobsFolderId === "__all__") headers.splice(3, 0, t("admin.partnerLabel"));
    const rows = folderJobsSorted.map((j: Job) => {
      const regionName = j.stockRegionId ? stockRegions.find((r) => r.id === j.stockRegionId)?.name ?? "" : "";
      const installerName = j.installerId ? installers.find((i: User) => i.id === j.installerId)?.name ?? "" : "";
      const base = [j.clientName ?? "", j.clientAddress ?? "", j.clientPhone ?? "", j.surface ?? "", regionName, j.lightingType ?? "", j.status, installerName, j.createdAt ? new Date(j.createdAt).toLocaleDateString("fr-FR") : "", j.partnerInvoiceTotalCents != null ? (j.partnerInvoiceTotalCents / 100).toFixed(2) : ""];
      if (jobsFolderId === "__all__") base.splice(3, 0, getPartnerOrCreatorLabel(j));
      return base;
    });
    const data = [headers, ...rows];
    if (format === "csv") {
      const csv = data.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dossiers-${getJobsFolderTitle().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "Dossiers");
      XLSX.writeFile(wb, `dossiers-${getJobsFolderTitle().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    }
  };

  /** Partenaires filtrés et triés pour la liste (onglets Dossiers / Partenaires). */
  const getFilteredAndSortedPartners = (): User[] => {
    let list = partners.filter((p: User) => {
      const partnerJobs = jobs.filter((j: Job) => j.partnerId === p.id);
      if (partnersListStatusFilter && !partnerJobs.some((j: Job) => j.status === partnersListStatusFilter))
        return false;
      if (partnersListRegionFilter && !partnerJobs.some((j: Job) => (j.stockRegionId ?? "") === partnersListRegionFilter))
        return false;
      return true;
    });
    const getCount = (p: User, status?: Job["status"]) => {
      const partnerJobs = jobs.filter((j: Job) => j.partnerId === p.id);
      return status ? partnerJobs.filter((j: Job) => j.status === status).length : partnerJobs.length;
    };
    list = [...list].sort((a, b) => {
      if (partnersListSortBy === "name")
        return (a.name ?? "").localeCompare(b.name ?? "");
      if (partnersListSortBy === "count") return getCount(b) - getCount(a);
      return 0;
    });
    return list;
  };
  const displayedPartners = getFilteredAndSortedPartners();

  /** Partenaire (si dossier partenaire) ou nom de l'admin/équipier ayant créé le dossier. */
  const getPartnerOrCreatorLabel = (job: Job) => {
    if (job.partnerId) {
      return partners.find((p: User) => p.id === job.partnerId)?.name ?? t("admin.partnerLabel");
    }
    if (job.createdBy) {
      return teamMembers.find((m: User) => m.id === job.createdBy)?.name ?? t("admin.folderAdmin");
    }
    return t("admin.folderAdmin");
  };

  const resetAddJobForm = () => {
    setNewJob({
      dossierType: undefined,
      clientName: "",
      clientFirstName: "",
      clientLastName: "",
      clientPhone: "",
      surface: "",
      lightingType: "",
      ceilingHeight: "",
      estimatedLedCount: "",
      dehumidifierCount: "",
      timerCount: "",
      needsCounter: false,
      needsGenerator: false,
      notes: "",
      partnerId: "",
      stockRegionId: selectedRegionId ?? "",
    });
    setAssignImmediately(false);
    setImmediateAssignment({ installerId: "", date: "", time: "", stockToPrepare: {} });
  };

  const handleCloseAddJobModal = () => {
    const hasUnsaved =
      !!newJob.dossierType ||
      !!newJob.clientName.trim() ||
      !!newJob.clientFirstName.trim() ||
      !!newJob.clientLastName.trim() ||
      !!newJob.clientPhone.trim() ||
      !!newJob.surface.trim() ||
      !!newJob.dehumidifierCount.trim() ||
      !!newJob.timerCount.trim() ||
      !!newJob.notes.trim();
    if (hasUnsaved && !window.confirm("Le dossier est incomplet. Voulez-vous vraiment fermer ?")) {
      return;
    }
    setIsAddJobModalOpen(false);
    resetAddJobForm();
  };

  const getMissingFieldsForDossierType = (job: Partial<Job>) => {
    const missing: string[] = [];
    if (!job.clientName?.trim()) missing.push("Nom client");
    if (!job.clientPhone?.trim()) missing.push("Téléphone");
    if (!job.clientAddress?.trim()) missing.push("Adresse");
    if (!job.dossierType) missing.push("Type de dossier");

    if (job.dossierType === "deshumidificateur" || job.dossierType === "vmc_agricoles") {
      if (!job.surface?.trim()) missing.push("Surface");
      if (job.dehumidifierCount == null || Number.isNaN(job.dehumidifierCount)) {
        missing.push("Nombre de déshumidificateurs");
      }
      if (!job.needsCounter && !job.needsGenerator) {
        missing.push("Option compteur ou générateur");
      }
    }
    if (job.dossierType === "th_122") {
      if (!job.surface?.trim()) missing.push("Surface m²");
      if (job.timerCount == null || Number.isNaN(job.timerCount)) {
        missing.push("Nombre de minuteries");
      }
    }
    return missing;
  };

  const isNewJobCoreComplete = (() => {
    if (!newJob.clientPhone.trim() || !newJob.dossierType) return false;
    if (usesRenovationQuestions(newJob.dossierType)) {
      if (!newJob.clientFirstName.trim() || !newJob.clientLastName.trim()) return false;
      return true;
    }
    if (!newJob.clientName.trim()) return false;
    if (newJob.dossierType === "deshumidificateur" || newJob.dossierType === "vmc_agricoles") {
      if (!newJob.surface.trim() || newJob.dehumidifierCount.trim() === "") return false;
      if (!newJob.needsCounter && !newJob.needsGenerator) return false;
    }
    if (newJob.dossierType === "th_122") {
      if (!newJob.surface.trim() || newJob.timerCount.trim() === "") return false;
    }
    return true;
  })();

  const normalizeEditableJob = (job: Job | null) => {
    if (!job) return null;
    const normalizedStock = Object.fromEntries(
      Object.entries(job.stockToPrepare ?? {})
        .map(([key, value]) => [key, Number(value) || 0] as const)
        .sort(([a], [b]) => a.localeCompare(b))
    );
    return {
      id: job.id,
      dossierType: job.dossierType ?? "",
      clientName: job.clientName ?? "",
      clientPhone: job.clientPhone ?? "",
      clientAddress: job.clientAddress ?? "",
      surface: job.surface ?? "",
      lightingType: job.lightingType ?? "",
      ceilingHeight: job.ceilingHeight ?? "",
      estimatedLedCount: job.estimatedLedCount ?? "",
      dehumidifierCount: job.dehumidifierCount ?? null,
      timerCount: job.timerCount ?? null,
      needsCounter: !!job.needsCounter,
      needsGenerator: !!job.needsGenerator,
      notes: job.notes ?? "",
      partnerId: job.partnerId ?? "",
      installerId: job.installerId ?? "",
      commercialId: job.commercialId ?? "",
      status: job.status,
      date: job.date ?? "",
      time: job.time ?? "",
      stockRegionId: job.stockRegionId ?? "",
      stockToPrepare: normalizedStock,
    };
  };

  const isEditingJobDirty = useMemo(() => {
    if (!editingJob || !editingJobInitial) return false;
    return (
      JSON.stringify(normalizeEditableJob(editingJob)) !==
      JSON.stringify(normalizeEditableJob(editingJobInitial))
    );
  }, [editingJob, editingJobInitial]);

  const handleAddJob = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidDossierType(newJob.dossierType)) {
      alert(t("admin.chooseJobTypeAlert"));
      return;
    }
    const res = await addJobAddressRef.current?.validate();
    if (!res?.ok) return;
    setAddJobSubmitting(true);
    try {
      const prep = assignImmediately ? immediateAssignment.stockToPrepare : undefined;
      const stockToPrepare =
        prep && Object.keys(prep).length
          ? Object.fromEntries(
              Object.entries(prep).filter(([, qty]) => qty != null && Number(qty) > 0)
            )
          : undefined;
      const jobPayload = {
        ...newJob,
        clientName: buildClientNameForDossierType(newJob.dossierType, {
          clientName: newJob.clientName,
          clientFirstName: newJob.clientFirstName,
          clientLastName: newJob.clientLastName,
        }),
        dehumidifierCount:
          newJob.dehumidifierCount.trim() !== "" ? Number(newJob.dehumidifierCount) : undefined,
        timerCount: newJob.timerCount.trim() !== "" ? Number(newJob.timerCount) : undefined,
        clientAddress: res.label,
        status: (assignImmediately ? "scheduled" : "pending") as Job["status"],
        stockRegionId: newJob.stockRegionId || selectedRegionId || undefined,
        ...(assignImmediately
          ? {
              installerId: immediateAssignment.installerId,
              date: immediateAssignment.date,
              time: immediateAssignment.time,
              stockToPrepare,
            }
          : {}),
      };
      const missing = getMissingFieldsForDossierType({
        ...jobPayload,
        clientAddress: res.label,
      });
      if (missing.length > 0) {
        alert(`Champs obligatoires manquants : ${missing.join(", ")}`);
        return;
      }
      delete (jobPayload as Record<string, unknown>).clientFirstName;
      delete (jobPayload as Record<string, unknown>).clientLastName;
      onAdminAddJob(jobPayload);
      setIsAddJobModalOpen(false);
      resetAddJobForm();
    } finally {
      setAddJobSubmitting(false);
    }
  };
  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingJob || editingJobReadOnly) return;
    if (!isEditingJobDirty) return;

    const hadExistingReport =
        (editingJob.stockUsedNeuf != null && Object.keys(editingJob.stockUsedNeuf).length > 0) ||
        (editingJob.stockUsedRemplacement != null && Object.keys(editingJob.stockUsedRemplacement).length > 0) ||
        (editingJob.stockUsed != null && Object.keys(editingJob.stockUsed).length > 0);

      // Aligner la logique admin sur celle du poseur :
      // un passage en "terminé" sans rapport doit ouvrir le flux de clôture (déduction stock).
      if (editingJob.status === "completed" && !hadExistingReport && onValidateJob) {
        setValidatingJob(editingJob);
        return;
      }

      const prep = editingJob.stockToPrepare
        ? Object.fromEntries(
            Object.entries(editingJob.stockToPrepare).filter(
              ([, qty]) => qty != null && Number(qty) > 0
            )
          )
        : undefined;
      const normalizedDate = editingJob.date?.trim() ? editingJob.date : undefined;
      const hasDate = !!normalizedDate;
      const isTerminalStatus =
        editingJob.status === "completed" ||
        editingJob.status === "cancelled" ||
        editingJob.status === "in_progress";

      const finalStatus = resolveJobStatusFromScheduling(editingJob.status, normalizedDate);

      const dateOut = isTerminalStatus || hasDate ? normalizedDate : undefined;
      const timeOut = isTerminalStatus
        ? editingJob.time
        : finalStatus === "scheduled"
          ? editingJob.time
          : hasDate
            ? editingJob.time
            : undefined;

      const result = await onUpdateJob({
        ...editingJob,
        status: finalStatus,
        date: dateOut,
        time: timeOut,
        stockRegionId: editingJob.stockRegionId ?? undefined,
        commercialId: editingJob.commercialId?.trim() || undefined,
        stockToPrepare: prep && Object.keys(prep).length ? prep : undefined,
      });
      if (result && typeof result === "object" && "error" in result && result.error) {
        return;
      }
    closeEditJobModal();
  };

  const handleAiSuggestJob = async (
    type: "notes" | "led_estimate",
    context: "create" | "edit"
  ) => {
    const key: typeof aiSuggestLoading =
      context === "create"
        ? (type === "notes" ? "create_notes" : "create_led")
        : (type === "notes" ? "edit_notes" : "edit_led");
    setAiSuggestLoading(key);
    setAiSuggestError(null);
    try {
      let jobPayload: { clientAddress?: string; surface?: string; lightingType?: string; ceilingHeight?: string };
      if (context === "create") {
        const addrRes = await addJobAddressRef.current?.validate().catch(() => null);
        jobPayload = {
          clientAddress: addrRes && addrRes.ok ? addrRes.label : "",
          surface: newJob.surface,
          lightingType: newJob.lightingType,
          ceilingHeight: newJob.ceilingHeight,
        };
      } else {
        if (!editingJob) return;
        jobPayload = {
          clientAddress: editingJob.clientAddress,
          surface: editingJob.surface,
          lightingType: editingJob.lightingType,
          ceilingHeight: editingJob.ceilingHeight,
        };
      }
      const { suggestion } = await invokeAiSuggestJob({ type, job: jobPayload });
      if (context === "create") {
        if (type === "notes") setNewJob((j) => ({ ...j, notes: suggestion }));
        else setNewJob((j) => ({ ...j, estimatedLedCount: suggestion }));
      } else if (editingJob) {
        if (type === "notes") setEditingJob((j) => (j ? { ...j, notes: suggestion } : j));
        else setEditingJob((j) => (j ? { ...j, estimatedLedCount: suggestion } : j));
      }
    } catch (err) {
      setAiSuggestError(err instanceof Error ? err.message : t("admin.errorAiSuggest"));
    } finally {
      setAiSuggestLoading(null);
    }
  };

  const handleAiSuggestStockPrepare = async (context: "edit" | "create") => {
    setAiStockPrepareLoading(context);
    setAiStockPrepareError(null);
    try {
      let regionId: string | undefined;
      let jobPayload: { surface?: string; lightingType?: string; ceilingHeight?: string; estimatedLedCount?: string };
      if (context === "edit" && editingJob) {
        regionId =
          editingJob.stockRegionId ??
          (editingJob.installerId
            ? installers.find((i: User) => i.id === editingJob.installerId)?.preferences?.defaultStockRegionId
            : undefined) ??
          stockRegions[0]?.id;
        jobPayload = {
          surface: editingJob.surface,
          lightingType: editingJob.lightingType,
          ceilingHeight: editingJob.ceilingHeight,
          estimatedLedCount: editingJob.estimatedLedCount,
        };
      } else if (context === "create") {
        regionId = newJob.stockRegionId || selectedRegionId || undefined;
        jobPayload = {
          surface: newJob.surface,
          lightingType: newJob.lightingType,
          ceilingHeight: newJob.ceilingHeight,
          estimatedLedCount: newJob.estimatedLedCount,
        };
      } else return;
      const partnerIdForJob = context === "edit" ? editingJob?.partnerId ?? null : newJob.partnerId ?? null;
      const regionStock = getStockForRegionAndPartner(regionId, partnerIdForJob);
      if (regionStock.length === 0) {
        setAiStockPrepareError(t("admin.noItemInRegion"));
        return;
      }
      const inventoryNames = regionStock.map((item) => ({ id: item.id, name: item.name }));
      const recentJobStock = jobs
        .filter((j) => j.status === "completed" && j.stockUsed && Object.keys(j.stockUsed).length > 0)
        .slice(0, 10)
        .map((j) => ({
          surface: j.surface,
          lightingType: j.lightingType,
          stockUsed: j.stockUsed ?? {},
        }));
      const { suggestion } = await invokeAiSuggestStockPrepare({
        job: jobPayload,
        inventoryNames,
        recentJobStock,
      });
      if (context === "edit" && editingJob) {
        setEditingJob((j) =>
          j ? { ...j, stockToPrepare: { ...(j.stockToPrepare ?? {}), ...suggestion } } : j
        );
      } else {
        setImmediateAssignment((prev) => ({
          ...prev,
          stockToPrepare: { ...prev.stockToPrepare, ...suggestion },
        }));
      }
    } catch (err) {
      setAiStockPrepareError(err instanceof Error ? err.message : t("admin.errorAiStock"));
    } finally {
      setAiStockPrepareLoading(false);
    }
  };

  /** Modifier un dossier : hasPermission(canEditJobs) OU créateur du dossier (partenaire). */
  const canEditJob = (job: Job) =>
    hasPermission(currentUser, "canEditJobs") || currentUser?.id === job.partnerId;

  /** Supprimer un dossier : uniquement si hasPermission(canDeleteJobs). */
  const canDeleteJob = () => hasPermission(currentUser, "canDeleteJobs");

  const statusLabelToValue: Record<string, "pending" | "scheduled" | "in_progress" | "completed" | "cancelled"> = {
    "en attente": "pending",
    "planifié": "scheduled",
    "en cours": "in_progress",
    "terminé": "completed",
    "annulé": "cancelled",
  };
  const parseImportRow = (row: Record<string, string>): Record<string, unknown> => {
    const get = (key: string) => (row[key] ?? "").trim();
    const stat = (get("Statut") || get("statut")).toLowerCase();
    return {
      clientName: get("Client") || get("client"),
      clientPhone: get("Téléphone") || get("telephone") || get("téléphone"),
      clientAddress: get("Adresse") || get("adresse"),
      status: statusLabelToValue[stat] ?? "pending",
      date: get("Date") || get("date") || undefined,
      time: get("Heure") || get("heure") || undefined,
      surface: get("Surface") || get("surface") || undefined,
      lightingType: get("Type d'éclairage") || get("type d'éclairage") || get("lightingType") || undefined,
      ceilingHeight: get("Hauteur plafond") || get("hauteur plafond") || get("ceilingHeight") || undefined,
      notes: get("Notes") || get("notes") || undefined,
    };
  };
  const parseFileToJobs = (file: File): Promise<Array<Record<string, unknown>>> => {
    return new Promise((resolve, reject) => {
      const isCsv = /\.csv$/i.test(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = (e.target?.result as string) || "";
          if (isCsv) {
            const lines = text.split(/\r?\n/).filter((l) => l.trim());
            if (lines.length < 2) {
              resolve([]);
              return;
            }
            const headers = parseCSVLine(lines[0]);
            const rows: Array<Record<string, unknown>> = [];
            for (let i = 1; i < lines.length; i++) {
              const cells = parseCSVLine(lines[i]);
              const row: Record<string, string> = {};
              headers.forEach((h, idx) => {
                row[h] = cells[idx] ?? "";
              });
              rows.push(parseImportRow(row));
            }
            resolve(rows);
          } else {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const wb = XLSX.read(data, { type: "array" });
            const firstSheet = wb.Sheets[wb.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json<Record<string, string>>(firstSheet, { defval: "" });
            const rows = json.map((row) => parseImportRow(row));
            resolve(rows);
          }
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error(t("admin.readFileError")));
      if (isCsv) reader.readAsText(file, "UTF-8");
      else reader.readAsArrayBuffer(file);
    });
  };
  function parseCSVLine(line: string): string[] {
    const out: string[] = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else inQuotes = !inQuotes;
      } else if (((c === "," || c === ";") && !inQuotes) || (c === "\n" && !inQuotes)) {
        out.push(cur.trim());
        cur = "";
        if (c === "\n") break;
      } else cur += c;
    }
    out.push(cur.trim());
    return out;
  }
  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportError(null);
    setImportLoading(true);
    try {
      const rows = await parseFileToJobs(file);
      const valid = rows.filter((r) => (String(r.clientName ?? "").trim() || String(r.clientAddress ?? "").trim()));
      if (valid.length === 0) {
        setImportError(t("admin.noValidRowImport"));
        return;
      }
      await onAdminImportJobs(valid);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : t("admin.errorImport"));
    } finally {
      setImportLoading(false);
    }
  };

  const overviewCategoryCounts = useMemo(() => countJobsByCategory(jobs), [jobs]);

  /** Données pour le suivi de budget (chantiers terminés avec facture partenaire) */
  const budgetStats = useMemo(() => {
    const withInvoice = jobs.filter(
      (j: Job) => j.status === "completed" && j.partnerInvoiceTotalCents != null && j.partnerInvoiceTotalCents > 0
    );
    const totalCents = withInvoice.reduce((s, j) => s + (j.partnerInvoiceTotalCents ?? 0), 0);
    const count = withInvoice.length;
    const avgCents = count > 0 ? Math.round(totalCents / count) : 0;

    const byPartnerMap = new Map<string, { totalCents: number; count: number }>();
    for (const j of withInvoice) {
      const pid = j.partnerId ?? "_sans_partenaire_";
      const cur = byPartnerMap.get(pid) ?? { totalCents: 0, count: 0 };
      cur.totalCents += j.partnerInvoiceTotalCents ?? 0;
      cur.count += 1;
      byPartnerMap.set(pid, cur);
    }
    const byPartner = Array.from(byPartnerMap.entries())
      .map(([partnerId, data]) => ({
        partnerId,
        name: partnerId === "_sans_partenaire_" ? "Sans partenaire" : (partners.find((p: User) => p.id === partnerId)?.name ?? "Partenaire"),
        ...data,
      }))
      .sort((a, b) => b.totalCents - a.totalCents);

    const byRegionMap = new Map<string, { totalCents: number; count: number }>();
    for (const j of withInvoice) {
      const rid = j.stockRegionId ?? "_sans_region_";
      const cur = byRegionMap.get(rid) ?? { totalCents: 0, count: 0 };
      cur.totalCents += j.partnerInvoiceTotalCents ?? 0;
      cur.count += 1;
      byRegionMap.set(rid, cur);
    }
    const byRegion = Array.from(byRegionMap.entries())
      .map(([regionId, data]) => ({
        regionId,
        name: regionId === "_sans_region_" ? "Non assigné" : (stockRegions.find((r: StockRegion) => r.id === regionId)?.name ?? "Région"),
        ...data,
      }))
      .sort((a, b) => b.totalCents - a.totalCents);

    const now = new Date();
    const monthKeys: string[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      monthKeys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    const byMonthMap = new Map<string, { totalCents: number; count: number }>();
    for (const key of monthKeys) byMonthMap.set(key, { totalCents: 0, count: 0 });
    for (const j of withInvoice) {
      const created = j.createdAt ? new Date(j.createdAt) : null;
      if (!created) continue;
      const key = `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, "0")}`;
      if (!byMonthMap.has(key)) continue;
      const cur = byMonthMap.get(key)!;
      cur.totalCents += j.partnerInvoiceTotalCents ?? 0;
      cur.count += 1;
      byMonthMap.set(key, cur);
    }
    const byMonth = monthKeys.map((key) => {
      const [y, m] = key.split("-");
      const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
      return {
        monthKey: key,
        label: d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }),
        ...(byMonthMap.get(key) ?? { totalCents: 0, count: 0 }),
      };
    });

    const maxMonthCents = Math.max(1, ...byMonth.map((m) => m.totalCents));
    const maxPartnerCents = Math.max(1, ...byPartner.map((p) => p.totalCents));
    const maxRegionCents = Math.max(1, ...byRegion.map((r) => r.totalCents));

    return {
      totalCents,
      count,
      avgCents,
      byPartner,
      byRegion,
      byMonth,
      maxMonthCents,
      maxPartnerCents,
      maxRegionCents,
      lastInvoices: [...withInvoice]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 5),
    };
  }, [jobs, partners, stockRegions]);

  /** Visite guidée complète : ordre d’utilisation (workflow), pas l’ordre des onglets. Au clic "Suivant" après un bouton d’onglet, on affiche cet onglet. */
  const onboardingTourSteps = useMemo((): ProductTourStep[] => {
    const steps: ProductTourStep[] = [
      {
        target: "[data-tour=\"sidebar-brand\"]",
        title: "Bienvenue dans votre Régie Admin",
        content: "Ce menu est le cœur de votre CRM. Nous allons parcourir les parties dans l’ordre où vous les utiliserez au quotidien.",
        placement: "right",
        beforeStep: () => { setIsMobileMenuOpen(true); requestTabChange("overview"); },
        onLeaveToNext: () => requestTabChange("overview"),
      },
      {
        target: "[data-tour=\"sidebar-overview\"]",
        title: t("admin.home"),
        content: "Le tableau de bord : accès rapide aux dossiers à programmer, chantiers en cours, planning, stock bas et budget.",
        placement: "right",
        onLeaveToNext: () => requestTabChange("overview"),
      },
      {
        target: "[data-tour=\"sidebar-jobs\"]",
        title: t("admin.jobs"),
        content: "Les dossiers (chantiers) : créez des chantiers, filtrez par statut, région, poseur ou partenaire. La recherche IA permet des requêtes en langage naturel.",
        placement: "right",
        onLeaveToNext: () => requestTabChange("jobs"),
      },
      {
        target: "[data-tour=\"sidebar-planning\"]",
        title: t("admin.planning"),
        content: "Le calendrier affiche les chantiers planifiés. Glisser-déposer pour réorganiser, et voir les RDV déshumidificateur en un coup d’œil.",
        placement: "right",
        onLeaveToNext: () => requestTabChange("planning"),
      },
      {
        target: "[data-tour=\"sidebar-regions\"]",
        title: t("admin.regions"),
        content: "Les régions structurent votre activité. Créez des régions, assignez des poseurs, gérez le stock et les dossiers par région.",
        placement: "right",
        onLeaveToNext: () => requestTabChange("regions"),
      },
      {
        target: "[data-tour=\"sidebar-partners\"]",
        title: t("admin.partners"),
        content: "Les partenaires sont vos clients (enseignes, etc.). Chaque partenaire a son stock, ses dossiers et ses factures dans l’onglet Budget.",
        placement: "right",
        onLeaveToNext: () => requestTabChange("partners"),
      },
      {
        target: "[data-tour=\"sidebar-installers\"]",
        title: t("admin.installers"),
        content: "Les poseurs sont assignés à des régions. Gérez ici leurs accès, leur région par défaut et consultez leurs chantiers.",
        placement: "right",
        onLeaveToNext: () => requestTabChange("installers"),
      },
      {
        target: "[data-tour=\"sidebar-budget\"]",
        title: t("admin.budget"),
        content: "Le budget regroupe les factures par partenaire et par région. Définissez les prix des articles et générez devis et factures PDF.",
        placement: "right",
        onLeaveToNext: () => requestTabChange("budget"),
      },
    ];
    if (canSeeStockTab(currentUser)) {
      steps.push({
        target: "[data-tour=\"sidebar-stock\"]",
        title: t("admin.stock"),
        content: "Le stock par région et par partenaire. Définissez des seuils d’alerte pour les ruptures. Les poseurs voient le stock de leur région.",
        placement: "right",
        onLeaveToNext: () => requestTabChange("stock"),
      });
    }
    if (canSeeAccessTab(currentUser)) {
      steps.push({
        target: "[data-tour=\"sidebar-access\"]",
        title: t("admin.access"),
        content: "Gérez les accès partenaires et poseurs : créez des comptes, assignez des régions et des partenaires.",
        placement: "right",
        onLeaveToNext: () => requestTabChange("access"),
      });
    }
    if (canSeeTeamTab(currentUser)) {
      steps.push({
        target: "[data-tour=\"sidebar-team\"]",
        title: t("admin.team"),
        content: "Invitez des membres à votre équipe admin et définissez leurs permissions (dossiers, stock, accès, équipe).",
        placement: "right",
        onLeaveToNext: () => requestTabChange("team"),
      });
    }
    steps.push(
      {
        target: "[data-tour=\"header-profile\"]",
        title: "Profil et réglages",
        content: "Modifiez votre profil, la langue, le thème, l’abonnement et les réglages de l’entreprise (SIRET, adresse, etc.).",
        placement: "bottom",
      },
      {
        target: "[data-tour=\"sidebar-logout\"]",
        title: t("admin.logout"),
        content: "Déconnexion sécurisée. Vous devrez vous reconnecter pour accéder à nouveau au CRM.",
        placement: "right",
      }
    );
    return steps;
  }, [t, currentUser]);

  const handleTourComplete = useCallback(async () => {
    setOnboardingDismissedLocal(true);
    await updateCurrentUserPreferences({ ...profile?.preferences, onboardingDone: true });
  }, [profile?.preferences, updateCurrentUserPreferences]);

  // Si la page Settings est ouverte, afficher uniquement celle-ci (après tous les hooks)
  if (showSettings) {
    return (
      <div className="w-full flex justify-center">
        <SettingsPage
          user={updatedUser}
          jobs={jobs}
          onBack={() => setShowSettings(false)}
          onUserUpdate={(user) => setUpdatedUser(user)}
          ownerId={ownerId}
          onManageSubscription={onManageSubscription}
          subscriptionError={subscriptionError}
          onImportJobs={onAdminImportJobs}
          isOwner={isOwner}
          onDeleteAccount={onDeleteAccount}
          onExportAllData={onExportAllData}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex relative">
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Visite guidée complète : spotlight (tout noir sauf l'élément) + flèches + étapes */}
      {showOnboarding && (
        <ProductTour
          tourId="admin-onboarding"
          steps={onboardingTourSteps}
          isOpen={true}
          onComplete={handleTourComplete}
          onSkip={handleTourComplete}
        />
      )}

      {/* Sidebar bleue */}
      <div
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-indigo-800 border-r border-indigo-700/50 p-4 flex flex-col shadow-2xl transform transition-transform duration-300 ease-in-out md:translate-x-0 ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center gap-3 px-2 mb-8 mt-2" data-tour="sidebar-brand">
          <div className="p-2 bg-white/15 rounded-lg">
            <LayoutDashboard className="w-6 h-6 text-white" />
          </div>
          <span className="font-bold text-lg text-white">{t("admin.regieAdmin")}</span>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="ml-auto md:hidden p-1 text-indigo-200 hover:text-white hover:bg-white/10 rounded-lg"
            aria-label="Fermer le menu"
          >
            <XCircle size={18} />
          </button>
        </div>

        <nav className="flex-1 space-y-3 overflow-y-auto">
          <div className="space-y-0.5">
            <button
              data-tour="sidebar-overview"
              onClick={() => requestTabChange("overview")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
                activeTab === "overview" ? "bg-white/20 text-white shadow-inner font-medium" : "text-indigo-100 hover:bg-white/10 hover:text-white"
              }`}
            >
              <LayoutDashboard size={20} /> {t("admin.home")}
            </button>
          </div>

          {canSeeJobsTab(currentUser) && (
            <div className="space-y-0.5">
              <p className="px-4 py-1.5 text-[11px] font-medium uppercase tracking-wider text-indigo-300/90">{t("admin.workSection")}</p>
              <button
                data-tour="sidebar-jobs"
                onClick={() => requestTabChange("jobs")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
                  activeTab === "jobs" ? "bg-white/20 text-white shadow-inner font-medium" : "text-indigo-100 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Briefcase size={20} /> {t("admin.jobs")}
              </button>
              <button
                data-tour="sidebar-planning"
                onClick={() => requestTabChange("planning")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
                  activeTab === "planning" ? "bg-white/20 text-white shadow-inner font-medium" : "text-indigo-100 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Calendar size={20} /> {t("admin.planning")}
              </button>
              <button
                data-tour="sidebar-regions"
                onClick={() => requestTabChange("regions")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
                  activeTab === "regions" ? "bg-white/20 text-white shadow-inner font-medium" : "text-indigo-100 hover:bg-white/10 hover:text-white"
                }`}
              >
                <MapPin size={20} /> {t("admin.regions")}
              </button>
              <button
                data-tour="sidebar-partners"
                onClick={() => requestTabChange("partners")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
                  activeTab === "partners" ? "bg-white/20 text-white shadow-inner font-medium" : "text-indigo-100 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Building2 size={20} /> {t("admin.partners")}
              </button>
              <button
                data-tour="sidebar-installers"
                onClick={() => requestTabChange("installers")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
                  activeTab === "installers" ? "bg-white/20 text-white shadow-inner font-medium" : "text-indigo-100 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Wrench size={20} /> {t("admin.installers")}
              </button>
              <button
                data-tour="sidebar-budget"
                onClick={() => requestTabChange("budget")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
                  activeTab === "budget" ? "bg-white/20 text-white shadow-inner font-medium" : "text-indigo-100 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Euro size={20} /> {t("admin.budget")}
              </button>
            </div>
          )}

          {canSeeStockTab(currentUser) && (
            <div className="space-y-0.5">
              <button
                data-tour="sidebar-stock"
                onClick={() => requestTabChange("stock")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
                  activeTab === "stock" ? "bg-white/20 text-white shadow-inner font-medium" : "text-indigo-100 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Package size={20} />
                <span className="relative">
                  {t("admin.stock")}
                  {totalLowStockCount > 0 && (
                    <span className="absolute -top-1 -right-6 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-medium text-white">
                      {totalLowStockCount}
                    </span>
                  )}
                </span>
              </button>
            </div>
          )}

          {(canSeeAccessTab(currentUser) || canSeeTeamTab(currentUser)) && (
            <div className="space-y-0.5">
              <p className="px-4 py-1.5 text-[11px] font-medium uppercase tracking-wider text-indigo-300/90">{t("admin.organizationSection")}</p>
              {canSeeAccessTab(currentUser) && (
                <button
                  data-tour="sidebar-access"
                  onClick={() => requestTabChange("access")}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
                    activeTab === "access" ? "bg-white/20 text-white shadow-inner font-medium" : "text-indigo-100 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <Users size={20} /> {t("admin.access")}
                </button>
              )}
              {canSeeTeamTab(currentUser) && (
                <button
                  data-tour="sidebar-team"
                  onClick={() => requestTabChange("team")}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
                    activeTab === "team" ? "bg-white/20 text-white shadow-inner font-medium" : "text-indigo-100 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <UserCircle size={20} /> {t("admin.team")}
                </button>
              )}
            </div>
          )}
        </nav>
        <div className="pt-4 border-t border-indigo-700/50">
          <button
            data-tour="sidebar-logout"
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-200 hover:bg-red-500/20 hover:text-white transition-colors"
          >
            <LogOut size={20} /> {t("admin.logout")}
          </button>
        </div>
      </div>

      <div className="flex-1 md:ml-64 p-4 md:p-6 lg:p-8 min-w-0 w-full">
        {/* Header — titre + profil uniquement */}
        <header className="z-20 mb-6 md:mb-8 flex justify-between items-center gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-2.5 text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 rounded-xl transition-colors"
              aria-label="Ouvrir le menu"
            >
              <Menu size={22} />
            </button>
            <h1 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white truncate">
              {activeTab === "overview"
                ? t("admin.home")
                : activeTab === "jobs"
                ? t("admin.jobs")
                : activeTab === "stock"
                ? t("admin.stock")
                : activeTab === "team"
                ? t("admin.team")
                : activeTab === "planning"
                ? t("admin.planning")
                : activeTab === "regions"
                ? t("admin.regions")
                : activeTab === "partners"
                ? t("admin.partners")
                : activeTab === "installers"
                ? t("admin.installers")
                : activeTab === "budget"
                ? t("admin.budget")
                : activeTab === "access"
                ? t("admin.access")
                : activeTab}
            </h1>
          </div>
          <div data-tour="header-profile">
            <ProfileBubble
              user={updatedUser}
              onSettingsClick={() => setShowSettings(true)}
              onLogout={onLogout}
            ownerLocale={updatedUser.is_owner ? ownerProfile?.preferences?.locale : undefined}
            onOwnerLocaleChange={
              updatedUser.is_owner
                ? (locale) => updateOwnerPreferences({ ...ownerProfile?.preferences, locale })
                : undefined
            }
            />
          </div>
        </header>

        {activeTab === "overview" && (
          <AdminOverview
            canViewJobs={hasPermission(currentUser, "canViewJobs")}
            canSeeJobsTab={canSeeJobsTab(currentUser)}
            canManageStock={hasPermission(currentUser, "canManageStock")}
            canViewMainHistory={hasPermission(currentUser, "canViewMainHistory")}
            canCreateJobs={hasPermission(currentUser, "canCreateJobs")}
            categoryCounts={overviewCategoryCounts}
            budgetTotalCents={budgetStats.totalCents}
            budgetCount={budgetStats.count}
            budgetByMonth={budgetStats.byMonth}
            lowStockCount={totalLowStockCount}
            logs={logs}
            onClearLogs={onClearLogs}
            onOpenJobsCategory={openOverviewJobsCategory}
            onOpenPlanning={() => {
              if (!canSeeJobsTab(currentUser)) return;
              requestTabChange("planning");
            }}
            onOpenBudget={() => {
              if (!canSeeJobsTab(currentUser)) return;
              requestTabChange("budget");
            }}
            onOpenStock={() => {
              if (!canSeeStockTab(currentUser)) return;
              requestTabChange("stock");
            }}
            onNewJob={() => {
              if (!hasPermission(currentUser, "canCreateJobs")) return;
              setIsAddJobModalOpen(true);
            }}
            labels={{
              title: t("admin.overviewTitle"),
              subtitle: t("admin.overviewSubtitle"),
              newJob: t("admin.newJob"),
              toSchedule: t("admin.overviewToSchedule"),
              toScheduleHint: t("admin.overviewToScheduleHint"),
              inProgress: t("admin.statusInProgress"),
              inProgressHint: t("admin.overviewInProgressHint"),
              done: t("admin.overviewCompleted"),
              doneHint: t("admin.overviewDoneHint"),
              planning: t("admin.planning"),
              planningHint: t("admin.overviewPlanningHint"),
              lowStock: t("admin.overviewLowStock"),
              lowStockHint: t("admin.overviewLowStockHint"),
              lowStockOk: t("admin.overviewLowStockOk"),
              budget: t("admin.budgetPartners"),
              seeDetail: t("admin.seeDetail"),
              noInvoiceYet: t("admin.noInvoiceYet"),
              budgetJobsLine: (count) => t("admin.budgetJobsLine", { count }),
              monthlyEvolution: t("admin.monthlyEvolution"),
              revenueTitle: t("admin.revenueTitle"),
              caTotal: t("admin.caTotal"),
              caThisMonth: t("admin.caThisMonth"),
              avgTicket: t("admin.avgTicket"),
              invoicesCount: t("admin.invoicesCount"),
              trendLabel: t("admin.trendLabel"),
              caChartHint: t("admin.caChartHint"),
              recentActivity: t("admin.overviewRecentActivity"),
              clear: t("admin.overviewClear"),
              noRecentActivity: t("admin.noRecentActivity"),
              noAccess: t("admin.overviewNoAccess"),
            }}
          />
        )}

        {activeTab === "planning" && canSeeJobsTab(currentUser) && (
          <AdminPlanningPanel
            jobs={jobs}
            installers={installers}
            partners={partners}
            stockRegions={stockRegions}
            planningRegionId={planningRegionId}
            planningPartnerId={planningPartnerId}
            planningInstallerId={planningInstallerId}
            planningInProgressJobs={planningInProgressJobs}
            onPlanningRegionIdChange={setPlanningRegionId}
            onPlanningPartnerIdChange={setPlanningPartnerId}
            onPlanningInstallerIdChange={setPlanningInstallerId}
            onJobSelect={(job) => openEditJobModal(job)}
            onUpdateJob={onUpdateJob}
            onAddJobAtDate={(date) => {
              setImmediateAssignment((prev) => ({ ...prev, date, time: "09:00" }));
              setAssignImmediately(true);
              setIsAddJobModalOpen(true);
            }}
            labels={{
              filterPlanning: t("admin.filterPlanning"),
              regionLabel: t("admin.regionLabel"),
              partnerLabel: t("admin.partnerLabel"),
              installerLabel: t("admin.installerLabel"),
              allRegions: t("admin.allRegions"),
              allPartners: t("admin.allPartners"),
              allInstallers: t("admin.allInstallers"),
              jobsInProgress: t("admin.jobsInProgress"),
              noJobsInProgress: t("admin.noJobsInProgress"),
            }}
          />
        )}

        {activeTab === "jobs" && canSeeJobsTab(currentUser) && (
          <div className="space-y-4 relative z-10">
            {(() => {
              const jobsShowListTools = jobsViewMode === "summary" || jobsNavScreen === "list";
              return (
            <div className="rounded-2xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/10 p-4 shadow-sm dark:shadow-none">
              <div className="flex flex-col gap-4">
                {jobsShowListTools ? (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="relative flex-1 min-w-0 flex gap-2 items-center">
                    <div className="relative flex-1 min-w-0">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400 w-5 h-5 pointer-events-none" />
                      <input
                        type="text"
                        placeholder={t("admin.searchPlaceholder")}
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setAiNaturalSearchError(null); }}
                        onKeyDown={(e) => { if (e.key === "Enter" && searchTerm.trim()) { e.preventDefault(); handleSmartSearch(); } }}
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50/80 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleSmartSearch}
                      disabled={!searchTerm.trim() || aiNaturalSearchLoading}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-blue-500/15 dark:bg-blue-400/15 text-blue-600 dark:text-blue-400 hover:bg-blue-500/25 dark:hover:bg-blue-400/25 transition-colors disabled:opacity-50 text-sm font-medium"
                      title="Interpréter la recherche avec l'IA (filtres statut, partenaire, poseur, dates, région)"
                    >
                      {aiNaturalSearchLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                      Recherche IA
                    </button>
                  </div>
                  {stockRegions.length > 0 && (
                    <select
                      value={jobsRegionFilterId}
                      onChange={(e) => setJobsRegionFilterId(e.target.value)}
                      className="sm:w-40 pl-4 pr-9 py-2.5 bg-gray-50/80 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 outline-none"
                    >
                      <option value="">{t("admin.allRegionsShort")}</option>
                      {stockRegions.map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  )}
                  {hasPermission(currentUser, "canCreateJobs") && (
                    <button
                      onClick={() => setIsAddJobModalOpen(true)}
                      className="shrink-0 flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium text-[15px] transition-colors shadow-lg shadow-blue-500/25"
                    >
                      <Plus size={20} strokeWidth={2} /> {t("admin.newShort")}
                    </button>
                  )}
                </div>
                ) : (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    {t("admin.jobsHubHint")}
                  </p>
                  {hasPermission(currentUser, "canCreateJobs") && (
                    <button
                      onClick={() => setIsAddJobModalOpen(true)}
                      className="shrink-0 flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium text-[15px] transition-colors shadow-lg shadow-blue-500/25"
                    >
                      <Plus size={20} strokeWidth={2} /> {t("admin.newShort")}
                    </button>
                  )}
                </div>
                )}
                {jobsShowListTools && hasActiveFilters && (
                  <div className="flex flex-wrap items-center gap-2">
                    {searchTerm.trim() && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 text-[12px] font-medium">
                        {t("admin.searchLabel")} {searchTerm.trim().length > 20 ? searchTerm.trim().slice(0, 20) + "…" : searchTerm.trim()}
                        <button type="button" onClick={() => setSearchTerm("")} className="hover:bg-blue-200/50 dark:hover:bg-blue-400/20 rounded p-0.5" aria-label={t("admin.clearLabel")}>×</button>
                      </span>
                    )}
                    {jobsStatusFilter && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[12px] font-medium">
                        {t("admin.filterStatus")} {jobsStatusFilter === "pending" ? t("admin.statusPending") : jobsStatusFilter === "scheduled" ? t("admin.statusScheduled") : jobsStatusFilter === "in_progress" ? t("admin.statusInProgress") : jobsStatusFilter === "completed" ? t("admin.statusCompleted") : t("admin.statusCancelled")}
                        <button type="button" onClick={() => setJobsStatusFilter("")} className="hover:bg-amber-200/50 dark:hover:bg-amber-400/20 rounded p-0.5" aria-label={t("admin.clearLabel")}>×</button>
                      </span>
                    )}
                    {jobsRegionFilterId && stockRegions.find((r) => r.id === jobsRegionFilterId)?.name && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[12px] font-medium">
                        {t("admin.filterRegion")} {stockRegions.find((r) => r.id === jobsRegionFilterId)?.name}
                        <button type="button" onClick={() => setJobsRegionFilterId("")} className="hover:bg-emerald-200/50 dark:hover:bg-emerald-400/20 rounded p-0.5" aria-label={t("admin.clearLabel")}>×</button>
                      </span>
                    )}
                    {jobsInstallerId && installers.find((i: User) => i.id === jobsInstallerId)?.name && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 text-[12px] font-medium">
                        {t("admin.filterInstaller")} {installers.find((i: User) => i.id === jobsInstallerId)?.name}
                        <button type="button" onClick={() => setJobsInstallerId("")} className="hover:bg-violet-200/50 dark:hover:bg-violet-400/20 rounded p-0.5" aria-label={t("admin.clearLabel")}>×</button>
                      </span>
                    )}
                    {(jobsDateFrom || jobsDateTo) && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 text-[12px] font-medium">
                        Date : {jobsDateFrom || "…"} → {jobsDateTo || "…"}
                        <button type="button" onClick={() => { setJobsDateFrom(""); setJobsDateTo(""); }} className="hover:bg-sky-200/50 dark:hover:bg-sky-400/20 rounded p-0.5" aria-label={t("admin.clearLabel")}>×</button>
                      </span>
                    )}
                  </div>
                )}
                {jobsShowListTools && aiNaturalSearchError && (
                  <p className="text-[13px] text-red-500 dark:text-red-400 mt-1">{aiNaturalSearchError}</p>
                )}
                <div className={`flex flex-wrap items-center justify-between gap-2 ${jobsShowListTools ? "pt-2 border-t border-gray-100 dark:border-white/5" : ""}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex p-0.5 rounded-lg bg-gray-100/80 dark:bg-white/[0.06]">
                      <button type="button" onClick={() => setJobsFolderId("__all__")} className={`px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors ${jobsFolderId === "__all__" ? "bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm" : "text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"}`}>{t("admin.allFolder")}</button>
                      <button type="button" onClick={() => setJobsFolderId("__admin__")} className={`px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors ${jobsFolderId === "__admin__" ? "bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm" : "text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"}`}>{t("admin.folderAdmin")}</button>
                      {partners.length > 0 && (
                        <select value={jobsFolderId && jobsFolderId !== "__all__" && jobsFolderId !== "__admin__" ? jobsFolderId : ""} onChange={(e) => setJobsFolderId(e.target.value || "__all__")} className="px-3 py-1.5 rounded-md text-[13px] font-medium bg-transparent border-0 focus:ring-0 outline-none text-gray-600 dark:text-slate-300 cursor-pointer">
                          <option value="">Partenaire…</option>
                          {partners.map((p: User) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      )}
                    </div>
                    {(hasActiveFilters || (jobsFolderId && jobsFolderId !== "__all__")) && (
                      <button
                        type="button"
                        onClick={handleResetDossiersPrefs}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-medium text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                        title="Réinitialiser filtres et vue"
                      >
                        <RotateCcw size={14} /> Réinitialiser
                      </button>
                    )}
                  </div>
                  {jobsShowListTools && (
                  <div className="flex items-center gap-3">
                    <input ref={importFileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} className="hidden" />
                    {hasPermission(currentUser, "canCreateJobs") && (
                      <button type="button" onClick={() => importFileInputRef.current?.click()} disabled={importLoading} className="text-xs text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors flex items-center gap-1">
                        {importLoading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {t("admin.importButton")}
                      </button>
                    )}
                    <button type="button" onClick={() => handleExportDossiers("csv")} disabled={jobsTotalFiltered === 0} className="text-xs text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors">CSV</button>
                    <button type="button" onClick={() => handleExportDossiers("xlsx")} disabled={jobsTotalFiltered === 0} className="text-xs text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors">Excel</button>
                  </div>
                  )}
                </div>
              </div>
            </div>
              );
            })()}
            {importError && (
              <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 px-4 py-2 rounded-xl">
                {importError}
              </p>
            )}

            <>
                {/* Une seule ligne : partenaire à gauche, boutons à droite */}
                <div className="flex flex-wrap items-center justify-between gap-y-3 py-3 px-1 mb-5">
                  <div className="flex items-center gap-3 min-w-0">
                    <Briefcase size={18} className="text-indigo-500 dark:text-indigo-400 shrink-0" />
                    <span className="font-semibold text-gray-900 dark:text-white truncate">{getJobsFolderTitle()}</span>
                    <span className="text-sm text-gray-500 dark:text-slate-400 tabular-nums shrink-0">
                      {!hasActiveFilters
                        ? `${jobsTotalFiltered} dossier${jobsTotalFiltered !== 1 ? "s" : ""}`
                        : `${jobsTotalFiltered} sur ${jobsTotalInFolder}`}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                    {jobsViewMode === "list" && jobsNavScreen === "list" && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-600 dark:text-slate-300 shrink-0">{t("admin.sortLabel")}</span>
                        <div className="relative">
                          <select
                            value={jobsListSortKey}
                            onChange={(e) => setJobsListSortKey(e.target.value as JobsListSortKey)}
                            className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm font-medium py-2.5 pl-3.5 pr-9 focus:ring-2 focus:ring-indigo-500/30 outline-none appearance-none cursor-pointer min-w-[7rem]"
                            aria-label={t("admin.sortLabel")}
                          >
                            <option value="clientName">{t("admin.client")}</option>
                            <option value="createdAt">{t("admin.dateShort")}</option>
                            <option value="status">{t("admin.statusLabel")}</option>
                            <option value="region">{t("admin.region")}</option>
                            <option value="installer">{t("admin.installer")}</option>
                            <option value="lightingType">{t("admin.typeLabel")}</option>
                          </select>
                          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" />
                        </div>
                        <button
                          type="button"
                          onClick={() => setJobsListSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                          className="shrink-0 p-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                          title={jobsListSortDir === "asc" ? "Croissant" : "Décroissant"}
                          aria-label={jobsListSortDir === "asc" ? "Ordre croissant" : "Ordre décroissant"}
                        >
                          {jobsListSortDir === "asc" ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                      </div>
                    )}
                    <div className="inline-flex p-1.5 rounded-xl bg-gray-100/80 dark:bg-white/[0.06] border border-gray-200/60 dark:border-white/10">
                      <button
                        type="button"
                        onClick={() => setJobsViewMode("list")}
                        className={`px-4 py-2.5 rounded-lg text-[13px] font-medium transition-all ${
                          jobsViewMode === "list"
                            ? "bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm border border-gray-200/60 dark:border-white/10"
                            : "text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
                        }`}
                      >
                        {t("admin.listView")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setJobsViewMode("summary")}
                        className={`px-4 py-2.5 rounded-lg text-[13px] font-medium transition-all flex items-center gap-1.5 ${
                          jobsViewMode === "summary"
                            ? "bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm border border-gray-200/60 dark:border-white/10"
                            : "text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
                        }`}
                      >
                        <BarChart3 size={14} /> {t("admin.summaryView")}
                      </button>
                    </div>
                    {jobsViewMode === "list" && jobsNavScreen === "list" && (
                      <div className="flex items-center gap-2.5">
                        <span className="text-[13px] text-gray-500 dark:text-slate-400">{t("admin.perPage")}</span>
                        <select
                          value={jobsPerPage}
                          onChange={(e) => {
                            setJobsPerPage(Number(e.target.value) as 25 | 50 | 100);
                            setJobsPage(1);
                          }}
                          className="pl-3.5 pr-8 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/25 outline-none"
                        >
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Vue Résumé (point 10) : synthèse par statut et par région */}
                {jobsViewMode === "summary" && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      {(["pending", "scheduled", "in_progress", "completed", "cancelled"] as const).map((status) => {
                        const count = folderJobsSorted.filter((j: Job) => j.status === status).length;
                        const labels: Record<Job["status"], string> = { pending: t("admin.statusPending"), scheduled: t("admin.statusScheduled"), in_progress: t("admin.statusInProgress"), completed: t("admin.statusCompleted"), cancelled: t("admin.statusCancelled") };
                        const colors: Record<Job["status"], string> = {
                          pending: "bg-amber-50 dark:bg-amber-500/10 border-amber-200/60 dark:border-amber-400/20 text-amber-700 dark:text-amber-300",
                          scheduled: "bg-blue-50 dark:bg-blue-500/10 border-blue-200/60 dark:border-blue-400/20 text-blue-700 dark:text-blue-300",
                          in_progress: "bg-amber-50 dark:bg-amber-500/10 border-amber-200/60 dark:border-amber-400/20 text-amber-700 dark:text-amber-300",
                          completed: "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200/60 dark:border-emerald-400/20 text-emerald-700 dark:text-emerald-300",
                          cancelled: "bg-gray-100 dark:bg-white/5 border-gray-200/60 dark:border-white/10 text-gray-600 dark:text-slate-400",
                        };
                        return (
                          <div
                            key={status}
                            className={`rounded-2xl border p-5 ${colors[status]} transition-all hover:shadow-md`}
                          >
                            <p className="text-[13px] font-medium opacity-90">{labels[status]}</p>
                            <p className="text-2xl font-bold mt-1 tabular-nums">{count}</p>
                            <p className="text-[12px] opacity-75 mt-0.5">{count !== 1 ? t("admin.jobPlural") : t("admin.jobSingular")}</p>
                          </div>
                        );
                      })}
                    </div>
                    {stockRegions.length > 0 && (
                      <div className="rounded-2xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-5 shadow-sm">
                        <h3 className="text-[15px] font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <MapPin size={16} className="text-blue-500 dark:text-blue-400" />
                          {t("admin.byRegion")}
                        </h3>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {stockRegions.map((region) => {
                            const count = folderJobsSorted.filter((j: Job) => j.stockRegionId === region.id).length;
                            if (count === 0 && folderJobsSorted.length > 0) return null;
                            return (
                              <div
                                key={region.id}
                                className="flex items-center justify-between rounded-xl bg-gray-50/80 dark:bg-white/[0.04] border border-gray-200/60 dark:border-white/10 px-4 py-3"
                              >
                                <span className="font-medium text-gray-900 dark:text-white text-[14px]">{region.name}</span>
                                <span className="text-lg font-bold text-blue-600 dark:text-blue-400 tabular-nums">{count}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <p className="text-[13px] text-gray-500 dark:text-slate-400">
                      {t("admin.summaryOf", { count: jobsTotalFiltered })}
                    </p>
                  </div>
                )}

                {jobsViewMode === "list" && jobsNavScreen === "hub" && (
                  <JobsCategoryHub
                    jobs={folderJobsForHub}
                    accent="blue"
                    onSelect={handleAdminSelectCategory}
                  />
                )}

                {jobsViewMode === "list" && jobsNavScreen === "types" && jobsBrowseCategory && (
                  <JobsDossierTypeHub
                    jobs={toScheduleJobsForTypes}
                    category={jobsBrowseCategory}
                    accent="blue"
                    onSelect={handleAdminSelectDossierType}
                    onBack={goToJobsCategoryHub}
                  />
                )}

                {jobsViewMode === "list" && jobsNavScreen === "list" && (
                <>
                <div className="flex flex-wrap items-center gap-3 px-1 mb-2">
                  <button
                    type="button"
                    onClick={
                      jobsBrowseCategory && categoryRequiresDossierType(jobsBrowseCategory)
                        ? goToJobsDossierTypes
                        : goToJobsCategoryHub
                    }
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                  >
                    <ArrowLeft size={18} />
                    {jobsBrowseCategory && categoryRequiresDossierType(jobsBrowseCategory)
                      ? "Types"
                      : "Catégories"}
                  </button>
                  {jobsBrowseCategory && (
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white tracking-tight">
                      {JOB_CATEGORY_LABELS[jobsBrowseCategory]}
                      {jobsBrowseDossierType ? (
                        <span className="text-gray-500 dark:text-slate-400 font-normal">
                          {" "}
                          · {DOSSIER_TYPE_FILTER_LABELS[jobsBrowseDossierType]}
                        </span>
                      ) : null}
                    </h2>
                  )}
                </div>
                <div className="md:hidden space-y-3">
                  {jobsToShow.map((job: Job) => (
                    <div
                      key={job.id}
                      onClick={() => openEditJobModal(job)}
                      className={`bg-white dark:bg-slate-800 dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 p-4 rounded-2xl shadow-xl dark:shadow-2xl transition-transform cursor-pointer active:scale-[0.99]`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-bold text-gray-900 dark:text-white">
                            {job.clientName}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-slate-400 tracking-tight">
                            {job.clientAddress}
                          </p>
                          {job.clientPhone && (
                            <p className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-full bg-gray-100/80 dark:bg-white/5 text-[13px] font-medium text-gray-700 dark:text-slate-200 tracking-tight">
                              <Phone size={12} className="text-gray-500 dark:text-slate-400 shrink-0 opacity-80" strokeWidth={2} />
                              {job.clientPhone}
                            </p>
                          )}
                          {job.surface?.trim() && (
                            <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium mt-0.5">
                              {job.surface.trim()} m²
                            </p>
                          )}
                          {job.estimatedLedCount && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-0.5">
                              {job.estimatedLedCount} LED estimées
                            </p>
                          )}
                          {job.dehumidifierCount != null && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-0.5">
                              {job.dehumidifierCount} déshumidificateur(s)
                            </p>
                          )}
                          {job.timerCount != null && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-0.5">
                              {job.timerCount} minuterie(s)
                            </p>
                          )}
                          {job.lightingType && (
                            <p className="text-xs text-teal-600 dark:text-teal-400 font-medium mt-0.5">
                              {job.lightingType === "visite technique" ? "Visite technique" : job.lightingType === "pose" ? "Pose" : job.lightingType}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {canEditJob(job) && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditJobModal(job);
                              }}
                              className="p-2 text-gray-400 dark:text-slate-400 hover:text-blue-500 dark:hover:text-blue-400"
                              title={t("admin.edit")}
                            >
                              <Edit2 size={18} />
                            </button>
                          )}
                          {canDeleteJob() && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteJob(job.id);
                              }}
                              className="text-gray-400 dark:text-slate-400 hover:text-red-500 dark:hover:text-red-400"
                              title={t("admin.delete")}
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                        {jobsFolderId === "__all__" && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-500/20 px-2 py-1 rounded-xl">
                            {getPartnerOrCreatorLabel(job)}
                          </span>
                        )}
                        {job.stockRegionId && stockRegions.find((r) => r.id === job.stockRegionId)?.name ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-500/20 px-2 py-1 rounded-xl">
                            <MapPin size={12} className="shrink-0 opacity-80" />
                            {stockRegions.find((r) => r.id === job.stockRegionId)?.name}
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-slate-500 text-xs">—</span>
                        )}
                        <StatusBadge status={job.status} installerId={job.installerId} />
                        {job.status === "completed" && job.partnerInvoiceTotalCents != null && (
                          <span className="text-xs font-medium text-green-600 dark:text-green-400">{t("admin.invoiceLabel")} {(job.partnerInvoiceTotalCents / 100).toFixed(2)} €</span>
                        )}
                        {job.installerId ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-500/20 px-2.5 py-1 rounded-xl border border-orange-200/50 dark:border-orange-400/20">
                            <Wrench size={12} className="shrink-0 text-orange-500 dark:text-orange-400" />
                            {installers.find((i: User) => i.id === job.installerId)
                              ?.name || job.installerId}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-slate-500 font-medium">
                            Non assigné
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="hidden md:block bg-white dark:bg-slate-800 dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl shadow-xl dark:shadow-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-gray-600 dark:text-slate-300">
                      <thead className="bg-gray-50 dark:bg-slate-700/80 text-xs uppercase font-semibold text-gray-500 dark:text-slate-400">
                        <tr>
                          <th className="px-6 py-4">
                            <button type="button" onClick={() => handleJobsSort("clientName")} className="flex items-center gap-1.5 hover:text-gray-900 dark:hover:text-white transition-colors text-left w-full group">
                              {t("admin.client")}
                              {jobsListSortKey === "clientName" ? (jobsListSortDir === "asc" ? <ChevronUp size={14} className="opacity-80" /> : <ChevronDown size={14} className="opacity-80" />) : <span className="opacity-0 group-hover:opacity-50">↕</span>}
                            </button>
                          </th>
                          {jobsFolderId === "__all__" && <th className="px-6 py-4">{t("admin.partnerLabel")}</th>}
                          <th className="px-6 py-4">
                            <button type="button" onClick={() => handleJobsSort("region")} className="flex items-center gap-1.5 hover:text-gray-900 dark:hover:text-white transition-colors text-left w-full group">
                              {t("admin.region")}
                              {jobsListSortKey === "region" ? (jobsListSortDir === "asc" ? <ChevronUp size={14} className="opacity-80" /> : <ChevronDown size={14} className="opacity-80" />) : <span className="opacity-0 group-hover:opacity-50">↕</span>}
                            </button>
                          </th>
                          <th className="px-6 py-4">
                            <button type="button" onClick={() => handleJobsSort("lightingType")} className="flex items-center gap-1.5 hover:text-gray-900 dark:hover:text-white transition-colors text-left w-full group">
                              {t("admin.typeLabel")}
                              {jobsListSortKey === "lightingType" ? (jobsListSortDir === "asc" ? <ChevronUp size={14} className="opacity-80" /> : <ChevronDown size={14} className="opacity-80" />) : <span className="opacity-0 group-hover:opacity-50">↕</span>}
                            </button>
                          </th>
                          <th className="px-6 py-4">
                            <button type="button" onClick={() => handleJobsSort("status")} className="flex items-center gap-1.5 hover:text-gray-900 dark:hover:text-white transition-colors text-left w-full group">
                              {t("admin.statusLabel")}
                              {jobsListSortKey === "status" ? (jobsListSortDir === "asc" ? <ChevronUp size={14} className="opacity-80" /> : <ChevronDown size={14} className="opacity-80" />) : <span className="opacity-0 group-hover:opacity-50">↕</span>}
                            </button>
                          </th>
                          <th className="px-6 py-4">{t("admin.invoice")}</th>
                          <th className="px-6 py-4">
                            <button type="button" onClick={() => handleJobsSort("installer")} className="flex items-center gap-1.5 hover:text-gray-900 dark:hover:text-white transition-colors text-left w-full group">
                              {t("admin.installer")}
                              {jobsListSortKey === "installer" ? (jobsListSortDir === "asc" ? <ChevronUp size={14} className="opacity-80" /> : <ChevronDown size={14} className="opacity-80" />) : <span className="opacity-0 group-hover:opacity-50">↕</span>}
                            </button>
                          </th>
                          <th className="px-6 py-4">
                            <button type="button" onClick={() => handleJobsSort("createdAt")} className="flex items-center gap-1.5 hover:text-gray-900 dark:hover:text-white transition-colors text-left w-full group">
                              {t("admin.dateShort")}
                              {jobsListSortKey === "createdAt" ? (jobsListSortDir === "asc" ? <ChevronUp size={14} className="opacity-80" /> : <ChevronDown size={14} className="opacity-80" />) : <span className="opacity-0 group-hover:opacity-50">↕</span>}
                            </button>
                          </th>
                          <th className="px-6 py-4 text-right">{t("admin.actions")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                        {jobsToShow.map((job: Job) => (
                          <tr
                            key={job.id}
                            className="hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer"
                            onClick={() => openEditJobModal(job)}
                          >
                            <td className="px-6 py-4">
                              <div className="font-bold text-gray-900 dark:text-white">
                                {job.clientName}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-slate-400 tracking-tight">
                                {job.clientAddress}
                              </div>
                              {job.clientPhone && (
                                <div className="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full bg-gray-100/80 dark:bg-white/5 text-[13px] font-medium text-gray-700 dark:text-slate-200 tracking-tight w-fit">
                                  <Phone size={12} className="text-gray-500 dark:text-slate-400 shrink-0 opacity-80" strokeWidth={2} />
                                  {job.clientPhone}
                                </div>
                              )}
                              {job.surface?.trim() && (
                                <div className="text-xs text-indigo-600 dark:text-indigo-400 font-medium mt-0.5">
                                  {job.surface.trim()} m²
                                </div>
                              )}
                              {job.estimatedLedCount && (
                                <div className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-0.5">
                                  {job.estimatedLedCount} LED estimées
                                </div>
                              )}
                              {job.dehumidifierCount != null && (
                                <div className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-0.5">
                                  {job.dehumidifierCount} déshumidificateur(s)
                                </div>
                              )}
                              {job.timerCount != null && (
                                <div className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-0.5">
                                  {job.timerCount} minuterie(s)
                                </div>
                              )}
                            </td>
                            {jobsFolderId === "__all__" && (
                              <td className="px-6 py-4">
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-500/20 px-2 py-1 rounded-xl">
                                  {getPartnerOrCreatorLabel(job)}
                                </span>
                              </td>
                            )}
                            <td className="px-6 py-4">
                              {job.stockRegionId && stockRegions.find((r) => r.id === job.stockRegionId)?.name ? (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-500/20 px-2 py-1 rounded-xl">
                                  <MapPin size={12} className="shrink-0 opacity-80" />
                                  {stockRegions.find((r) => r.id === job.stockRegionId)?.name}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-slate-500 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              {job.lightingType ? (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-teal-600 dark:text-teal-400 bg-teal-100 dark:bg-teal-500/20 px-2 py-1 rounded-xl">
                                  {job.lightingType === "visite technique" ? "Visite technique" : job.lightingType === "pose" ? "Pose" : job.lightingType}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-slate-500 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <StatusBadge status={job.status} installerId={job.installerId} />
                            </td>
                            <td className="px-6 py-4 text-gray-600 dark:text-slate-300">
                              {job.status === "completed" && job.partnerInvoiceTotalCents != null ? (job.partnerInvoiceTotalCents / 100).toFixed(2) + " €" : "—"}
                            </td>
                            <td className="px-6 py-4">
                              {job.installerId ? (
                                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-500/20 px-2.5 py-1 rounded-xl border border-orange-200/50 dark:border-orange-400/20">
                                  <Wrench className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400 shrink-0" />
                                  {installers.find(
                                    (i: User) => i.id === job.installerId
                                  )?.name || job.installerId}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400 dark:text-slate-500 font-medium">
                                  Non assigné
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-gray-500 dark:text-slate-400 text-xs whitespace-nowrap">
                              {job.createdAt ? new Date(job.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "2-digit" }) : "—"}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end gap-0">
                                {canEditJob(job) && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditJobModal(job);
                                    }}
                                    className="p-2 text-gray-400 dark:text-slate-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                                    title={t("admin.edit")}
                                  >
                                    <Edit2 size={18} />
                                  </button>
                                )}
                                {canDeleteJob() && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDeleteJob(job.id);
                                    }}
                                    className="p-2 text-gray-400 dark:text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                                    title={t("admin.delete")}
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination : style premium */}
                {jobsTotalPages > 1 && jobsTotalFiltered > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-white dark:bg-slate-800/80 border border-gray-200/80 dark:border-white/10 px-4 py-3">
                    <p className="text-[13px] text-gray-500 dark:text-slate-400 tabular-nums">
                      Page {jobsPage} sur {jobsTotalPages}
                      <span className="ml-2 text-gray-400 dark:text-slate-500">
                        ({(jobsPage - 1) * jobsPerPage + 1}–{Math.min(jobsPage * jobsPerPage, jobsTotalFiltered)})
                      </span>
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      {jobsTotalPages > 5 && (
                        <div className="flex items-center gap-1.5">
                          <label htmlFor="jobs-goto-page" className="text-[12px] text-gray-500 dark:text-slate-400 whitespace-nowrap">{t("admin.goTo")}</label>
                          <input
                            id="jobs-goto-page"
                            type="number"
                            min={1}
                            max={jobsTotalPages}
                            value={jobsPage}
                            onChange={(e) => {
                              const n = parseInt(e.target.value, 10);
                              if (!Number.isNaN(n) && n >= 1 && n <= jobsTotalPages) setJobsPage(n);
                            }}
                            className="w-14 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-900 dark:text-white tabular-nums focus:ring-2 focus:ring-blue-500/20 outline-none"
                          />
                        </div>
                      )}
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setJobsPage((p) => Math.max(1, p - 1))}
                        disabled={jobsPage <= 1}
                        className="p-2.5 rounded-xl border border-gray-200 dark:border-white/10 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-40 disabled:pointer-events-none transition-all"
                      >
                        <ChevronRight size={18} className="rotate-180" />
                      </button>
                      {Array.from({ length: Math.min(5, jobsTotalPages) }, (_, i) => {
                        let pageNum: number;
                        if (jobsTotalPages <= 5) pageNum = i + 1;
                        else if (jobsPage <= 3) pageNum = i + 1;
                        else if (jobsPage >= jobsTotalPages - 2) pageNum = jobsTotalPages - 4 + i;
                        else pageNum = jobsPage - 2 + i;
                        return (
                          <button
                            key={pageNum}
                            type="button"
                            onClick={() => setJobsPage(pageNum)}
                            className={`min-w-[2.25rem] py-2.5 px-2 rounded-xl text-[14px] font-medium transition-all ${
                              jobsPage === pageNum
                                ? "bg-blue-500 text-white shadow-lg shadow-blue-500/30"
                                : "text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-white/5 border border-gray-200 dark:border-white/10"
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => setJobsPage((p) => Math.min(jobsTotalPages, p + 1))}
                        disabled={jobsPage >= jobsTotalPages}
                        className="p-2.5 rounded-xl border border-gray-200 dark:border-white/10 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-40 disabled:pointer-events-none transition-all"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </div>
                )}

                {jobsTotalFiltered === 0 && (
                  <div className="flex flex-col items-center justify-center py-20 rounded-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-white/10">
                    <Briefcase size={48} className="text-gray-300 dark:text-slate-600 mb-3" />
                    <p className="text-gray-500 dark:text-slate-400 font-medium">{t("admin.noJobs")}</p>
                    <p className="text-sm text-gray-400 dark:text-slate-500 mt-1">
                      {hasActiveFilters ? "Modifiez les filtres ou la recherche pour afficher des dossiers." : "Aucun dossier dans cette vue."}
                    </p>
                  </div>
                )}
              </>
            )}
            </>
          </div>
        )}

        {activeTab === "regions" && canSeeJobsTab(currentUser) && (
          <div className="space-y-6 relative z-10">
            {/* En-tête + segment control style Apple */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">{t("admin.regions")}</h1>
                <p className="mt-1 text-[15px] text-gray-500 dark:text-slate-400">
                  {selectedRegionsTabRegionId != null
                    ? t("admin.regionDetailDesc")
                    : regionsSubTab === "poseurs"
                    ? t("admin.installersByRegion")
                    : regionsSubTab === "stocks"
                    ? t("admin.stockByRegionDesc")
                    : t("admin.regionChooseDesc")}
                </p>
              </div>
              <div className="inline-flex p-1 rounded-2xl bg-gray-100/80 dark:bg-white/[0.06] border border-gray-200/80 dark:border-white/10 shadow-inner">
                {(["poseurs", "stocks", "dossiers"] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setRegionsSubTab(tab)}
                    className={`px-5 py-2.5 rounded-xl text-[14px] font-medium transition-all duration-200 ease-out ${
                      regionsSubTab === tab
                        ? "bg-white dark:bg-slate-700/90 text-gray-900 dark:text-white shadow-sm border border-gray-200/60 dark:border-white/10"
                        : "text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
                    }`}
                  >
                    {tab === "poseurs" && t("admin.installers")}
                    {tab === "stocks" && t("admin.stocksTabLabel")}
                    {tab === "dossiers" && t("admin.jobsTabLabel")}
                  </button>
                ))}
              </div>
            </div>

            {/* Vue Poseurs : poseurs regroupés par région (masquée si une région est sélectionnée) */}
            {regionsSubTab === "poseurs" && selectedRegionsTabRegionId === null && (
              <div className="space-y-8">
                {stockRegions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 rounded-3xl bg-white dark:bg-slate-800/60 border border-gray-200/80 dark:border-white/[0.08]">
                    <Wrench size={40} className="text-gray-300 dark:text-slate-500 mb-3" />
                    <p className="text-gray-500 dark:text-slate-400 font-medium text-[15px]">{t("admin.noRegionYet")}</p>
                    <p className="text-[13px] text-gray-400 dark:text-slate-500 mt-1">{t("admin.addRegionsToSee")}</p>
                  </div>
                ) : (
                  stockRegions.map((region) => {
                    // Afficher les poseurs assignés à cette région (defaultStockRegionId), pas ceux qui y ont des chantiers historiques
                    const regionInstallers = installers.filter((inst: User) =>
                      inst.preferences?.defaultStockRegionId === region.id
                    );
                    const hasAny = regionInstallers.length > 0;
                    return (
                      <div key={region.id} className="space-y-3">
                        <button
                          type="button"
                          onClick={() => setSelectedRegionsTabRegionId(region.id)}
                          className="text-left w-full text-[13px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider px-1 flex items-center gap-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer"
                        >
                          <MapPin size={14} className="opacity-70" />
                          {region.name}
                          <span className="font-normal normal-case text-gray-400 dark:text-slate-500">
                            · {regionInstallers.length} poseur{regionInstallers.length !== 1 ? "s" : ""}
                          </span>
                          <ChevronRight size={14} className="ml-1 opacity-70" />
                        </button>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {hasAny ? (
                            regionInstallers.map((inst: User) => {
                              const count = jobs.filter((j: Job) => j.stockRegionId === region.id && j.installerId === inst.id).length;
                              return (
                                <div
                                  key={inst.id}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => {
                                    setActiveTab("installers");
                                    setSelectedInstallersTabInstallerId(inst.id);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      setActiveTab("installers");
                                      setSelectedInstallersTabInstallerId(inst.id);
                                    }
                                  }}
                                  className="group flex items-center gap-4 p-5 rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] shadow-sm dark:shadow-none hover:shadow-lg hover:shadow-gray-200/50 dark:hover:bg-slate-800 hover:border-gray-300/80 dark:hover:border-white/12 transition-all duration-300 ease-out text-left active:scale-[0.995] cursor-pointer"
                                >
                                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400">
                                    <Wrench size={24} strokeWidth={1.5} />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="font-medium text-gray-900 dark:text-white truncate text-[15px]">{inst.name}</div>
                                    <div className="text-[13px] text-gray-500 dark:text-slate-400 mt-0.5">{count} chantier{count !== 1 ? "s" : ""}</div>
                                  </div>
                                  <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-orange-500 dark:group-hover:text-orange-400 group-hover:translate-x-0.5 transition-all duration-200" />
                                </div>
                              );
                            })
                          ) : (
                            <div className="col-span-full py-8 rounded-2xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-200/60 dark:border-white/[0.06] text-center">
                              <p className="text-[14px] text-gray-500 dark:text-slate-400">{t("admin.noInstallerInRegion")}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Vue Stocks : régions avec résumé du stock (masquée si une région est sélectionnée) */}
            {regionsSubTab === "stocks" && selectedRegionsTabRegionId === null && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {stockRegions.map((region) => {
                  const regionStock = stock.filter((s) => s.regionId === region.id);
                  const lowCount = regionStock.filter((item) => {
                    const t = item.lowStockThreshold ?? 10;
                    return item.quantity <= t;
                  }).length;
                  return (
                    <div
                      key={region.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedRegionsTabRegionId(region.id)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedRegionsTabRegionId(region.id); } }}
                      className="group relative flex items-center gap-5 rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-6 shadow-sm dark:shadow-none hover:shadow-lg hover:shadow-gray-200/50 dark:hover:bg-slate-800 hover:border-gray-300/80 dark:hover:border-white/12 transition-all duration-300 ease-out cursor-pointer text-left"
                    >
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-blue-100/80 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400">
                        <Package size={26} strokeWidth={1.5} className="opacity-90" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[17px] font-medium text-blue-700 dark:text-blue-300 truncate tracking-tight">{region.name}</div>
                        <div className="mt-0.5 text-[13px] text-gray-500 dark:text-slate-400">
                          {regionStock.length} référence{regionStock.length !== 1 ? "s" : ""}
                          {lowCount > 0 && (
                            <span className="ml-1.5 inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium">
                              · {lowCount} en alerte
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setActiveTab("stock"); setSelectedRegionId(region.id); }}
                        className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-slate-300 text-[13px] font-medium hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                      >
                        Voir le stock
                        <ChevronRight size={16} />
                      </button>
                      <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all duration-200" />
                    </div>
                  );
                })}
                {onAddRegion && (
                  <button
                    type="button"
                    onClick={() => setIsAddRegionModalOpen(true)}
                    className="group flex items-center gap-5 rounded-3xl border-2 border-dashed border-gray-200 dark:border-slate-600/80 p-6 bg-gray-50/50 dark:bg-slate-800/30 hover:border-blue-300 dark:hover:border-blue-500/50 hover:bg-blue-50/30 dark:hover:bg-blue-500/5 transition-all duration-300 ease-out text-left active:scale-[0.995]"
                  >
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white dark:bg-white/5 border border-gray-200/80 dark:border-white/10 text-gray-400 dark:text-slate-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-300">
                      <ListPlus size={26} strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[17px] font-medium text-gray-600 dark:text-slate-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 tracking-tight">{t("admin.newRegion")}</div>
                      <div className="mt-0.5 text-[13px] text-gray-400 dark:text-slate-500">{t("admin.addRegionShort")}</div>
                    </div>
                    <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-200" />
                  </button>
                )}
                {stockRegions.length === 0 && !onAddRegion && (
                  <div className="col-span-full flex flex-col items-center justify-center py-16 rounded-3xl bg-white dark:bg-slate-800/60 border border-gray-200/80 dark:border-white/[0.08]">
                    <Package size={40} className="text-gray-300 dark:text-slate-500 mb-3" />
                    <p className="text-gray-500 dark:text-slate-400 font-medium text-[15px]">{t("admin.noRegionHere")}</p>
                  </div>
                )}
              </div>
            )}

            {/* Vue Dossiers : comportement actuel (liste régions → détail dossiers) */}
            {regionsSubTab === "dossiers" && selectedRegionsTabRegionId === null && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {stockRegions.map((region) => {
                  const count = jobs.filter((j: Job) => j.stockRegionId === region.id).length;
                  return (
                    <div
                      key={region.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedRegionsTabRegionId(region.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedRegionsTabRegionId(region.id);
                        }
                      }}
                      className="group relative flex items-center gap-5 rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-6 shadow-sm dark:shadow-none hover:shadow-lg hover:shadow-gray-200/50 dark:hover:bg-slate-800 hover:border-gray-300/80 dark:hover:border-white/12 transition-all duration-300 ease-out text-left active:scale-[0.995] cursor-pointer"
                    >
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-blue-100/80 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 transition-colors duration-300">
                        <MapPin size={26} strokeWidth={1.5} className="opacity-90" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[17px] font-medium text-blue-700 dark:text-blue-300 truncate tracking-tight">{region.name}</div>
                        <div className="mt-0.5 text-[13px] text-gray-500 dark:text-slate-400">
                          {count} dossier{count !== 1 ? "s" : ""}
                        </div>
                      </div>
                      <div
                        className="flex items-center gap-0.5 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        {onUpdateRegion && (
                          <button
                            type="button"
                            onClick={() => setEditingRegion(region)}
                            className="p-2.5 rounded-xl text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors duration-200"
                            title={t("admin.editRegion")}
                          >
                            <Edit2 size={18} strokeWidth={1.75} />
                          </button>
                        )}
                        {onRemoveRegion && (
                          <button
                            type="button"
                            onClick={async () => {
                              if (!window.confirm(t("admin.deleteRegionConfirm", { name: region.name }))) return;
                              const { error } = await onRemoveRegion(region.id);
                              if (!error) {
                                if (selectedRegionsTabRegionId === region.id) setSelectedRegionsTabRegionId(null);
                                refetchStockRegions?.();
                              }
                            }}
                            className="p-2.5 rounded-xl text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50/80 dark:hover:bg-red-500/10 transition-colors duration-200"
                            title={t("admin.deleteRegion")}
                          >
                            <Trash2 size={18} strokeWidth={1.75} />
                          </button>
                        )}
                      </div>
                      <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all duration-200" />
                    </div>
                  );
                })}
                {onAddRegion && (
                  <button
                    type="button"
                    onClick={() => setIsAddRegionModalOpen(true)}
                    className="group flex items-center gap-5 rounded-3xl border-2 border-dashed border-gray-200 dark:border-slate-600/80 p-6 bg-gray-50/50 dark:bg-slate-800/30 hover:border-blue-300 dark:hover:border-blue-500/50 hover:bg-blue-50/30 dark:hover:bg-blue-500/5 transition-all duration-300 ease-out text-left active:scale-[0.995]"
                  >
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white dark:bg-white/5 border border-gray-200/80 dark:border-white/10 text-gray-400 dark:text-slate-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-300">
                      <ListPlus size={26} strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[17px] font-medium text-gray-600 dark:text-slate-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 tracking-tight">
                        Nouvelle région
                      </div>
                      <div className="mt-0.5 text-[13px] text-gray-400 dark:text-slate-500">
                        Ajouter une région
                      </div>
                    </div>
                    <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-200" />
                  </button>
                )}
              </div>
            )}

            {/* Détail complet d'une région : stocks, poseurs, partenaires, dossiers, factures */}
            {selectedRegionsTabRegionId !== null && (
              (() => {
                const region = stockRegions.find((r) => r.id === selectedRegionsTabRegionId);
                const regionJobs = jobs.filter((j: Job) => j.stockRegionId === selectedRegionsTabRegionId);
                const term = searchTerm.toLowerCase().trim();
                const regionJobsToShow = term
                  ? regionJobs.filter(
                      (j: Job) =>
                        j.clientName.toLowerCase().includes(term) || j.id.toLowerCase().includes(term)
                    )
                  : regionJobs;
                const regionStock = stock.filter((s) => s.regionId === selectedRegionsTabRegionId);
                // Poseurs assignés à cette région (defaultStockRegionId), pas seulement ceux qui y ont des chantiers
                const regionInstallers = installers.filter((inst: User) =>
                  inst.preferences?.defaultStockRegionId === selectedRegionsTabRegionId
                );
                const regionPartnerIds = [...new Set(regionJobs.map((j: Job) => j.partnerId).filter(Boolean))] as string[];
                const regionPartners = partners.filter((p: User) => regionPartnerIds.includes(p.id));
                const regionInvoices = regionJobs.filter(
                  (j: Job) => j.status === "completed" && j.partnerInvoiceTotalCents != null && j.partnerInvoiceTotalCents > 0
                );
                const totalInvoicesCents = regionInvoices.reduce((s, j) => s + (j.partnerInvoiceTotalCents ?? 0), 0);
                const lowStockCount = regionStock.filter((item) => {
                  const t = item.lowStockThreshold ?? 10;
                  return item.quantity <= t;
                }).length;

                return (
                  <>
                    <div className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-4 md:p-5 shadow-sm dark:shadow-none flex flex-col md:flex-row justify-between items-center gap-4">
                      <button
                        type="button"
                        onClick={() => setSelectedRegionsTabRegionId(null)}
                        className="flex items-center gap-2 text-[15px] text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 font-medium"
                      >
                        <ArrowLeft size={18} className="shrink-0" strokeWidth={2} />
                        Retour aux régions
                      </button>
                      <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto md:items-center">
                        <div className="relative w-full md:w-64 lg:w-80">
                          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400 w-5 h-5" />
                          <input
                            type="text"
                            placeholder="Rechercher dans les dossiers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-gray-50/80 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 rounded-2xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/25 outline-none transition-all"
                          />
                        </div>
                        {hasPermission(currentUser, "canCreateJobs") && (
                          <>
                            <input
                              ref={importFileInputRef}
                              type="file"
                              accept=".xlsx,.xls,.csv"
                              onChange={handleImportFile}
                              className="hidden"
                            />
                            <button
                              type="button"
                              onClick={() => importFileInputRef.current?.click()}
                              disabled={importLoading}
                              className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 text-gray-700 dark:text-slate-200 rounded-2xl hover:bg-gray-200/80 dark:hover:bg-white/10 transition-colors disabled:opacity-50 text-[15px] font-medium"
                            >
                              {importLoading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
                              {importLoading ? t("admin.importLoading") : t("admin.importButton")}
                            </button>
                            <button
                              onClick={() => setIsAddJobModalOpen(true)}
                              className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-2xl font-medium text-[15px] hover:opacity-90 transition-opacity"
                            >
                              <Plus size={20} /> {t("admin.newJob")}
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 px-1 mb-4">
                      <MapPin size={20} className="text-blue-500 dark:text-blue-400" />
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">{region?.name ?? "Région"}</h2>
                    </div>

                    {/* Résumé en cartes (cliquables → scroll vers la section) */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
                      <button
                        type="button"
                        onClick={() => document.getElementById("region-detail-dossiers")?.scrollIntoView({ behavior: "smooth" })}
                        className="rounded-2xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-4 shadow-sm text-left hover:border-blue-300 dark:hover:border-blue-500/50 hover:shadow-md transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-2 text-gray-500 dark:text-slate-400 text-[13px] font-medium">
                          <Briefcase size={16} />
                          Dossiers
                        </div>
                        <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{regionJobs.length}</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => document.getElementById("region-detail-poseurs")?.scrollIntoView({ behavior: "smooth" })}
                        className="rounded-2xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-4 shadow-sm text-left hover:border-orange-300 dark:hover:border-orange-500/50 hover:shadow-md transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-2 text-gray-500 dark:text-slate-400 text-[13px] font-medium">
                          <Wrench size={16} />
                          Poseurs
                        </div>
                        <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{regionInstallers.length}</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => document.getElementById("region-detail-partenaires")?.scrollIntoView({ behavior: "smooth" })}
                        className="rounded-2xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-4 shadow-sm text-left hover:border-purple-300 dark:hover:border-purple-500/50 hover:shadow-md transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-2 text-gray-500 dark:text-slate-400 text-[13px] font-medium">
                          <Building2 size={16} />
                          Partenaires
                        </div>
                        <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{regionPartners.length}</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => document.getElementById("region-detail-stock")?.scrollIntoView({ behavior: "smooth" })}
                        className="rounded-2xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-4 shadow-sm text-left hover:border-blue-300 dark:hover:border-blue-500/50 hover:shadow-md transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-2 text-gray-500 dark:text-slate-400 text-[13px] font-medium">
                          <Package size={16} />
                          Réf. stock
                        </div>
                        <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                          {regionStock.length}
                          {lowStockCount > 0 && (
                            <span className="ml-1 text-amber-600 dark:text-amber-400 text-base font-normal">· {lowStockCount} alerte</span>
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => document.getElementById("region-detail-factures")?.scrollIntoView({ behavior: "smooth" })}
                        className="rounded-2xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-4 shadow-sm col-span-2 sm:col-span-1 text-left hover:border-green-300 dark:hover:border-green-500/50 hover:shadow-md transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-2 text-gray-500 dark:text-slate-400 text-[13px] font-medium">
                          <FileText size={16} />
                          Factures
                        </div>
                        <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                          {(totalInvoicesCents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                        </div>
                        <div className="text-[12px] text-gray-400 dark:text-slate-500">{regionInvoices.length} dossier(s) facturé(s)</div>
                      </button>
                    </div>

                    {/* Section Stock (par partenaire) */}
                    <div id="region-detail-stock" className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-5 shadow-sm dark:shadow-none mb-6 scroll-mt-4">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Package size={18} className="text-blue-500 dark:text-blue-400" />
                        Stock de la région (par partenaire)
                      </h3>
                      {regionStock.length > 0 ? (
                        (() => {
                          const byPartner = new Map<string | null, typeof regionStock>();
                          regionStock.forEach((item) => {
                            const pid = item.partnerId ?? null;
                            if (!byPartner.has(pid)) byPartner.set(pid, []);
                            byPartner.get(pid)!.push(item);
                          });
                          const allPartnerIds = [...new Set(regionStock.map((s) => s.partnerId ?? null))];
                          const order = [null, ...allPartnerIds.filter((id): id is string => id !== null)];
                          return (
                            <div className="space-y-6">
                              {order.map((pid) => {
                                const items = byPartner.get(pid) ?? [];
                                if (items.length === 0) return null;
                                const label = pid === null ? "Stock interne" : (partners ?? []).find((p: User) => p.id === pid)?.company ?? (partners ?? []).find((p: User) => p.id === pid)?.name ?? "Partenaire";
                                return (
                                  <div key={pid ?? "__internal__"}>
                                    <p className="text-[12px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                                      {label}
                                    </p>
                                    <div className="overflow-x-auto rounded-xl border border-gray-200/80 dark:border-white/10">
                                      <table className="w-full text-left text-sm text-gray-600 dark:text-slate-300">
                                        <thead className="bg-gray-50/80 dark:bg-white/[0.03] text-[11px] uppercase font-semibold tracking-wider text-gray-500 dark:text-slate-400">
                                          <tr>
                                            <th className="px-4 py-3 rounded-tl-xl">{t("admin.reference")}</th>
                                            <th className="px-4 py-3">{t("admin.quantity")}</th>
                                            <th className="px-4 py-3 rounded-tr-xl">{t("admin.alertLabel")}</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                                          {items.map((item) => {
                                            const threshold = item.lowStockThreshold ?? 10;
                                            const isLow = item.quantity <= threshold;
                                            return (
                                              <tr key={item.id} className={isLow ? "bg-amber-50/50 dark:bg-amber-500/5" : ""}>
                                                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{item.name}</td>
                                                <td className="px-4 py-3">{item.quantity}</td>
                                                <td className="px-4 py-3">
                                                  {isLow ? (
                                                    <span className="text-amber-600 dark:text-amber-400 font-medium">≤ {threshold}</span>
                                                  ) : (
                                                    <span className="text-gray-400 dark:text-slate-500">—</span>
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()
                      ) : (
                        <p className="text-[14px] text-gray-500 dark:text-slate-400 py-4">{t("admin.noStockInRegion")}</p>
                      )}
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => { setActiveTab("stock"); setSelectedRegionId(selectedRegionsTabRegionId); }}
                          className="text-[13px] font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                        >
                          Voir / gérer le stock
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Section Poseurs */}
                    <div id="region-detail-poseurs" className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-5 shadow-sm dark:shadow-none mb-6 scroll-mt-4">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Wrench size={18} className="text-orange-500 dark:text-orange-400" />
                        {t("admin.installersInRegion")}
                      </h3>
                      {regionInstallers.length > 0 ? (
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {regionInstallers.map((inst: User) => {
                            const count = regionJobs.filter((j: Job) => j.installerId === inst.id).length;
                            return (
                              <div
                                key={inst.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => { setActiveTab("installers"); setSelectedInstallersTabInstallerId(inst.id); }}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveTab("installers"); setSelectedInstallersTabInstallerId(inst.id); } }}
                                className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50/80 dark:bg-white/[0.04] border border-gray-200/60 dark:border-white/[0.06] hover:bg-gray-100/80 dark:hover:bg-white/[0.08] transition-colors cursor-pointer text-left"
                              >
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400">
                                  <Wrench size={20} strokeWidth={1.5} />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-gray-900 dark:text-white truncate text-[15px]">{inst.name}</div>
                                  <div className="text-[13px] text-gray-500 dark:text-slate-400">{count} chantier{count !== 1 ? "s" : ""}</div>
                                </div>
                                <ChevronRight size={18} className="shrink-0 text-gray-300 dark:text-slate-600" />
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-[14px] text-gray-500 dark:text-slate-400 py-4">{t("admin.noInstallerWithJobInRegion")}</p>
                      )}
                    </div>

                    {/* Section Partenaires */}
                    <div id="region-detail-partenaires" className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-5 shadow-sm dark:shadow-none mb-6 scroll-mt-4">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Building2 size={18} className="text-purple-500 dark:text-purple-400" />
                        {t("admin.partnersInRegion")}
                      </h3>
                      {regionPartners.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-sm text-gray-600 dark:text-slate-300">
                            <thead className="bg-gray-50/80 dark:bg-white/[0.03] text-[11px] uppercase font-semibold tracking-wider text-gray-500 dark:text-slate-400">
                              <tr>
                                <th className="px-4 py-3 rounded-l-xl">{t("admin.partnerLabel")}</th>
                                <th className="px-4 py-3">{t("admin.jobsTabLabel")}</th>
                                <th className="px-4 py-3 rounded-r-xl">{t("admin.totalInvoiced")}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                              {regionPartners.map((p: User) => {
                                const partnerJobs = regionJobs.filter((j: Job) => j.partnerId === p.id);
                                const totalCents = partnerJobs.reduce((s, j) => s + (j.partnerInvoiceTotalCents ?? 0), 0);
                                return (
                                  <tr
                                    key={p.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => { setActiveTab("partners"); setSelectedPartnersTabPartnerId(p.id); }}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveTab("partners"); setSelectedPartnersTabPartnerId(p.id); } }}
                                    className="hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer"
                                  >
                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{p.name}</td>
                                    <td className="px-4 py-3">{partnerJobs.length}</td>
                                    <td className="px-4 py-3 text-gray-600 dark:text-slate-300">
                                      {(totalCents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-[14px] text-gray-500 dark:text-slate-400 py-4">{t("admin.noPartnerInRegion")}</p>
                      )}
                    </div>

                    {/* Section Dossiers */}
                    <div id="region-detail-dossiers" className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-5 shadow-sm dark:shadow-none mb-6 scroll-mt-4">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Briefcase size={18} className="text-blue-500 dark:text-blue-400" />
                        Dossiers de la région
                        <span className="text-[13px] font-normal text-gray-500 dark:text-slate-400">({regionJobsToShow.length})</span>
                      </h3>
                      <div className="md:hidden space-y-3">
                        {regionJobsToShow.map((job: Job) => (
                          <div
                            key={job.id}
                            onClick={() => openEditJobModal(job)}
                            className={`bg-gray-50/80 dark:bg-white/[0.04] border border-gray-200/60 dark:border-white/[0.06] p-4 rounded-2xl cursor-pointer active:scale-[0.99]`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h3 className="font-bold text-gray-900 dark:text-white">{job.clientName}</h3>
                                <p className="text-xs text-gray-500 dark:text-slate-400 tracking-tight">{job.clientAddress}</p>
                                {job.clientPhone && (
                                  <p className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-full bg-gray-100/80 dark:bg-white/5 text-[13px] font-medium text-gray-700 dark:text-slate-200 tracking-tight">
                                    <Phone size={12} className="shrink-0 opacity-80" strokeWidth={2} /> {job.clientPhone}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                {canEditJob(job) && (
                                  <button type="button" onClick={(e) => { e.stopPropagation(); openEditJobModal(job); }} className="p-2 text-gray-400 dark:text-slate-400 hover:text-blue-500 dark:hover:text-blue-400" title={t("admin.edit")}><Edit2 size={18} /></button>
                                )}
                                {canDeleteJob() && (
                                  <button onClick={(e) => { e.stopPropagation(); onDeleteJob(job.id); }} className="text-gray-400 dark:text-slate-400 hover:text-red-500 dark:hover:text-red-400" title={t("admin.delete")}><Trash2 size={18} /></button>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-3">
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-500/20 px-2 py-1 rounded-xl">{getPartnerOrCreatorLabel(job)}</span>
                              <StatusBadge status={job.status} installerId={job.installerId} />
                              {job.status === "completed" && job.partnerInvoiceTotalCents != null && (
                                <span className="text-xs font-medium text-green-600 dark:text-green-400">{t("admin.invoiceLabel")} {(job.partnerInvoiceTotalCents / 100).toFixed(2)} €</span>
                              )}
                              {job.installerId ? (
                                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-500/20 px-2.5 py-1 rounded-xl">
                                  <Wrench size={12} className="shrink-0" />
                                  {installers.find((i: User) => i.id === job.installerId)?.name || job.installerId}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400 dark:text-slate-500 font-medium">{t("admin.unassigned")}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-left text-sm text-gray-600 dark:text-slate-300">
                          <thead className="bg-gray-50/80 dark:bg-white/[0.03] text-[11px] uppercase font-semibold tracking-wider text-gray-500 dark:text-slate-400">
                            <tr>
                              <th className="px-6 py-4">{t("admin.client")}</th>
                              <th className="px-6 py-4">Partenaire</th>
                              <th className="px-6 py-4">Type</th>
                              <th className="px-6 py-4">{t("admin.statusLabel")}</th>
                              <th className="px-6 py-4">{t("admin.invoice")}</th>
                              <th className="px-6 py-4">{t("admin.installer")}</th>
                              <th className="px-6 py-4 text-right">{t("admin.actions")}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                            {regionJobsToShow.map((job: Job) => (
                              <tr
                                key={job.id}
                                className="hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer"
                                onClick={() => openEditJobModal(job)}
                              >
                                <td className="px-6 py-4">
                                  <div className="font-bold text-gray-900 dark:text-white">{job.clientName}</div>
                                  <div className="text-xs text-gray-500 dark:text-slate-400">{job.clientAddress}</div>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-500/20 px-2 py-1 rounded-xl">{getPartnerOrCreatorLabel(job)}</span>
                                </td>
                                <td className="px-6 py-4">
                                  {job.lightingType ? (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-teal-600 dark:text-teal-400 bg-teal-100 dark:bg-teal-500/20 px-2 py-1 rounded-xl">
                                      {job.lightingType === "visite technique" ? "Visite technique" : job.lightingType === "pose" ? "Pose" : job.lightingType}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400 dark:text-slate-500 text-xs">—</span>
                                  )}
                                </td>
                                <td className="px-6 py-4"><StatusBadge status={job.status} installerId={job.installerId} /></td>
                                <td className="px-6 py-4 text-gray-600 dark:text-slate-300">
                                  {job.status === "completed" && job.partnerInvoiceTotalCents != null ? (job.partnerInvoiceTotalCents / 100).toFixed(2) + " €" : "—"}
                                </td>
                                <td className="px-6 py-4">
                                  {job.installerId ? (
                                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-500/20 px-2.5 py-1 rounded-xl">
                                      {installers.find((i: User) => i.id === job.installerId)?.name || job.installerId}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-400 dark:text-slate-500 font-medium">{t("admin.unassigned")}</span>
                                  )}
                                </td>
                                <td className="px-6 py-4 text-right">
                                  {canEditJob(job) && (
                                    <button type="button" onClick={(e) => { e.stopPropagation(); openEditJobModal(job); }} className="p-2 text-gray-400 dark:text-slate-400 hover:text-blue-500 dark:hover:text-blue-400" title={t("admin.edit")}><Edit2 size={18} /></button>
                                  )}
                                  {canDeleteJob() && (
                                    <button onClick={(e) => { e.stopPropagation(); onDeleteJob(job.id); }} className="p-2 text-gray-400 dark:text-slate-400 hover:text-red-500 dark:hover:text-red-400" title={t("admin.delete")}><Trash2 size={18} /></button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {regionJobsToShow.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-12 rounded-2xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-200/60 dark:border-white/[0.06] mt-4">
                          <Briefcase size={32} className="text-gray-300 dark:text-slate-500 mb-2" />
                          <p className="text-gray-500 dark:text-slate-400 font-medium text-[14px]">{searchTerm.trim() ? "Aucun résultat pour cette recherche." : "Aucun dossier dans cette région."}</p>
                        </div>
                      )}
                    </div>

                    {/* Section Factures (dossiers terminés avec facture) */}
                    <div id="region-detail-factures" className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-5 shadow-sm dark:shadow-none scroll-mt-4">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <FileText size={18} className="text-green-500 dark:text-green-400" />
                        {t("admin.invoicesInRegion")}
                        <span className="text-[13px] font-normal text-gray-500 dark:text-slate-400">({regionInvoices.length} dossier(s) facturé(s))</span>
                      </h3>
                      {regionInvoices.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-sm text-gray-600 dark:text-slate-300">
                            <thead className="bg-gray-50/80 dark:bg-white/[0.03] text-[11px] uppercase font-semibold tracking-wider text-gray-500 dark:text-slate-400">
                              <tr>
                                <th className="px-4 py-3 rounded-l-xl">{t("admin.client")}</th>
                                <th className="px-4 py-3">Partenaire</th>
                                <th className="px-4 py-3 rounded-r-xl">Montant</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                              {regionInvoices.map((job: Job) => (
                                <tr
                                  key={job.id}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => openEditJobModal(job)}
                                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openEditJobModal(job); } }}
                                  className="hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer"
                                >
                                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{job.clientName}</td>
                                  <td className="px-4 py-3">{getPartnerOrCreatorLabel(job)}</td>
                                  <td className="px-4 py-3 text-green-600 dark:text-green-400 font-medium">
                                    {((job.partnerInvoiceTotalCents ?? 0) / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-[14px] text-gray-500 dark:text-slate-400 py-4">{t("admin.noInvoiceInRegion")}</p>
                      )}
                    </div>
                  </>
                );
              })()
            )}
          </div>
        )}

        {activeTab === "partners" && canSeeJobsTab(currentUser) && (
          <div className="space-y-6 relative z-10">
            {selectedPartnersTabPartnerId === null ? (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">{t("admin.partnersTitle")}</h1>
                    <p className="mt-1 text-[15px] text-gray-500 dark:text-slate-400">{t("admin.choosePartnerDesc")}</p>
                  </div>
                  <div className="inline-flex flex-wrap items-center gap-2 p-2 rounded-2xl bg-gray-100/80 dark:bg-white/[0.06] border border-gray-200/80 dark:border-white/10">
                    <select
                      value={partnersListStatusFilter}
                      onChange={(e) => setPartnersListStatusFilter((e.target.value || "") as "" | Job["status"])}
                      className="pl-3 pr-8 py-2 bg-white dark:bg-slate-700/90 border border-gray-200/60 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/25 outline-none shadow-sm"
                    >
                      <option value="">{t("admin.allStatuses")}</option>
                      <option value="pending">{t("admin.statusPending")}</option>
                      <option value="scheduled">{t("admin.statusScheduled")}</option>
                      <option value="in_progress">{t("admin.statusInProgress")}</option>
                      <option value="completed">{t("admin.statusCompleted")}</option>
                      <option value="cancelled">{t("admin.statusCancelled")}</option>
                    </select>
                    {stockRegions.length > 0 && (
                      <select
                        value={partnersListRegionFilter}
                        onChange={(e) => setPartnersListRegionFilter(e.target.value)}
                        className="pl-3 pr-8 py-2 bg-white dark:bg-slate-700/90 border border-gray-200/60 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/25 outline-none shadow-sm"
                      >
                        <option value="">{t("admin.allRegions")}</option>
                        {stockRegions.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 dark:text-slate-400">{t("admin.sortLabel")}</span>
                      <button
                        type="button"
                        onClick={() => setPartnersListSortBy("name")}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${partnersListSortBy === "name" ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300" : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"}`}
                      >
                        Nom
                      </button>
                      <button
                        type="button"
                        onClick={() => setPartnersListSortBy("count")}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${partnersListSortBy === "count" ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300" : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"}`}
                      >
                        Nb dossiers
                      </button>
                    </div>
                  </div>
                </div>
                {(displayedPartners.length > 0 || hasPermission(currentUser, "canManagePartners")) && (
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {displayedPartners.map((partner: User) => {
                      const count = jobs.filter((j: Job) => j.partnerId === partner.id).length;
                      return (
                        <div
                          key={partner.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedPartnersTabPartnerId(partner.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedPartnersTabPartnerId(partner.id);
                            }
                          }}
                          className="group flex items-center gap-4 p-5 rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] shadow-sm dark:shadow-none hover:shadow-lg hover:shadow-gray-200/50 dark:hover:bg-slate-800 hover:border-gray-300/80 dark:hover:border-white/12 transition-all duration-300 text-left active:scale-[0.995] cursor-pointer"
                        >
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                            <Building2 size={24} strokeWidth={1.5} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 dark:text-white truncate text-[15px]">{partner.name}</div>
                            <div className="text-[13px] text-gray-500 dark:text-slate-400 mt-0.5">
                              {count} dossier{count !== 1 ? "s" : ""}
                            </div>
                          </div>
                          {hasPermission(currentUser, "canManagePartners") && (
                            <div
                              className="shrink-0"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                onClick={() => onManageAccess("partner")}
                                className="p-2.5 rounded-xl text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                                title={t("admin.editPartners")}
                              >
                                <Edit2 size={18} strokeWidth={1.75} />
                              </button>
                            </div>
                          )}
                          <ChevronRight size={20} className="shrink-0 text-gray-400 dark:text-slate-500 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transition-colors" />
                        </div>
                      );
                    })}
                    {hasPermission(currentUser, "canManagePartners") && (
                      <button
                        type="button"
                        onClick={() => onManageAccess("partner")}
                        className="group flex items-center gap-5 rounded-3xl border-2 border-dashed border-gray-200 dark:border-slate-600/80 p-6 bg-gray-50/50 dark:bg-slate-800/30 hover:border-blue-300 dark:hover:border-blue-500/50 hover:bg-blue-50/30 dark:hover:bg-blue-500/5 transition-all duration-300 ease-out text-left active:scale-[0.995]"
                      >
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white dark:bg-white/5 border border-gray-200/80 dark:border-white/10 text-gray-400 dark:text-slate-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-300">
                          <UserPlus size={26} strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[17px] font-medium text-gray-600 dark:text-slate-400 group-hover:text-gray-900 dark:group-hover:text-white tracking-tight">
                            Nouveau partenaire
                          </div>
                          <div className="mt-0.5 text-[13px] text-gray-400 dark:text-slate-500">
                            Ajouter un partenaire
                          </div>
                        </div>
                        <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-200" />
                      </button>
                    )}
                  </div>
                )}
                {(partners.length === 0 || displayedPartners.length === 0) && !hasPermission(currentUser, "canManagePartners") && (
                  <div className="flex flex-col items-center justify-center py-16 rounded-3xl bg-white dark:bg-slate-800/60 border border-gray-200/80 dark:border-white/[0.08]">
                    <Building2 size={48} className="text-gray-300 dark:text-slate-500 mb-3" />
                    <p className="text-gray-500 dark:text-slate-400 font-medium text-[15px]">
                      {partners.length === 0 ? "Aucun partenaire" : "Aucun partenaire ne correspond aux filtres"}
                    </p>
                    <p className="text-[13px] text-gray-400 dark:text-slate-500 mt-1">
                      {partners.length === 0 ? "Ajoutez des partenaires dans Gestion Accès." : "Modifiez les filtres (statut ou région) pour afficher plus de résultats."}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              (() => {
                const partner = partners.find((p: User) => p.id === selectedPartnersTabPartnerId);
                const partnerJobs = jobs.filter((j: Job) => j.partnerId === selectedPartnersTabPartnerId);
                const byRegion = new Map<string, Job[]>();
                partnerJobs.forEach((j: Job) => {
                  const rid = j.stockRegionId ?? "__none__";
                  if (!byRegion.has(rid)) byRegion.set(rid, []);
                  byRegion.get(rid)!.push(j);
                });
                const regionIds = Array.from(byRegion.keys()).sort((a, b) => {
                  if (a === "__none__") return 1;
                  if (b === "__none__") return -1;
                  const na = stockRegions.find((r) => r.id === a)?.name ?? "";
                  const nb = stockRegions.find((r) => r.id === b)?.name ?? "";
                  return na.localeCompare(nb);
                });
                return (
                  <>
                    <div className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-4 md:p-5 shadow-sm dark:shadow-none flex flex-col md:flex-row justify-between items-center gap-4">
                      <button
                        type="button"
                        onClick={() => setSelectedPartnersTabPartnerId(null)}
                        className="flex items-center gap-2 text-[15px] text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 font-medium"
                      >
                        <ArrowLeft size={18} className="shrink-0" strokeWidth={2} />
                        Retour aux partenaires
                      </button>
                      <div className="flex flex-wrap gap-2">
                        {hasPermission(currentUser, "canManageStock") && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPartnerIdForStock(selectedPartnersTabPartnerId);
                              const firstRegionWithJobs = regionIds.find((rid) => rid !== "__none__");
                              if (firstRegionWithJobs && stockRegions.some((r) => r.id === firstRegionWithJobs)) {
                                setSelectedRegionId(firstRegionWithJobs);
                                setStockRegionFolderId(firstRegionWithJobs);
                              } else if (stockRegions.length > 0) {
                                setSelectedRegionId(stockRegions[0].id);
                                setStockRegionFolderId(stockRegions[0].id);
                              }
                              requestTabChange("stock");
                              setIsMobileMenuOpen(false);
                            }}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 text-gray-700 dark:text-slate-200 rounded-2xl hover:bg-gray-200/80 dark:hover:bg-white/10 transition-colors text-[15px] font-medium"
                          >
                            <Package size={18} /> Gérer son stock
                          </button>
                        )}
                        {hasPermission(currentUser, "canCreateJobs") && (
                          <>
                            <input ref={importFileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} className="hidden" />
                            <button type="button" onClick={() => importFileInputRef.current?.click()} disabled={importLoading} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 text-gray-700 dark:text-slate-200 rounded-2xl hover:bg-gray-200/80 dark:hover:bg-white/10 transition-colors disabled:opacity-50 text-[15px] font-medium">
                              {importLoading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
                              {importLoading ? t("admin.importLoading") : t("admin.importButton")}
                            </button>
                            <button onClick={() => setIsAddJobModalOpen(true)} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-2xl font-medium text-[15px] hover:opacity-90 transition-opacity">
                              <Plus size={20} /> {t("admin.newJob")}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-1">
                      <Building2 size={18} className="text-indigo-500 dark:text-indigo-400" />
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">{partner?.name ?? "Partenaire"}</h2>
                      <span className="text-sm text-gray-500 dark:text-slate-400">· Chantiers par région</span>
                    </div>
                    <div className="space-y-8">
                      {regionIds.map((rid) => {
                        const regionJobs = byRegion.get(rid)!;
                        const regionName = rid === "__none__" ? "Sans région" : (stockRegions.find((r) => r.id === rid)?.name ?? "Région");
                        return (
                          <div key={rid} className="space-y-3">
                            <h3 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide px-1">
                              {regionName} · {regionJobs.length} dossier{regionJobs.length !== 1 ? "s" : ""}
                            </h3>
                            <div className="md:hidden space-y-3">
                              {regionJobs.map((job: Job) => (
                                <div
                                  key={job.id}
                                  onClick={() => openEditJobModal(job)}
                                  className={`bg-white dark:bg-slate-800 dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 p-4 rounded-2xl shadow-xl dark:shadow-2xl transition-transform cursor-pointer active:scale-[0.99]`}
                                >
                                  <div className="flex justify-between items-start mb-2">
                                    <div>
                                      <h3 className="font-bold text-gray-900 dark:text-white">{job.clientName}</h3>
                                      <p className="text-xs text-gray-500 dark:text-slate-400 tracking-tight">{job.clientAddress}</p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      {canEditJob(job) && <button type="button" onClick={(e) => { e.stopPropagation(); openEditJobModal(job); }} className="p-2 text-gray-400 hover:text-blue-500" title={t("admin.edit")}><Edit2 size={18} /></button>}
                                      {canDeleteJob() && <button onClick={(e) => { e.stopPropagation(); onDeleteJob(job.id); }} className="text-gray-400 hover:text-red-500" title={t("admin.delete")}><Trash2 size={18} /></button>}
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2 mt-3">
                                    {job.stockRegionId && stockRegions.find((r) => r.id === job.stockRegionId)?.name ? (
                                      <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-500/20 px-2 py-1 rounded-xl">
                                        <MapPin size={12} className="shrink-0 opacity-80" />
                                        {stockRegions.find((r) => r.id === job.stockRegionId)?.name}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400 dark:text-slate-500 text-xs">—</span>
                                    )}
                                    <StatusBadge status={job.status} installerId={job.installerId} />
                                    {job.status === "completed" && job.partnerInvoiceTotalCents != null && (
                                      <span className="text-xs font-medium text-green-600 dark:text-green-400">{t("admin.invoiceLabel")} {(job.partnerInvoiceTotalCents / 100).toFixed(2)} €</span>
                                    )}
                                    {job.installerId ? (
                                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-500/20 px-2.5 py-1 rounded-xl">
                                        {installers.find((i: User) => i.id === job.installerId)?.name || job.installerId}
                                      </span>
                                    ) : (
                                      <span className="text-xs text-gray-400 dark:text-slate-500 font-medium">{t("admin.unassigned")}</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="hidden md:block bg-white dark:bg-slate-800 dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl shadow-xl dark:shadow-2xl overflow-hidden">
                              <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm text-gray-600 dark:text-slate-300">
                                  <thead className="bg-gray-50 dark:bg-slate-700 text-xs uppercase font-semibold text-gray-500 dark:text-slate-400">
                                    <tr>
                                      <th className="px-6 py-4">{t("admin.client")}</th>
                                      <th className="px-6 py-4">{t("admin.region")}</th>
                                      <th className="px-6 py-4">Type</th>
                                      <th className="px-6 py-4">{t("admin.statusLabel")}</th>
                                      <th className="px-6 py-4">{t("admin.invoice")}</th>
                                      <th className="px-6 py-4">{t("admin.installer")}</th>
                                      <th className="px-6 py-4 text-right">{t("admin.actions")}</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                                    {regionJobs.map((job: Job) => (
                                      <tr
                                        key={job.id}
                                        className="hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer"
                                        onClick={() => openEditJobModal(job)}
                                      >
                                        <td className="px-6 py-4">
                                          <div className="font-bold text-gray-900 dark:text-white">{job.clientName}</div>
                                          <div className="text-xs text-gray-500 dark:text-slate-400">{job.clientAddress}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                          {job.stockRegionId && stockRegions.find((r) => r.id === job.stockRegionId)?.name ? (
                                            <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-500/20 px-2 py-1 rounded-xl">
                                              <MapPin size={12} className="shrink-0 opacity-80" />
                                              {stockRegions.find((r) => r.id === job.stockRegionId)?.name}
                                            </span>
                                          ) : (
                                            <span className="text-gray-400 dark:text-slate-500 text-xs">—</span>
                                          )}
                                        </td>
                                        <td className="px-6 py-4">
                                          {job.lightingType ? (
                                            <span className="inline-flex items-center gap-1 text-xs font-medium text-teal-600 dark:text-teal-400 bg-teal-100 dark:bg-teal-500/20 px-2 py-1 rounded-xl">
                                              {job.lightingType === "visite technique" ? "Visite technique" : job.lightingType === "pose" ? "Pose" : job.lightingType}
                                            </span>
                                          ) : (
                                            <span className="text-gray-400 dark:text-slate-500 text-xs">—</span>
                                          )}
                                        </td>
                                        <td className="px-6 py-4"><StatusBadge status={job.status} installerId={job.installerId} /></td>
                                        <td className="px-6 py-4 text-gray-600 dark:text-slate-300">
                                          {job.status === "completed" && job.partnerInvoiceTotalCents != null ? (job.partnerInvoiceTotalCents / 100).toFixed(2) + " €" : "—"}
                                        </td>
                                        <td className="px-6 py-4">
                                          {job.installerId ? (
                                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-500/20 px-2.5 py-1 rounded-xl">
                                              {installers.find((i: User) => i.id === job.installerId)?.name || job.installerId}
                                            </span>
                                          ) : (
                                            <span className="text-xs text-gray-400 dark:text-slate-500 font-medium">{t("admin.unassigned")}</span>
                                          )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                          {canEditJob(job) && <button type="button" onClick={(e) => { e.stopPropagation(); openEditJobModal(job); }} className="p-2 text-gray-400 hover:text-blue-500" title={t("admin.edit")}><Edit2 size={18} /></button>}
                                          {canDeleteJob() && <button onClick={(e) => { e.stopPropagation(); onDeleteJob(job.id); }} className="p-2 text-gray-400 hover:text-red-500" title={t("admin.delete")}><Trash2 size={18} /></button>}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()
            )}
          </div>
        )}

        {activeTab === "installers" && canSeeJobsTab(currentUser) && (
          <div className="space-y-6 relative z-10">
            {selectedInstallersTabInstallerId === null ? (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">{t("admin.installers")}</h1>
                    <p className="mt-1 text-[15px] text-gray-500 dark:text-slate-400">{t("admin.chooseInstallerDesc")}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-gray-500 dark:text-slate-400">{t("admin.sortLabel")}</span>
                    <button
                      type="button"
                      onClick={() => setInstallersSortBy("region")}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${installersSortBy === "region" ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300" : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"}`}
                    >
                      Région
                    </button>
                    <button
                      type="button"
                      onClick={() => setInstallersSortBy("count")}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${installersSortBy === "count" ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300" : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"}`}
                    >
                      Nb chantiers
                    </button>
                  </div>
                </div>

                {installers.length === 0 && !hasPermission(currentUser, "canManageInstallers") ? (
                  <div className="flex flex-col items-center justify-center py-16 rounded-3xl bg-white dark:bg-slate-800/60 border border-gray-200/80 dark:border-white/[0.08]">
                    <Wrench size={48} className="text-gray-300 dark:text-slate-500 mb-3" />
                    <p className="text-gray-500 dark:text-slate-400 font-medium text-[15px]">{t("admin.noInstallerHere")}</p>
                    <p className="text-[13px] text-gray-400 dark:text-slate-500 mt-1">{t("admin.addInstallersInAccess")}</p>
                  </div>
                ) : installersSortBy === "count" ? (
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {[...installers]
                      .map((inst: User) => ({
                        inst,
                        count: jobs.filter((j: Job) => j.installerId === inst.id).length,
                      }))
                      .sort((a, b) => b.count - a.count)
                      .map(({ inst, count }) => (
                        <div
                          key={inst.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedInstallersTabInstallerId(inst.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedInstallersTabInstallerId(inst.id);
                            }
                          }}
                          className="group flex items-center gap-4 p-5 rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] shadow-sm dark:shadow-none hover:shadow-lg hover:shadow-gray-200/50 dark:hover:bg-slate-800 hover:border-gray-300/80 dark:hover:border-white/12 transition-all duration-300 text-left active:scale-[0.995] cursor-pointer"
                        >
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400">
                            <Wrench size={24} strokeWidth={1.5} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 dark:text-white truncate text-[15px]">{inst.name}</div>
                            <div className="text-[13px] text-gray-500 dark:text-slate-400 mt-0.5">
                              {count} chantier{count !== 1 ? "s" : ""}
                            </div>
                          </div>
                          {hasPermission(currentUser, "canManageInstallers") && (
                            <div className="shrink-0" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                              <button type="button" onClick={() => onManageAccess("installer")} className="p-2.5 rounded-xl text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors" title={t("admin.manageInstallers")}>
                                <Edit2 size={18} strokeWidth={1.75} />
                              </button>
                            </div>
                          )}
                          <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-orange-500 dark:group-hover:text-orange-400 group-hover:translate-x-0.5 transition-all duration-200" />
                        </div>
                      ))}
                    {hasPermission(currentUser, "canManageInstallers") && (
                      <button
                        type="button"
                        onClick={() => onManageAccess("installer")}
                        className="group flex items-center gap-5 rounded-3xl border-2 border-dashed border-gray-200 dark:border-slate-600/80 p-6 bg-gray-50/50 dark:bg-slate-800/30 hover:border-blue-300 dark:hover:border-blue-500/50 hover:bg-blue-50/30 dark:hover:bg-blue-500/5 transition-all duration-300 ease-out text-left active:scale-[0.995]"
                      >
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white dark:bg-white/5 border border-gray-200/80 dark:border-white/10 text-gray-400 dark:text-slate-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-300">
                          <UserPlus size={26} strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[17px] font-medium text-gray-600 dark:text-slate-400 group-hover:text-gray-900 dark:group-hover:text-white tracking-tight">
                            Nouveau poseur
                          </div>
                          <div className="mt-0.5 text-[13px] text-gray-400 dark:text-slate-500">
                            Ajouter un poseur
                          </div>
                        </div>
                        <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-200" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-8">
                    {stockRegions.length > 0 && hasPermission(currentUser, "canManageInstallers") && (
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        <button
                          type="button"
                          onClick={() => onManageAccess("installer")}
                          className="group flex items-center gap-5 rounded-3xl border-2 border-dashed border-gray-200 dark:border-slate-600/80 p-6 bg-gray-50/50 dark:bg-slate-800/30 hover:border-blue-300 dark:hover:border-blue-500/50 hover:bg-blue-50/30 dark:hover:bg-blue-500/5 transition-all duration-300 ease-out text-left active:scale-[0.995]"
                        >
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white dark:bg-white/5 border border-gray-200/80 dark:border-white/10 text-gray-400 dark:text-slate-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-300">
                            <UserPlus size={26} strokeWidth={1.5} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-[17px] font-medium text-gray-600 dark:text-slate-400 group-hover:text-gray-900 dark:group-hover:text-white tracking-tight">
                              Nouveau poseur
                            </div>
                            <div className="mt-0.5 text-[13px] text-gray-400 dark:text-slate-500">
                              Ajouter un poseur
                            </div>
                          </div>
                          <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-200" />
                        </button>
                      </div>
                    )}
                    {stockRegions.length === 0 ? (
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide px-1">{t("admin.allInstallersTitle")}</h3>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {installers.map((inst: User) => {
                            const count = jobs.filter((j: Job) => j.installerId === inst.id).length;
                            return (
                              <div
                                key={inst.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedInstallersTabInstallerId(inst.id)}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedInstallersTabInstallerId(inst.id); } }}
                                className="group flex items-center gap-4 p-5 rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] shadow-sm dark:shadow-none hover:shadow-lg hover:shadow-gray-200/50 dark:hover:bg-slate-800 hover:border-gray-300/80 dark:hover:border-white/12 transition-all duration-300 text-left active:scale-[0.995] cursor-pointer"
                              >
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400">
                                  <Wrench size={24} strokeWidth={1.5} />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-gray-900 dark:text-white truncate text-[15px]">{inst.name}</div>
                                  <div className="text-[13px] text-gray-500 dark:text-slate-400 mt-0.5">{count} chantier{count !== 1 ? "s" : ""}</div>
                                </div>
                                {hasPermission(currentUser, "canManageInstallers") && (
                                  <div className="shrink-0" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                    <button type="button" onClick={() => onManageAccess("installer")} className="p-2.5 rounded-xl text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors" title={t("admin.manageInstallers")}><Edit2 size={18} strokeWidth={1.75} /></button>
                                  </div>
                                )}
                                <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-orange-500 dark:group-hover:text-orange-400 group-hover:translate-x-0.5 transition-all duration-200" />
                              </div>
                            );
                          })}
                          {hasPermission(currentUser, "canManageInstallers") && (
                            <button
                              type="button"
                              onClick={() => onManageAccess("installer")}
                              className="group flex items-center gap-5 rounded-3xl border-2 border-dashed border-gray-200 dark:border-slate-600/80 p-6 bg-gray-50/50 dark:bg-slate-800/30 hover:border-blue-300 dark:hover:border-blue-500/50 hover:bg-blue-50/30 dark:hover:bg-blue-500/5 transition-all duration-300 ease-out text-left active:scale-[0.995]"
                            >
                              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white dark:bg-white/5 border border-gray-200/80 dark:border-white/10 text-gray-400 dark:text-slate-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-300">
                                <UserPlus size={26} strokeWidth={1.5} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-[17px] font-medium text-gray-600 dark:text-slate-400 group-hover:text-gray-900 dark:group-hover:text-white tracking-tight">
                                  Nouveau poseur
                                </div>
                                <div className="mt-0.5 text-[13px] text-gray-400 dark:text-slate-500">
                                  Ajouter un poseur
                                </div>
                              </div>
                              <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-200" />
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      stockRegions.map((region) => (
                        <div key={region.id} className="space-y-3">
                          <h3 className="text-[13px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider px-1">{region.name}</h3>
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {installers
                              .filter((inst: User) => inst.preferences?.defaultStockRegionId === region.id)
                              .map((inst: User) => {
                              const count = jobs.filter((j: Job) => j.stockRegionId === region.id && j.installerId === inst.id).length;
                              return (
                                <div
                                  key={inst.id}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setSelectedInstallersTabInstallerId(inst.id)}
                                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedInstallersTabInstallerId(inst.id); } }}
                                  className="group flex items-center gap-4 p-5 rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] shadow-sm dark:shadow-none hover:shadow-lg hover:shadow-gray-200/50 dark:hover:bg-slate-800 hover:border-gray-300/80 dark:hover:border-white/12 transition-all duration-300 text-left active:scale-[0.995] cursor-pointer"
                                >
                                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400">
                                    <Wrench size={24} strokeWidth={1.5} />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="font-medium text-gray-900 dark:text-white truncate text-[15px]">{inst.name}</div>
                                    <div className="text-[13px] text-gray-500 dark:text-slate-400 mt-0.5">{count} chantier{count !== 1 ? "s" : ""} dans cette région</div>
                                  </div>
                                  {hasPermission(currentUser, "canManageInstallers") && (
                                    <div className="shrink-0" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                      <button type="button" onClick={() => onManageAccess("installer")} className="p-2.5 rounded-xl text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors" title={t("admin.manageInstallers")}><Edit2 size={18} strokeWidth={1.75} /></button>
                                    </div>
                                  )}
                                  <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-orange-500 dark:group-hover:text-orange-400 group-hover:translate-x-0.5 transition-all duration-200" />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ) : (
              (() => {
                const installer = installers.find((i: User) => i.id === selectedInstallersTabInstallerId);
                const installerJobs = jobs.filter((j: Job) => j.installerId === selectedInstallersTabInstallerId);
                const term = searchTerm.toLowerCase().trim();
                const installerJobsToShow = term
                  ? installerJobs.filter(
                      (j: Job) =>
                        j.clientName.toLowerCase().includes(term) || j.id.toLowerCase().includes(term)
                    )
                  : installerJobs;
                return (
                  <>
                    <div className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-4 md:p-5 shadow-sm dark:shadow-none flex flex-col md:flex-row justify-between items-center gap-4">
                      <button
                        type="button"
                        onClick={() => setSelectedInstallersTabInstallerId(null)}
                        className="flex items-center gap-2 text-[15px] text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 font-medium"
                      >
                        <ArrowLeft size={18} className="shrink-0" strokeWidth={2} />
                        Retour aux poseurs
                      </button>
                      <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto md:items-center">
                        <div className="relative w-full md:w-64 lg:w-80">
                          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400 w-5 h-5" />
                          <input
                            type="text"
                            placeholder="Rechercher dans les dossiers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-gray-50/80 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 rounded-2xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/25 outline-none transition-all"
                          />
                        </div>
                        {hasPermission(currentUser, "canCreateJobs") && (
                          <>
                            <input ref={importFileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} className="hidden" />
                            <button type="button" onClick={() => importFileInputRef.current?.click()} disabled={importLoading} className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 text-gray-700 dark:text-slate-200 rounded-2xl hover:bg-gray-200/80 dark:hover:bg-white/10 transition-colors disabled:opacity-50 text-[15px] font-medium">
                              {importLoading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
                              {importLoading ? t("admin.importLoading") : t("admin.importButton")}
                            </button>
                            <button onClick={() => setIsAddJobModalOpen(true)} className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-2xl font-medium text-[15px] hover:opacity-90 transition-opacity">
                              <Plus size={20} /> {t("admin.newJob")}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-1">
                      <Wrench size={18} className="text-orange-500 dark:text-orange-400" />
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">{installer?.name ?? "Poseur"}</h2>
                      <span className="text-sm text-gray-500 dark:text-slate-400">
                        · {installerJobsToShow.length} dossier{installerJobsToShow.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="md:hidden space-y-3">
                      {installerJobsToShow.map((job: Job) => (
                        <div
                          key={job.id}
                          onClick={() => openEditJobModal(job)}
                          className={`bg-white dark:bg-slate-800 dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 p-4 rounded-2xl shadow-xl dark:shadow-2xl transition-transform cursor-pointer active:scale-[0.99]`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="font-bold text-gray-900 dark:text-white">{job.clientName}</h3>
                              <p className="text-xs text-gray-500 dark:text-slate-400 tracking-tight">{job.clientAddress}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              {canEditJob(job) && <button type="button" onClick={(e) => { e.stopPropagation(); openEditJobModal(job); }} className="p-2 text-gray-400 hover:text-blue-500" title={t("admin.edit")}><Edit2 size={18} /></button>}
                              {canDeleteJob() && <button onClick={(e) => { e.stopPropagation(); onDeleteJob(job.id); }} className="text-gray-400 hover:text-red-500" title={t("admin.delete")}><Trash2 size={18} /></button>}
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-3">
                            {job.stockRegionId && stockRegions.find((r) => r.id === job.stockRegionId)?.name ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-500/20 px-2 py-1 rounded-xl">
                                <MapPin size={12} className="shrink-0 opacity-80" />
                                {stockRegions.find((r) => r.id === job.stockRegionId)?.name}
                              </span>
                            ) : (
                              <span className="text-gray-400 dark:text-slate-500 text-xs">—</span>
                            )}
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-500/20 px-2 py-1 rounded-xl">
                              {getPartnerOrCreatorLabel(job)}
                            </span>
                            <StatusBadge status={job.status} installerId={job.installerId} />
                            {job.status === "completed" && job.partnerInvoiceTotalCents != null && (
                              <span className="text-xs font-medium text-green-600 dark:text-green-400">{t("admin.invoiceLabel")} {(job.partnerInvoiceTotalCents / 100).toFixed(2)} €</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="hidden md:block bg-white dark:bg-slate-800 dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl shadow-xl dark:shadow-2xl overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm text-gray-600 dark:text-slate-300">
                          <thead className="bg-gray-50 dark:bg-slate-700 text-xs uppercase font-semibold text-gray-500 dark:text-slate-400">
                            <tr>
                              <th className="px-6 py-4">{t("admin.client")}</th>
                              <th className="px-6 py-4">Partenaire</th>
                              <th className="px-6 py-4">{t("admin.region")}</th>
                              <th className="px-6 py-4">Type</th>
                              <th className="px-6 py-4">{t("admin.statusLabel")}</th>
                              <th className="px-6 py-4">{t("admin.invoice")}</th>
                              <th className="px-6 py-4 text-right">{t("admin.actions")}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                            {installerJobsToShow.map((job: Job) => (
                              <tr
                                key={job.id}
                                className="hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer"
                                onClick={() => openEditJobModal(job)}
                              >
                                <td className="px-6 py-4">
                                  <div className="font-bold text-gray-900 dark:text-white">{job.clientName}</div>
                                  <div className="text-xs text-gray-500 dark:text-slate-400">{job.clientAddress}</div>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-500/20 px-2 py-1 rounded-xl">
                                    {getPartnerOrCreatorLabel(job)}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  {job.stockRegionId && stockRegions.find((r) => r.id === job.stockRegionId)?.name ? (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-500/20 px-2 py-1 rounded-xl">
                                      <MapPin size={12} className="shrink-0 opacity-80" />
                                      {stockRegions.find((r) => r.id === job.stockRegionId)?.name}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400 dark:text-slate-500 text-xs">—</span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  {job.lightingType ? (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-teal-600 dark:text-teal-400 bg-teal-100 dark:bg-teal-500/20 px-2 py-1 rounded-xl">
                                      {job.lightingType === "visite technique" ? "Visite technique" : job.lightingType === "pose" ? "Pose" : job.lightingType}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400 dark:text-slate-500 text-xs">—</span>
                                  )}
                                </td>
                                <td className="px-6 py-4"><StatusBadge status={job.status} installerId={job.installerId} /></td>
                                <td className="px-6 py-4 text-gray-600 dark:text-slate-300">
                                  {job.status === "completed" && job.partnerInvoiceTotalCents != null ? (job.partnerInvoiceTotalCents / 100).toFixed(2) + " €" : "—"}
                                </td>
                                <td className="px-6 py-4 text-right">
                                  {canEditJob(job) && <button type="button" onClick={(e) => { e.stopPropagation(); openEditJobModal(job); }} className="p-2 text-gray-400 hover:text-blue-500" title={t("admin.edit")}><Edit2 size={18} /></button>}
                                  {canDeleteJob() && <button onClick={(e) => { e.stopPropagation(); onDeleteJob(job.id); }} className="p-2 text-gray-400 hover:text-red-500" title={t("admin.delete")}><Trash2 size={18} /></button>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {installerJobsToShow.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-16 rounded-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-white/10">
                        <Briefcase size={48} className="text-gray-300 dark:text-slate-600 mb-3" />
                        <p className="text-gray-500 dark:text-slate-400 font-medium">{t("admin.noJobs")}</p>
                        <p className="text-sm text-gray-400 dark:text-slate-500 mt-1">
                          {searchTerm.trim() ? "Essayez un autre terme." : "Aucun dossier assigné à ce poseur."}
                        </p>
                      </div>
                    )}
                  </>
                );
              })()
            )}
          </div>
        )}

        {activeTab === "budget" && canSeeJobsTab(currentUser) && (
          <div className="w-full">
            {/* En-tête : titre à gauche, segment control en haut à droite */}
            <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">
                  Budget partenaires
                </h1>
                <p className="mt-1 text-[15px] text-gray-500 dark:text-slate-400 max-w-2xl">
                  Tarifs par partenaire, région et type — facturation et détail des factures.
                </p>
              </div>
              <div className="shrink-0 flex flex-wrap items-center justify-end sm:justify-end">
                <div className="inline-flex rounded-2xl bg-gray-100/80 dark:bg-white/[0.06] p-1 border border-gray-200/60 dark:border-white/10">
                  {(["overview", "tariffs", "invoices"] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setBudgetSubTab(tab)}
                      className={`px-5 py-2.5 rounded-xl text-[14px] font-medium transition-all duration-200 ${
                        budgetSubTab === tab
                          ? "bg-white dark:bg-slate-700/90 text-gray-900 dark:text-white shadow-sm dark:shadow-none border border-gray-200/80 dark:border-white/10"
                          : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                      }`}
                    >
                      {tab === "overview" ? t("admin.budgetOverviewTab") : tab === "tariffs" ? t("admin.budgetTariffsTab") : t("admin.budgetInvoicesTab")}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {budgetPrices.loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-500 dark:text-slate-400">
                  <Loader2 size={24} className="animate-spin" />
                  <span className="text-[15px]">{t("admin.loadingTariffs")}</span>
                </div>
              </div>
            ) : budgetPrices.error ? (
              <div className="rounded-3xl bg-red-50/80 dark:bg-red-950/20 border border-red-200/80 dark:border-red-500/20 p-6">
                <p className="text-red-600 dark:text-red-400 font-medium">{budgetPrices.error}</p>
              </div>
            ) : (
              <>
                {/* ——— Vue d'ensemble ——— */}
                {budgetSubTab === "overview" && (
                  <div className="space-y-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-6 shadow-sm dark:shadow-none">
                        <p className="text-[12px] font-medium uppercase tracking-wider text-gray-400 dark:text-slate-500">{t("admin.totalInvoiced")}</p>
                        <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white tabular-nums tracking-tight">
                          {(budgetStats.totalCents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                        </p>
                      </div>
                      <div className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-6 shadow-sm dark:shadow-none">
                        <p className="text-[12px] font-medium uppercase tracking-wider text-gray-400 dark:text-slate-500">{t("admin.jobsInvoicedCount")}</p>
                        <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white tabular-nums">{budgetStats.count}</p>
                      </div>
                      <div className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-6 shadow-sm dark:shadow-none sm:col-span-2 lg:col-span-1 flex flex-col justify-center">
                        {onRecalculatePartnerInvoices && (
                          <div className="flex flex-col gap-2">
                            <button
                              type="button"
                              disabled={recalculateInvoicesLoading}
                              onClick={async () => {
                                setRecalculateInvoicesMessage(null);
                                setRecalculateInvoicesLoading(true);
                                try {
                                  const { updated, error } = await onRecalculatePartnerInvoices();
                                  if (error) setRecalculateInvoicesMessage(`Erreur : ${error}`);
                                  else setRecalculateInvoicesMessage(updated === 0 ? "Aucun dossier à recalculer." : `${updated} facture(s) recalculée(s).`);
                                } finally {
                                  setRecalculateInvoicesLoading(false);
                                }
                              }}
                              className="w-full px-4 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium transition-colors"
                            >
                              {recalculateInvoicesLoading ? t("admin.recalculateLoading") : t("admin.recalculateInvoices")}
                            </button>
                            {recalculateInvoicesMessage && (
                              <p className="text-[13px] text-gray-600 dark:text-slate-400">{recalculateInvoicesMessage}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="rounded-3xl bg-white/60 dark:bg-slate-800/60 border border-gray-200/60 dark:border-white/[0.06] p-6">
                      <p className="text-[14px] text-gray-600 dark:text-slate-400">
                        {t("admin.budgetIntro")}
                      </p>
                    </div>
                  </div>
                )}

                {/* ——— Tarifs ——— */}
                {budgetSubTab === "tariffs" && (
                  <div className="space-y-8">
                    {budgetPartnerId === null ? (
                      <div className="space-y-6">
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">{t("admin.tariffGrid")}</h2>
                          <p className="mt-1 text-[14px] text-gray-500 dark:text-slate-400">
                            Choisissez un partenaire pour gérer ses tarifs par région et par type.
                          </p>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          {[
                            { id: "__internal__", label: "Stock interne" },
                            ...(partners ?? []).map((p: User) => ({
                              id: p.id,
                              label: p.company ?? p.name ?? p.id,
                            })),
                          ].map(({ id, label }) => (
                            <div
                              key={id}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                setBudgetPartnerId(id);
                                setBudgetRegionId("");
                                setBudgetRefId("");
                                setBudgetEditedCents(null);
                                setBudgetPriceDirty(false);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setBudgetPartnerId(id);
                                  setBudgetRegionId("");
                                  setBudgetRefId("");
                                  setBudgetEditedCents(null);
                                  setBudgetPriceDirty(false);
                                }
                              }}
                              className="group relative flex items-center gap-5 rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-6 shadow-sm dark:shadow-none hover:shadow-lg hover:shadow-gray-200/50 dark:hover:bg-slate-800 hover:border-gray-300/80 dark:hover:border-white/12 transition-all duration-300 ease-out text-left active:scale-[0.995] cursor-pointer"
                            >
                              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-indigo-100/80 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 transition-colors duration-300">
                                <Building2 size={26} strokeWidth={1.5} className="opacity-90" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <span className="text-[17px] font-medium text-gray-900 dark:text-white truncate tracking-tight block">
                                  {label}
                                </span>
                                <div className="mt-0.5 text-[13px] text-gray-500 dark:text-slate-400">
                                  Voir les régions
                                </div>
                              </div>
                              <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 group-hover:translate-x-0.5 transition-all duration-200" />
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : !budgetRegionId ? (
                      <div className="space-y-6">
                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setBudgetPartnerId(null);
                              setBudgetRegionId("");
                              setBudgetRefId("");
                              setBudgetEditedCents(null);
                              setBudgetPriceDirty(false);
                            }}
                            className="flex items-center gap-2 text-[15px] text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                          >
                            <ArrowLeft size={18} className="shrink-0" strokeWidth={2} />
                            Partenaires
                          </button>
                          <span className="text-gray-300 dark:text-slate-600">/</span>
                          <span className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
                            {budgetPartnerId === "__internal__"
                              ? "Stock interne"
                              : (partners ?? []).find((p: User) => p.id === budgetPartnerId)?.company ??
                                (partners ?? []).find((p: User) => p.id === budgetPartnerId)?.name ??
                                "Partenaire"}
                          </span>
                        </div>
                        <p className="text-[14px] text-gray-500 dark:text-slate-400">
                          Choisissez une région pour voir et modifier les tarifs de ce partenaire.
                        </p>
                        {(stockRegions ?? []).length === 0 ? (
                          <div className="rounded-3xl border border-gray-200/80 dark:border-white/[0.08] p-8 text-center text-gray-500 dark:text-slate-400 text-[14px]">
                            Aucune région. Créez-en une dans l&apos;onglet Stock.
                          </div>
                        ) : (
                          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {(stockRegions ?? []).map((r) => {
                              const count = getStockForRegionAndPartner(r.id, resolvedBudgetPartnerId).length;
                              return (
                                <div
                                  key={r.id}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => {
                                    setBudgetRegionId(r.id);
                                    const refs = sortBudgetStockItems(
                                      getStockForRegionAndPartner(r.id, resolvedBudgetPartnerId)
                                    );
                                    setBudgetRefId(refs[0]?.id ?? "");
                                    setBudgetEditedCents(null);
                                    setBudgetPriceDirty(false);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      setBudgetRegionId(r.id);
                                      const refs = sortBudgetStockItems(
                                        getStockForRegionAndPartner(r.id, resolvedBudgetPartnerId)
                                      );
                                      setBudgetRefId(refs[0]?.id ?? "");
                                      setBudgetEditedCents(null);
                                      setBudgetPriceDirty(false);
                                    }
                                  }}
                                  className="group relative flex items-center gap-5 rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-6 shadow-sm dark:shadow-none hover:shadow-lg hover:shadow-gray-200/50 dark:hover:bg-slate-800 hover:border-gray-300/80 dark:hover:border-white/12 transition-all duration-300 ease-out text-left active:scale-[0.995] cursor-pointer"
                                >
                                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-emerald-100/80 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                                    <MapPin size={26} strokeWidth={1.5} className="opacity-90" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="text-[17px] font-medium text-gray-900 dark:text-white truncate tracking-tight block">
                                      {r.name}
                                    </span>
                                    <div className="mt-0.5 text-[13px] text-gray-500 dark:text-slate-400">
                                      {count} type{count !== 1 ? "s" : ""} / référence{count !== 1 ? "s" : ""}
                                    </div>
                                  </div>
                                  <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-emerald-500 dark:group-hover:text-emerald-400 group-hover:translate-x-0.5 transition-all duration-200" />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      (() => {
                        const partnerLabel =
                          budgetPartnerId === "__internal__"
                            ? "Stock interne"
                            : (partners ?? []).find((p: User) => p.id === budgetPartnerId)?.company ??
                              (partners ?? []).find((p: User) => p.id === budgetPartnerId)?.name ??
                              "Partenaire";
                        const regionLabel =
                          (stockRegions ?? []).find((r) => r.id === budgetRegionId)?.name ?? "Région";
                        const regionStock = sortBudgetStockItems(
                          getStockForRegionAndPartner(budgetRegionId, resolvedBudgetPartnerId)
                        );
                        return (
                          <div className="space-y-8">
                            <div className="flex flex-wrap items-center gap-3">
                              <button
                                type="button"
                                onClick={() => {
                                  setBudgetPartnerId(null);
                                  setBudgetRegionId("");
                                  setBudgetRefId("");
                                  setBudgetEditedCents(null);
                                  setBudgetPriceDirty(false);
                                }}
                                className="flex items-center gap-2 text-[15px] text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                              >
                                <ArrowLeft size={18} className="shrink-0" strokeWidth={2} />
                                Partenaires
                              </button>
                              <span className="text-gray-300 dark:text-slate-600">/</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setBudgetRegionId("");
                                  setBudgetRefId("");
                                  setBudgetEditedCents(null);
                                  setBudgetPriceDirty(false);
                                }}
                                className="text-[15px] text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                              >
                                {partnerLabel}
                              </button>
                              <span className="text-gray-300 dark:text-slate-600">/</span>
                              <span className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
                                {regionLabel}
                              </span>
                            </div>

                            <div className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] overflow-hidden shadow-sm dark:shadow-none">
                              <div className="p-6 border-b border-gray-100 dark:border-white/[0.06]">
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">{t("admin.tariffGrid")}</h2>
                                <p className="mt-1 text-[14px] text-gray-500 dark:text-slate-400">
                                  {t("admin.unitPrice")} par type — neuf et remplacement (ce partenaire, cette région).
                                </p>
                              </div>
                              {regionStock.length === 0 ? (
                                <div className="p-8 text-center text-gray-500 dark:text-slate-400 text-[14px]">
                                  Aucun type dans cette région pour ce partenaire.
                                </div>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-left border-collapse">
                                    <thead>
                                      <tr className="bg-gray-50/80 dark:bg-white/[0.04] border-b border-gray-200/80 dark:border-white/[0.08]">
                                        <th className="py-3.5 px-5 text-[11px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Type</th>
                                        <th className="py-3.5 px-5 text-[11px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider text-right w-28">{t("admin.newPrice")}</th>
                                        <th className="py-3.5 px-5 text-[11px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider text-right w-28">{t("admin.replacementPrice")}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {regionStock.map((item) => (
                                        <tr key={item.id} className="border-b border-gray-100 dark:border-white/[0.04]">
                                          <td className="py-3.5 px-5 font-medium text-gray-900 dark:text-white">{item.name}</td>
                                          <td className="py-3.5 px-5 text-right tabular-nums text-gray-700 dark:text-slate-200">
                                            {(budgetPrices.getPrice(budgetRegionId, item.id, "neuf") / 100).toFixed(2)}
                                          </td>
                                          <td className="py-3.5 px-5 text-right tabular-nums text-gray-700 dark:text-slate-200">
                                            {(budgetPrices.getPrice(budgetRegionId, item.id, "remplacement") / 100).toFixed(2)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>

                            <div className="rounded-3xl overflow-hidden bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border border-gray-200/60 dark:border-white/10 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.02)] dark:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.3)]">
                              <div className="p-6 md:p-8">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400 dark:text-slate-500 mb-4">{t("admin.editTariff")}</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  <div className="relative">
                                    <label className="block text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-2">Type</label>
                                    <div className="relative">
                                      <Package className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" />
                                      <select
                                        value={budgetRefId}
                                        onChange={(e) => {
                                          setBudgetRefId(e.target.value);
                                          setBudgetEditedCents(null);
                                          setBudgetPriceDirty(false);
                                        }}
                                        className="w-full pl-10 pr-10 py-3 rounded-2xl border border-gray-200/80 dark:border-white/10 bg-gray-50/80 dark:bg-white/5 text-gray-900 dark:text-white text-[15px] font-medium focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400/50 outline-none transition-all appearance-none cursor-pointer disabled:opacity-60"
                                        disabled={regionStock.length === 0}
                                      >
                                        <option value="">Choisir un type</option>
                                        {regionStock.map((item) => (
                                          <option key={item.id} value={item.id}>{item.name}</option>
                                        ))}
                                      </select>
                                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" />
                                    </div>
                                  </div>
                                  <div className="relative">
                                    <label className="block text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-2">Neuf / Remplacement</label>
                                    <div className="relative">
                                      <Euro className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" />
                                      <select
                                        value={budgetTariffType}
                                        onChange={(e) => {
                                          setBudgetTariffType(e.target.value as "neuf" | "remplacement");
                                          setBudgetEditedCents(null);
                                          setBudgetPriceDirty(false);
                                        }}
                                        className="w-full pl-10 pr-10 py-3 rounded-2xl border border-gray-200/80 dark:border-white/10 bg-gray-50/80 dark:bg-white/5 text-gray-900 dark:text-white text-[15px] font-medium focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400/50 outline-none transition-all appearance-none cursor-pointer"
                                      >
                                        <option value="neuf">{t("admin.newLabel")}</option>
                                        <option value="remplacement">{t("admin.replacementLabel")}</option>
                                      </select>
                                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" />
                                    </div>
                                  </div>
                                </div>
                                {(() => {
                                  const selectedRef = regionStock.find((s) => s.id === budgetRefId) ?? regionStock[0];
                                  const currentCents = selectedRef
                                    ? budgetPrices.getPrice(budgetRegionId, selectedRef.id, budgetTariffType)
                                    : 0;
                                  if (!selectedRef) return null;
                                  return (
                                    <div className="mt-6 flex flex-col sm:flex-row sm:items-end gap-4">
                                      <div className="flex-1 max-w-xs">
                                        <label className="block text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-2">{t("admin.unitPrice")}</label>
                                        <input
                                          type="number"
                                          min={0}
                                          step={0.01}
                                          value={budgetEditedCents !== null ? budgetEditedCents / 100 : currentCents / 100}
                                          onChange={(e) => {
                                            const cents = Math.round((parseFloat(e.target.value) || 0) * 100);
                                            setBudgetEditedCents(cents);
                                            setBudgetPriceDirty(true);
                                          }}
                                          className="w-full px-5 py-3.5 rounded-2xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-slate-800/80 text-gray-900 dark:text-white text-[17px] font-semibold tracking-tight focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400/50 outline-none transition-all"
                                        />
                                      </div>
                                      <div className="flex flex-wrap items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const cents = budgetEditedCents !== null ? budgetEditedCents : currentCents;
                                            budgetPrices.setPrice(budgetRegionId, selectedRef.id, budgetTariffType, cents);
                                            setBudgetEditedCents(null);
                                            setBudgetPriceDirty(false);
                                            if (budgetPendingTab !== null) {
                                              setActiveTab(budgetPendingTab);
                                              setBudgetPendingTab(null);
                                            }
                                          }}
                                          disabled={!budgetPriceDirty && budgetPendingTab === null}
                                          className="px-4 py-2.5 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[14px] font-medium hover:opacity-90 disabled:opacity-50 disabled:pointer-events-none transition-opacity"
                                        >
                                          Enregistrer
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setBudgetEditedCents(null);
                                            setBudgetPriceDirty(false);
                                            if (budgetPendingTab !== null) {
                                              setActiveTab(budgetPendingTab);
                                              setBudgetPendingTab(null);
                                            }
                                          }}
                                          disabled={!budgetPriceDirty && budgetPendingTab === null}
                                          className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-700/80 text-gray-700 dark:text-slate-200 text-[14px] font-medium hover:bg-gray-50 dark:hover:bg-slate-600/80 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                                        >
                                          Annuler
                                        </button>
                                      </div>
                                      {budgetPriceDirty && (
                                        <p className="text-[13px] text-amber-600 dark:text-amber-400 w-full sm:w-auto">
                                          Modifications non enregistrées.
                                        </p>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        );
                      })()
                    )}
                  </div>
                )}

                {/* ——— Factures ——— */}
                {budgetSubTab === "invoices" && (
                  <div className="space-y-8">
                    {/* Résultats par partenaire */}
                    <div className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-6 shadow-sm dark:shadow-none">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight mb-1">{t("admin.byPartner")}</h2>
                      <p className="text-[14px] text-gray-500 dark:text-slate-400 mb-4">{t("admin.totalInvoicesByPartner")}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="text-xs font-medium text-gray-500 dark:text-slate-400">{t("admin.sortLabel")}</span>
                        <button type="button" onClick={() => setPartnersResultsSortBy("name")} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${partnersResultsSortBy === "name" ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300" : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"}`}>Nom</button>
                        <button type="button" onClick={() => setPartnersResultsSortBy("region")} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${partnersResultsSortBy === "region" ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300" : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"}`}>Région</button>
                        <button type="button" onClick={() => setPartnersResultsSortBy(partnersResultsSortBy === "totalDesc" ? "totalAsc" : "totalDesc")} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${partnersResultsSortBy === "totalDesc" || partnersResultsSortBy === "totalAsc" ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300" : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"}`}>Montant {partnersResultsSortBy === "totalAsc" ? "↑" : "↓"}</button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-slate-600">
                              <th className="py-3 px-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">{t("admin.partnerLabel")}</th>
                              <th className="py-3 px-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">{t("admin.mainRegionLabel")}</th>
                              <th className="py-3 px-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">{t("admin.jobsTabLabel")}</th>
                              <th className="py-3 px-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">{t("admin.totalInvoiced")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const withTotals = (partners ?? []).map((p) => {
                                const partnerJobs = jobs.filter((j: Job) => j.partnerId === p.id && j.status === "completed");
                                const totalCents = partnerJobs.reduce((sum, j) => sum + (j.partnerInvoiceTotalCents ?? 0), 0);
                                const regionCounts: Record<string, number> = {};
                                partnerJobs.forEach((j: Job) => {
                                  const r = j.stockRegionId ?? "";
                                  regionCounts[r] = (regionCounts[r] ?? 0) + 1;
                                });
                                const mainRegionId = Object.keys(regionCounts).sort((a, b) => regionCounts[b] - regionCounts[a])[0];
                                const mainRegion = (stockRegions ?? []).find((r) => r.id === mainRegionId);
                                return { partner: p, totalCents, count: partnerJobs.length, mainRegionName: mainRegion?.name ?? "—" };
                              });
                              const sorted = [...withTotals].sort((a, b) => {
                                if (partnersResultsSortBy === "name") return (a.partner.name ?? "").localeCompare(b.partner.name ?? "");
                                if (partnersResultsSortBy === "region") return a.mainRegionName.localeCompare(b.mainRegionName) || (a.partner.name ?? "").localeCompare(b.partner.name ?? "");
                                if (partnersResultsSortBy === "totalAsc") return a.totalCents - b.totalCents;
                                return b.totalCents - a.totalCents;
                              });
                              return sorted.map(({ partner, totalCents, count, mainRegionName }) => (
                                <tr key={partner.id} className="border-b border-gray-100 dark:border-slate-700/50">
                                  <td className="py-3 px-2 font-medium text-gray-900 dark:text-white">{partner.name ?? partner.company ?? "—"}</td>
                                  <td className="py-3 px-2 text-gray-600 dark:text-slate-300">{mainRegionName}</td>
                                  <td className="py-3 px-2 text-gray-600 dark:text-slate-300">{count}</td>
                                  <td className="py-3 px-2 font-semibold text-gray-900 dark:text-white">{(totalCents / 100).toFixed(2)} €</td>
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Détail des factures : cartes premium, détail matériel dépliable */}
              <div className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-6 md:p-8 shadow-sm dark:shadow-none">
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">{t("admin.invoiceDetail")}</h2>
                  <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Dossiers terminés · partenaire, poseur, matériel (neuf / remplacement)</p>
                </div>
                {(() => {
                  const completedWithInvoice = (jobs ?? []).filter(
                    (j: Job) => j.status === "completed" && j.partnerId != null
                  );
                  const sorted = completedWithInvoice
                    .slice()
                    .sort((a: Job, b: Job) => (b.date ?? "").localeCompare(a.date ?? ""));
                  if (sorted.length === 0) {
                    return (
                      <div className="py-12 rounded-2xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-200/60 dark:border-white/[0.06] text-center">
                        <p className="text-[14px] text-gray-500 dark:text-slate-400">{t("admin.noJobWithPartner")}</p>
                      </div>
                    );
                  }
                  return (
                    <ul className="space-y-3">
                      {sorted.map((job: Job) => {
                        const partner = (partners ?? []).find((p) => p.id === job.partnerId);
                        const installer = (installers ?? []).find((i: User) => i.id === job.installerId);
                        const region = (stockRegions ?? []).find((r) => r.id === job.stockRegionId);
                        const neuf = job.stockUsedNeuf ?? {};
                        const rem = job.stockUsedRemplacement ?? {};
                        const allItemIds = new Set([...Object.keys(neuf), ...Object.keys(rem)]);
                        const hasDetail = allItemIds.size > 0 && job.stockRegionId;
                        const hasLegacyUsage = !hasDetail && job.stockUsed && Object.keys(job.stockUsed).length > 0;
                        const isExpanded = expandedInvoiceJobId === job.id;
                        const totalCents = job.partnerInvoiceTotalCents ?? 0;
                        const meta = [
                          job.date ? new Date(job.date + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : null,
                          partner?.name ?? partner?.company ?? null,
                          installer?.name ?? null,
                          region?.name ?? null,
                        ].filter(Boolean).join(" · ");
                        return (
                          <li key={job.id}>
                            <div
                              className={`rounded-2xl border transition-all duration-200 ease-out overflow-hidden ${
                                isExpanded
                                  ? "border-gray-300/80 dark:border-white/15 bg-gray-50/50 dark:bg-white/[0.04] shadow-sm"
                                  : "border-gray-200/80 dark:border-white/[0.08] bg-white dark:bg-slate-800/80 hover:border-gray-300/80 dark:hover:border-white/12 hover:shadow-md"
                              }`}
                            >
                              <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-gray-900 dark:text-white text-[15px] truncate">{job.clientName}</p>
                                  {meta && (
                                    <p className="text-[13px] text-gray-500 dark:text-slate-400 mt-1 truncate">{meta}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 shrink-0">
                                  <p className="text-base font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">{(totalCents / 100).toFixed(2)} €</p>
                                  <button
                                    type="button"
                                    onClick={() => openEditJobModal(job)}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                                    title={t("admin.editJob")}
                                  >
                                    <Edit2 size={14} />
                                    {t("admin.openDossier")}
                                  </button>
                                  {(hasDetail || hasLegacyUsage) && (
                                    <button
                                      type="button"
                                      onClick={() => setExpandedInvoiceJobId(isExpanded ? null : job.id)}
                                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white transition-colors"
                                      aria-expanded={isExpanded}
                                    >
                                      {isExpanded ? "Masquer" : "Détail"}
                                      {isExpanded ? <ChevronDown size={16} className="rotate-180" /> : <ChevronDown size={16} />}
                                    </button>
                                  )}
                                </div>
                              </div>
                              {isExpanded && (hasDetail || hasLegacyUsage) && (
                                <div className="px-5 pb-5 pt-0">
                                  <div className="rounded-xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-4 space-y-3">
                                    {hasDetail && job.stockRegionId ? (
                                      <>
                                        {Array.from(allItemIds).map((itemId) => {
                                          const qtyNeuf = neuf[itemId] ?? 0;
                                          const qtyRem = rem[itemId] ?? 0;
                                          const priceNeuf = budgetPrices.getPrice(job.stockRegionId!, itemId, "neuf");
                                          const priceRem = budgetPrices.getPrice(job.stockRegionId!, itemId, "remplacement");
                                          const itemName = stock.find((s: StockItem) => s.id === itemId)?.name ?? itemId;
                                          const subNeuf = (qtyNeuf * priceNeuf) / 100;
                                          const subRem = (qtyRem * priceRem) / 100;
                                          return (
                                            <div key={itemId} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[13px]">
                                              <span className="font-medium text-gray-900 dark:text-white">{itemName}</span>
                                              {qtyNeuf > 0 && (
                                                <span className="text-gray-600 dark:text-slate-300">
                                                  Neuf : {qtyNeuf} × {(priceNeuf / 100).toFixed(2)} € = {subNeuf.toFixed(2)} €
                                                </span>
                                              )}
                                              {qtyRem > 0 && (
                                                <span className="text-gray-600 dark:text-slate-300">
                                                  Remplacement : {qtyRem} × {(priceRem / 100).toFixed(2)} € = {subRem.toFixed(2)} €
                                                </span>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </>
                                    ) : job.stockUsed ? (
                                      <div className="space-y-1.5 text-[13px] text-gray-600 dark:text-slate-300">
                                        {Object.entries(job.stockUsed)
                                          .filter(([, q]) => q > 0)
                                          .map(([itemId, qty]) => {
                                            const itemName = stock.find((s: StockItem) => s.id === itemId)?.name ?? itemId;
                                            return (
                                              <p key={itemId}>{itemName} : {qty} unité{qty > 1 ? "s" : ""} (détail neuf/remplacement non enregistré)</p>
                                            );
                                          })}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  );
                })()}
              </div>

                  </div>
                )}

              </>
            )}
          </div>
        )}

        {activeTab === "stock" && canSeeStockTab(currentUser) && (
          <div className="w-full">
            <div className="flex rounded-2xl bg-gray-100 dark:bg-white/5 p-1 mb-6 w-fit">
              <button
                type="button"
                onClick={() => { setStockViewMode("by_region"); setStockPartnerFolderId(null); }}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${stockViewMode === "by_region" ? "bg-white dark:bg-slate-800 text-gray-900 dark:text-white shadow-sm" : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"}`}
              >
                Par région
              </button>
              <button
                type="button"
                onClick={() => { setStockViewMode("by_partner"); setStockRegionFolderId(null); }}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${stockViewMode === "by_partner" ? "bg-white dark:bg-slate-800 text-gray-900 dark:text-white shadow-sm" : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"}`}
              >
                Par partenaire
              </button>
            </div>
            {stockViewMode === "by_region" && (
            <>
            {stockRegionFolderId === null ? (
              /* {t("admin.listView")} des régions de stock — même charte que l’onglet Régions */
              <div className="space-y-8">
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">
                    {t("admin.stockRegionsTitle")}
                  </h1>
                  <p className="mt-1 text-[15px] text-gray-500 dark:text-slate-400">
                    Choisissez une région pour gérer son inventaire.
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {stockRegions.map((region) => {
                    const count = stock.filter((s) => s.regionId === region.id).length;
                    const lowCountInRegion = getLowStockCountForRegion(region.id);
                    return (
                      <div
                        key={region.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setStockRegionFolderId(region.id);
                          setSelectedRegionId(region.id);
                          setStockRegionDetailPartnerFilter("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setStockRegionFolderId(region.id);
                            setSelectedRegionId(region.id);
                            setStockRegionDetailPartnerFilter("");
                          }
                        }}
                        className="group relative flex items-center gap-5 rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-6 shadow-sm dark:shadow-none hover:shadow-lg hover:shadow-gray-200/50 dark:hover:bg-slate-800 hover:border-gray-300/80 dark:hover:border-white/12 transition-all duration-300 ease-out text-left active:scale-[0.995] cursor-pointer"
                      >
                        {lowCountInRegion > 0 && (
                          <span className="absolute top-3 right-3 z-10" onClick={(e) => e.stopPropagation()}>
                            <PulseBadge count={lowCountInRegion} />
                          </span>
                        )}
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-blue-100/80 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 transition-colors duration-300">
                          <MapPin size={26} strokeWidth={1.5} className="opacity-90" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[17px] font-medium text-blue-700 dark:text-blue-300 truncate tracking-tight">
                              {region.name}
                            </span>
                          </div>
                          <div className="mt-0.5 text-[13px] text-gray-500 dark:text-slate-400">
                            {count} référence{count !== 1 ? "s" : ""}
                          </div>
                        </div>
                        <div
                          className="flex items-center gap-0.5 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          {onUpdateRegion && (
                            <button
                              type="button"
                              onClick={() => setEditingRegion(region)}
                              className="p-2.5 rounded-xl text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors duration-200"
                              title={t("admin.editRegion")}
                            >
                              <Edit2 size={18} strokeWidth={1.75} />
                            </button>
                          )}
                          {onRemoveRegion && (
                            <button
                              type="button"
                              onClick={async () => {
                                if (!window.confirm(t("admin.deleteRegionConfirm", { name: region.name }))) return;
                                const { error } = await onRemoveRegion(region.id);
                                if (!error) {
                                  if (stockRegionFolderId === region.id) setStockRegionFolderId(null);
                                  if (selectedRegionId === region.id) setSelectedRegionId(stockRegions.find((r) => r.id !== region.id)?.id ?? null);
                                  refetchStockRegions?.();
                                }
                              }}
                              className="p-2.5 rounded-xl text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50/80 dark:hover:bg-red-500/10 transition-colors duration-200"
                              title={t("admin.deleteRegion")}
                            >
                              <Trash2 size={18} strokeWidth={1.75} />
                            </button>
                          )}
                        </div>
                        <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all duration-200" />
                      </div>
                    );
                  })}
                  {onAddRegion && (
                    <button
                      type="button"
                      onClick={() => setIsAddRegionModalOpen(true)}
                      className="group flex items-center gap-5 rounded-3xl border-2 border-dashed border-gray-200 dark:border-slate-600/80 p-6 bg-gray-50/50 dark:bg-slate-800/30 hover:border-blue-300 dark:hover:border-blue-500/50 hover:bg-blue-50/30 dark:hover:bg-blue-500/5 transition-all duration-300 ease-out text-left active:scale-[0.995]"
                    >
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white dark:bg-white/5 border border-gray-200/80 dark:border-white/10 text-gray-400 dark:text-slate-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-300">
                        <ListPlus size={26} strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[17px] font-medium text-gray-600 dark:text-slate-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 tracking-tight">
                          Nouvelle région
                        </div>
                        <div className="mt-0.5 text-[13px] text-gray-400 dark:text-slate-500">
                          Ajouter une région de stock
                        </div>
                      </div>
                      <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-200" />
                    </button>
                  )}
                </div>
                {stockRegionsLoading && (
                  <p className="text-[13px] text-gray-400 dark:text-slate-500">{t("admin.loading")}</p>
                )}
              </div>
            ) : (
              /* Catalogue d'une région — design premium */
              <div className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] shadow-sm dark:shadow-none overflow-hidden">
                <div className="p-6 md:p-8 border-b border-gray-100 dark:border-white/5">
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => { setStockRegionFolderId(null); setStockRegionDetailPartnerFilter(""); }}
                      className="flex items-center gap-2 text-[15px] text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                    >
                      <ArrowLeft size={18} className="shrink-0" strokeWidth={2} />
                      Régions
                    </button>
                    <span className="text-gray-300 dark:text-slate-600">/</span>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
                      {stockRegions.find((r) => r.id === stockRegionFolderId)?.name ?? "Région"}
                    </span>
                    {onUpdateRegion && stockRegionFolderId && (
                      <button
                        type="button"
                        onClick={() => setEditingRegion(stockRegions.find((r) => r.id === stockRegionFolderId) ?? null)}
                        className="ml-1 p-2 rounded-xl text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors duration-200"
                        title={t("admin.editOrDeleteRegion")}
                      >
                        <Edit2 size={18} strokeWidth={1.75} />
                      </button>
                    )}
                  </div>
                  {stockRegionFolderId && (
                    <>
                      <div className="mt-4 flex flex-wrap items-center gap-3">
                        <span className="text-[14px] font-semibold text-gray-800 dark:text-slate-200">{t("admin.seeStockOf")}</span>
                        <select
                          value={stockRegionDetailPartnerFilter}
                          onChange={(e) => setStockRegionDetailPartnerFilter(e.target.value)}
                          className="px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800/80 text-[15px] font-medium text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all min-w-[220px]"
                        >
                          <option value="">{t("admin.choosePartnerOption")}</option>
                          <option value="__internal__">{t("admin.internalStock")}</option>
                          {(partners ?? []).map((p: User) => (
                            <option key={p.id} value={p.id}>{p.company ?? p.name}</option>
                          ))}
                        </select>
                        {stockRegionDetailPartnerFilter !== "" && (
                          <span className="text-[14px] text-gray-600 dark:text-slate-300">
                            dans {stockRegions.find((r) => r.id === stockRegionFolderId)?.name ?? "cette région"}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="p-6 md:p-8">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                      Catalogue
                    </h2>
                    <p className="mt-0.5 text-[14px] text-gray-500 dark:text-slate-400">
                      Consultez le stock du partenaire choisi ci-dessus, puis ajoutez des références en bas de page.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-[13px] font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                      {stockRegionDetailPartnerFilter === "" ? "Références par partenaire" : "Références"}
                    </h3>
                    {(() => {
                      const regionItems = getStockForRegion(stockRegionFolderId);
                      const byPartner = new Map<string | null, StockItem[]>();
                      regionItems.forEach((item: StockItem) => {
                        const pid = item.partnerId ?? null;
                        if (!byPartner.has(pid)) byPartner.set(pid, []);
                        byPartner.get(pid)!.push(item);
                      });
                      if (stockRegionDetailPartnerFilter === "") {
                        const order: (string | null)[] = [null, ...(partners ?? []).map((p: User) => p.id)];
                        const sections = order.map((pid) => {
                          const items = byPartner.get(pid) ?? [];
                          if (items.length === 0) return null;
                          const label = pid === null ? "Stock interne" : (partners ?? []).find((p: User) => p.id === pid)?.company ?? (partners ?? []).find((p: User) => p.id === pid)?.name ?? "Partenaire";
                          return (
                            <div key={pid ?? "__internal__"} className="mb-6">
                              <p className="text-[12px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                                {label}
                              </p>
                              <div className="space-y-2">
                                {items.map((item: StockItem) => {
                                  const threshold = item.lowStockThreshold ?? 10;
                                  const isLowStock = item.quantity <= threshold;
                                  return (
                                    <div
                                      key={item.id}
                                      className={`flex justify-between items-center px-5 py-4 rounded-2xl border transition-all duration-200 ${
                                        isLowStock
                                          ? "bg-red-50/80 dark:bg-red-950/30 border-red-200/60 dark:border-red-500/20"
                                          : "bg-gray-50/60 dark:bg-white/[0.03] border-gray-200/50 dark:border-white/[0.06] hover:bg-gray-100/80 dark:hover:bg-white/[0.05] hover:border-gray-300/60 dark:hover:border-white/10"
                                      }`}
                                    >
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          <span className="text-[16px] font-medium text-gray-900 dark:text-white truncate">
                                            {item.name}
                                          </span>
                                          {isLowStock && (
                                            <span className="shrink-0 text-[11px] font-medium text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/20 px-2 py-0.5 rounded-md">
                                              Stock bas
                                            </span>
                                          )}
                                        </div>
                                        <div className="mt-0.5 text-[13px] text-gray-500 dark:text-slate-400">
                                          Seuil : {threshold}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className={`min-w-[2.5rem] text-right text-[17px] font-semibold tabular-nums ${
                                          isLowStock ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-white"
                                        }`}>
                                          {item.quantity}
                                        </span>
                                        <button
                                          onClick={() => setEditingStockItem(item)}
                                          className="p-2.5 rounded-xl text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                                          title={t("admin.edit")}
                                        >
                                          <Edit2 size={18} strokeWidth={1.75} />
                                        </button>
                                        <button
                                          onClick={() => onDeleteStockItem(item.id)}
                                          className="p-2.5 rounded-xl text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                          title={t("admin.delete")}
                                        >
                                          <Trash2 size={18} strokeWidth={1.75} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }).filter(Boolean);
                        if (sections.length === 0) {
                          return (
                            <p className="text-[14px] text-gray-500 dark:text-slate-400 py-4">
                              Aucune référence dans cette région. Ajoutez-en via le formulaire en bas de page.
                            </p>
                          );
                        }
                        return sections;
                      }
                      const filterPid = stockRegionDetailPartnerFilter === "__internal__" ? null : stockRegionDetailPartnerFilter;
                      const items = byPartner.get(filterPid) ?? [];
                      if (items.length === 0) {
                        const regionName = stockRegions.find((r) => r.id === stockRegionFolderId)?.name ?? "cette région";
                        const partnerLabel = stockRegionDetailPartnerFilter === "__internal__" ? "Stock interne" : (partners ?? []).find((p: User) => p.id === stockRegionDetailPartnerFilter)?.company ?? (partners ?? []).find((p: User) => p.id === stockRegionDetailPartnerFilter)?.name ?? "Ce partenaire";
                        return (
                          <p className="text-[14px] text-gray-500 dark:text-slate-400 py-4">
                            Aucune référence pour {partnerLabel} dans {regionName}. Ajoutez-en via le formulaire en bas de page.
                          </p>
                        );
                      }
                      return (
                        <div className="space-y-2">
                          {items.map((item: StockItem) => {
                            const threshold = item.lowStockThreshold ?? 10;
                            const isLowStock = item.quantity <= threshold;
                            return (
                              <div
                                key={item.id}
                                className={`flex justify-between items-center px-5 py-4 rounded-2xl border transition-all duration-200 ${
                                  isLowStock
                                    ? "bg-red-50/80 dark:bg-red-950/30 border-red-200/60 dark:border-red-500/20"
                                    : "bg-gray-50/60 dark:bg-white/[0.03] border-gray-200/50 dark:border-white/[0.06] hover:bg-gray-100/80 dark:hover:bg-white/[0.05] hover:border-gray-300/60 dark:hover:border-white/10"
                                }`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[16px] font-medium text-gray-900 dark:text-white truncate">
                                      {item.name}
                                    </span>
                                    {isLowStock && (
                                      <span className="shrink-0 text-[11px] font-medium text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/20 px-2 py-0.5 rounded-md">
                                        Stock bas
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-0.5 text-[13px] text-gray-500 dark:text-slate-400">
                                    Seuil : {threshold}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`min-w-[2.5rem] text-right text-[17px] font-semibold tabular-nums ${
                                    isLowStock ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-white"
                                  }`}>
                                    {item.quantity}
                                  </span>
                                  <button
                                    onClick={() => setEditingStockItem(item)}
                                    className="p-2.5 rounded-xl text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                                    title={t("admin.edit")}
                                  >
                                    <Edit2 size={18} strokeWidth={1.75} />
                                  </button>
                                  <button
                                    onClick={() => onDeleteStockItem(item.id)}
                                    className="p-2.5 rounded-xl text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                    title={t("admin.delete")}
                                  >
                                    <Trash2 size={18} strokeWidth={1.75} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>

                  <div className="mt-10 pt-8 border-t border-gray-200/80 dark:border-white/10">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight mb-1">
                      Ajouter du stock
                    </h3>
                    <p className="text-[14px] text-gray-500 dark:text-slate-400 mb-5">
                      Nouvelle référence pour cette région. Choisissez le partenaire (interne ou partenaire).
                    </p>
                    <form
                      onSubmit={handleCreateStockItem}
                      className="flex flex-col gap-5 p-6 rounded-3xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-200/50 dark:border-white/[0.06]"
                    >
                      <div className="flex flex-col md:flex-row gap-5 md:items-end flex-wrap">
                        {stockRegionFolderId && (
                          <div className="w-full md:w-48">
                            <label className="block text-[13px] font-medium text-gray-600 dark:text-slate-400 mb-1.5 tracking-tight">
                              Partenaire
                            </label>
                            <select
                              value={selectedPartnerIdForStock}
                              onChange={(e) => setSelectedPartnerIdForStock(e.target.value)}
                              className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-2xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                            >
                              <option value="__internal__">{t("admin.internalStock")}</option>
                              {(partners ?? []).map((p: User) => (
                                <option key={p.id} value={p.id}>{p.company ?? p.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="w-full md:flex-1">
                          <label className="block text-[13px] font-medium text-gray-600 dark:text-slate-400 mb-1.5 tracking-tight">
                            Nom de la référence
                          </label>
                          <input
                            required
                            type="text"
                            placeholder="Ex: Spot 5W"
                            value={newStockItem.name}
                            onChange={(e) =>
                              setNewStockItem({ ...newStockItem, name: e.target.value })
                            }
                            className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-2xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 dark:focus:border-blue-500 outline-none transition-all"
                          />
                        </div>
                        <div className="w-full md:w-28">
                          <label className="block text-[13px] font-medium text-gray-600 dark:text-slate-400 mb-1.5 tracking-tight">
                            Quantité
                          </label>
                          <input
                            required
                            type="number"
                            min="0"
                            placeholder="0"
                            value={newStockItem.quantity}
                            onChange={(e) =>
                              setNewStockItem({
                                ...newStockItem,
                                quantity: e.target.value,
                              })
                            }
                            className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-2xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                          />
                        </div>
                        <div className="w-full md:w-28">
                          <label className="block text-[13px] font-medium text-gray-600 dark:text-slate-400 mb-1.5 tracking-tight">
                            Seuil alerte
                          </label>
                          <input
                            required
                            type="number"
                            min="0"
                            placeholder="10"
                            value={newStockItem.lowStockThreshold}
                            onChange={(e) =>
                              setNewStockItem({
                                ...newStockItem,
                                lowStockThreshold: e.target.value,
                              })
                            }
                            className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-2xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={stockAddLoading}
                          className="w-full md:w-auto px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[15px] font-medium rounded-2xl hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                        >
                          {stockAddLoading ? (
                            <>
                              <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
                              Ajout…
                            </>
                          ) : (
                            "Ajouter"
                          )}
                        </button>
                      </div>
                      {stockAddError && (
                        <p className="text-[13px] text-red-500 dark:text-red-400">
                          {stockAddError}
                        </p>
                      )}
                      <p className="text-[12px] text-gray-500 dark:text-slate-500">
                        Le seuil d’alerte déclenche une notification quand la quantité est atteinte.
                      </p>
                    </form>
                  </div>
                </div>
              </div>
            )}</>
            )}

            {stockViewMode === "by_partner" && (
            <>
            {stockPartnerFolderId === null ? (
              <div className="space-y-8">
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">
                    Stock par partenaire
                  </h1>
                  <p className="mt-1 text-[15px] text-gray-500 dark:text-slate-400">
                    Choisissez un partenaire pour voir son stock dans toutes les régions.
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {[
                    { id: "__internal__", label: "Stock interne" },
                    ...(partners ?? []).map((p: User) => ({ id: p.id, label: p.company ?? p.name ?? p.id })),
                  ].map(({ id, label }) => {
                    const count = getStockForPartner(id).length;
                    const lowCount = getLowStockCountForPartner(id);
                    return (
                      <div
                        key={id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setStockPartnerFolderId(id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setStockPartnerFolderId(id);
                          }
                        }}
                        className="group relative flex items-center gap-5 rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-6 shadow-sm dark:shadow-none hover:shadow-lg hover:shadow-gray-200/50 dark:hover:bg-slate-800 hover:border-gray-300/80 dark:hover:border-white/12 transition-all duration-300 ease-out text-left active:scale-[0.995] cursor-pointer"
                      >
                        {lowCount > 0 && (
                          <span className="absolute top-3 right-3 z-10" onClick={(e) => e.stopPropagation()}>
                            <PulseBadge count={lowCount} />
                          </span>
                        )}
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-orange-100/80 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 transition-colors duration-300">
                          <Building2 size={26} strokeWidth={1.5} className="opacity-90" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[17px] font-medium text-orange-700 dark:text-orange-300 truncate tracking-tight">
                              {label}
                            </span>
                          </div>
                          <div className="mt-0.5 text-[13px] text-gray-500 dark:text-slate-400">
                            {count} référence{count !== 1 ? "s" : ""}
                          </div>
                        </div>
                        <ChevronRight size={20} className="shrink-0 text-gray-300 dark:text-slate-600 group-hover:text-orange-500 dark:group-hover:text-orange-400 group-hover:translate-x-0.5 transition-all duration-200" />
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] shadow-sm dark:shadow-none overflow-hidden">
                <div className="p-6 md:p-8 border-b border-gray-100 dark:border-white/5">
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setStockPartnerFolderId(null)}
                      className="flex items-center gap-2 text-[15px] text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                    >
                      <ArrowLeft size={18} className="shrink-0" strokeWidth={2} />
                      Partenaires
                    </button>
                    <span className="text-gray-300 dark:text-slate-600">/</span>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
                      {stockPartnerFolderId === "__internal__"
                        ? "Stock interne"
                        : (partners ?? []).find((p: User) => p.id === stockPartnerFolderId)?.company ?? (partners ?? []).find((p: User) => p.id === stockPartnerFolderId)?.name ?? "Partenaire"}
                    </span>
                  </div>
                  <p className="mt-2 text-[14px] text-gray-500 dark:text-slate-400">
                    Stock de ce partenaire dans chaque région.
                  </p>
                </div>
                <div className="p-6 md:p-8 space-y-8">
                  {stockRegions.map((region) => {
                    const resolvedPartnerId = stockPartnerFolderId === "__internal__" || !stockPartnerFolderId ? null : stockPartnerFolderId;
                    const items = getStockForRegionAndPartner(region.id, resolvedPartnerId);
                    if (items.length === 0) return null;
                    return (
                      <div key={region.id} className="rounded-2xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-200/50 dark:border-white/[0.06] p-5">
                        <h3 className="text-[13px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <MapPin size={14} />
                          {region.name}
                        </h3>
                        <div className="space-y-2">
                          {items.map((item: StockItem) => {
                            const threshold = item.lowStockThreshold ?? 10;
                            const isLowStock = item.quantity <= threshold;
                            return (
                              <div
                                key={item.id}
                                className={`flex justify-between items-center px-4 py-3 rounded-xl border transition-all ${
                                  isLowStock
                                    ? "bg-red-50/80 dark:bg-red-950/30 border-red-200/60 dark:border-red-500/20"
                                    : "bg-white dark:bg-slate-800/50 border-gray-200/50 dark:border-white/[0.06]"
                                }`}
                              >
                                <span className="text-[15px] font-medium text-gray-900 dark:text-white truncate">{item.name}</span>
                                <div className="flex items-center gap-2">
                                  <span className={`tabular-nums font-semibold ${isLowStock ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-white"}`}>
                                    {item.quantity}
                                  </span>
                                  <span className="text-[12px] text-gray-500 dark:text-slate-400">/ {threshold}</span>
                                  <button
                                    onClick={() => setEditingStockItem(item)}
                                    className="p-2 rounded-lg text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-white/5"
                                    title={t("admin.edit")}
                                  >
                                    <Edit2 size={16} strokeWidth={1.75} />
                                  </button>
                                  <button
                                    onClick={() => onDeleteStockItem(item.id)}
                                    className="p-2 rounded-lg text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10"
                                    title={t("admin.delete")}
                                  >
                                    <Trash2 size={16} strokeWidth={1.75} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {stockRegions.every((r) => getStockForRegionAndPartner(r.id, stockPartnerFolderId === "__internal__" ? null : stockPartnerFolderId).length === 0) && (
                    <p className="text-[14px] text-gray-500 dark:text-slate-400">{t("admin.noReferenceForPartner")}</p>
                  )}
                </div>
              </div>
            )}
            </>
            )}
          </div>
        )}

        {activeTab === "team" && canSeeTeamTab(currentUser) && (
          <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800 dark:backdrop-blur-xl shadow-xl dark:shadow-2xl p-6 md:p-8">
            <TeamManager
              members={teamMembers}
              loading={teamLoading}
              error={teamError}
              canAddMore={isOwner ? teamCanAddMore : false}
              canManageTeam={isOwner}
              onInvite={onTeamInvite}
              onUpdatePermissions={onTeamUpdatePermissions}
              onRemoveMember={onTeamRemoveMember}
            />
          </div>
        )}

        {activeTab === "access" && canSeeAccessTab(currentUser) && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-slate-800 dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 p-6 rounded-2xl shadow-xl dark:shadow-2xl flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Comptes Partenaires
                  </h3>
                  <div className="p-2 bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400 rounded-xl">
                    <Briefcase size={20} />
                  </div>
                </div>
                <p className="text-gray-600 dark:text-slate-300 text-sm mb-6">
                  Créez et gérez les codes d'accès.
                </p>
              </div>
              <button
                onClick={() => onManageAccess("partner")}
                className="w-full py-3 border border-gray-200 dark:border-white/10 rounded-xl text-gray-700 dark:text-white font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-all flex items-center justify-center gap-2"
              >
                <Settings size={18} /> Gérer les Partenaires
              </button>
            </div>
            <div className="bg-white dark:bg-slate-800 dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 p-6 rounded-2xl shadow-xl dark:shadow-2xl flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Comptes Poseurs
                  </h3>
                  <div className="p-2 bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 rounded-xl">
                    <Wrench size={20} />
                  </div>
                </div>
                <p className="text-gray-600 dark:text-slate-300 text-sm mb-6">
                  Gérez les codes d'accès des techniciens.
                </p>
              </div>
              <button
                onClick={() => onManageAccess("installer")}
                className="w-full py-3 border border-gray-200 dark:border-white/10 rounded-xl text-gray-700 dark:text-white font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-all flex items-center justify-center gap-2"
              >
                <Settings size={18} /> Gérer les Poseurs
              </button>
            </div>
            <div className="bg-white dark:bg-slate-800 dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 p-6 rounded-2xl shadow-xl dark:shadow-2xl flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Comptes Commerciaux
                  </h3>
                  <div className="p-2 bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-xl">
                    <UserCircle size={20} />
                  </div>
                </div>
                <p className="text-gray-600 dark:text-slate-300 text-sm mb-6">
                  Planning et documents sur le terrain.
                </p>
              </div>
              <button
                onClick={() => onManageAccess("commercial")}
                className="w-full py-3 border border-gray-200 dark:border-white/10 rounded-xl text-gray-700 dark:text-white font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-all flex items-center justify-center gap-2"
              >
                <Settings size={18} /> Gérer les Commerciaux
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Alerte stock bas */}
      {!lowStockAlertDismissed && lowStockItems.length > 0 && (
        <LowStockAlert
          lowStockItems={lowStockItems}
          onClose={() => setLowStockAlertDismissed(true)}
          onNavigateToStock={() => {
            setActiveTab("stock");
            setLowStockAlertDismissed(true);
          }}
        />
      )}

      <Modal
        isOpen={!!editingStockItem}
        onClose={() => setEditingStockItem(null)}
        size="lg"
      >
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight mb-1">{t("admin.editReference")}</h3>
        <p className="text-[15px] text-gray-500 dark:text-slate-400 mb-6">{t("admin.nameQtyAlert")}</p>
        {editingStockItem && (
          <form onSubmit={handleSaveItemEdit} className="space-y-5">
            <div>
              <label className="block text-[13px] font-medium text-gray-600 dark:text-slate-400 mb-2 tracking-tight">
                Nom
              </label>
              <input
                type="text"
                value={editingStockItem.name}
                onChange={(e) =>
                  setEditingStockItem({
                    ...editingStockItem,
                    name: e.target.value,
                  })
                }
                className="w-full px-4 py-3.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-gray-600 dark:text-slate-400 mb-2 tracking-tight">
                Quantité
              </label>
              <input
                type="number"
                value={editingStockItem.quantity}
                onChange={(e) =>
                  setEditingStockItem({
                    ...editingStockItem,
                    quantity: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-4 py-3.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-gray-600 dark:text-slate-400 mb-2 tracking-tight">
                Seuil d'alerte stock bas
              </label>
              <input
                type="number"
                min="0"
                value={editingStockItem.lowStockThreshold ?? 10}
                onChange={(e) =>
                  setEditingStockItem({
                    ...editingStockItem,
                    lowStockThreshold: parseInt(e.target.value) || 10,
                  })
                }
                className="w-full px-4 py-3.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
              />
              <p className="text-[12px] text-gray-500 dark:text-slate-500 mt-1.5">
                Vous serez alerté quand la quantité atteint ou descend sous ce seuil.
              </p>
            </div>
            <button
              type="submit"
              className="w-full py-3.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[15px] font-medium rounded-2xl hover:opacity-90 transition-opacity"
            >
              Enregistrer
            </button>
          </form>
        )}
      </Modal>

      {/* Modal Ajouter une unité de stock (depuis Ajuster le stock, pour la région sélectionnée) */}
      <Modal
        isOpen={isAddStockUnitOverviewOpen}
        onClose={() => {
          setIsAddStockUnitOverviewOpen(false);
          setNewStockUnitOverview({ name: "", quantity: "", lowStockThreshold: "10" });
          setAddStockUnitOverviewError(null);
        }}
        size="lg"
      >
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight mb-1">
          Nouvelle unité de stock
        </h3>
        <p className="text-[15px] text-gray-500 dark:text-slate-400 mb-6">
          Région : <strong className="text-gray-700 dark:text-slate-300">{stockRegions.find((r) => r.id === selectedRegionId)?.name ?? "—"}</strong>
        </p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setAddStockUnitOverviewError(null);
            const name = newStockUnitOverview.name.trim();
            if (!name) return;
            const qty = parseInt(newStockUnitOverview.quantity, 10);
            const threshold = parseInt(newStockUnitOverview.lowStockThreshold, 10);
            if (!selectedRegionId) return;
            setAddStockUnitOverviewLoading(true);
            const { error } = await onAddNewStockItem(
              name,
              isNaN(qty) ? 0 : qty,
              isNaN(threshold) ? 10 : threshold,
              selectedRegionId,
              resolvedPartnerIdForStock ?? undefined,
            );
            setAddStockUnitOverviewLoading(false);
            if (error) {
              setAddStockUnitOverviewError(error);
              return;
            }
            setIsAddStockUnitOverviewOpen(false);
            setNewStockUnitOverview({ name: "", quantity: "", lowStockThreshold: "10" });
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Nom de l’unité (dales, LED, etc.)
            </label>
            <input
              type="text"
              required
              placeholder="Ex: Dales, LED 5W, Ruban..."
              value={newStockUnitOverview.name}
              onChange={(e) =>
                setNewStockUnitOverview((prev) => ({ ...prev, name: e.target.value }))
              }
              className="w-full px-4 py-3.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-gray-600 dark:text-slate-400 mb-2 tracking-tight">
              Quantité initiale
            </label>
            <input
              type="number"
              min="0"
              value={newStockUnitOverview.quantity}
              onChange={(e) =>
                setNewStockUnitOverview((prev) => ({ ...prev, quantity: e.target.value }))
              }
              className="w-full px-4 py-3.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-gray-600 dark:text-slate-400 mb-2 tracking-tight">
              Seuil d’alerte stock bas
            </label>
            <input
              type="number"
              min="0"
              value={newStockUnitOverview.lowStockThreshold}
              onChange={(e) =>
                setNewStockUnitOverview((prev) => ({ ...prev, lowStockThreshold: e.target.value }))
              }
              className="w-full px-4 py-3.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
            />
          </div>
          {addStockUnitOverviewError && (
            <p className="text-[13px] text-red-500 dark:text-red-400 bg-red-50/80 dark:bg-red-500/10 px-4 py-3 rounded-2xl">
              {addStockUnitOverviewError}
            </p>
          )}
          <button
            type="submit"
            disabled={addStockUnitOverviewLoading}
            className="w-full py-3.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[15px] font-medium rounded-2xl hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
          >
            {addStockUnitOverviewLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Ajout…
              </>
            ) : (
              "Créer l’unité"
            )}
          </button>
        </form>
      </Modal>

      {/* Modal Budget : modifications non enregistrées (quitter l'onglet) */}
      <Modal
        isOpen={budgetPendingTab !== null}
        onClose={() => setBudgetPendingTab(null)}
      >
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight mb-1">
          Modifications non enregistrées
        </h3>
        <p className="text-[15px] text-gray-500 dark:text-slate-400 mb-6">
          Enregistrez ou annulez vos modifications avant de quitter l’onglet Budget.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => {
              const regionStock = budgetRegionId
                ? sortBudgetStockItems(
                    getStockForRegionAndPartner(budgetRegionId, resolvedBudgetPartnerId)
                  )
                : [];
              const selectedRef = regionStock.find((s) => s.id === budgetRefId) ?? regionStock[0];
              if (budgetRegionId && selectedRef) {
                const currentCents = budgetPrices.getPrice(budgetRegionId, selectedRef.id, budgetTariffType);
                const cents = budgetEditedCents !== null ? budgetEditedCents : currentCents;
                budgetPrices.setPrice(budgetRegionId, selectedRef.id, budgetTariffType, cents);
              }
              setBudgetEditedCents(null);
              setBudgetPriceDirty(false);
              if (budgetPendingTab !== null) {
                setActiveTab(budgetPendingTab);
                setBudgetPendingTab(null);
              }
            }}
            className="px-5 py-3 rounded-2xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[15px] font-medium hover:opacity-90 transition-opacity"
          >
            Enregistrer
          </button>
          <button
            type="button"
            onClick={() => {
              setBudgetEditedCents(null);
              setBudgetPriceDirty(false);
              if (budgetPendingTab !== null) {
                setActiveTab(budgetPendingTab);
                setBudgetPendingTab(null);
              }
            }}
            className="px-5 py-3 rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-700/80 text-gray-700 dark:text-slate-200 text-[15px] font-medium hover:bg-gray-50 dark:hover:bg-slate-600/80 transition-colors"
          >
            Annuler et quitter
          </button>
          <button
            type="button"
            onClick={() => setBudgetPendingTab(null)}
            className="px-5 py-3 rounded-2xl text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700/80 text-[15px] font-medium transition-colors"
          >
            Rester
          </button>
        </div>
      </Modal>

      {/* Modal Ajouter une région — premium */}
      <Modal
        isOpen={isAddRegionModalOpen}
        onClose={() => {
          setIsAddRegionModalOpen(false);
          setNewRegionName("");
          setAddRegionError(null);
        }}
        size="lg"
      >
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight mb-1">
          Nouvelle région
        </h3>
        <p className="text-[15px] text-gray-500 dark:text-slate-400 mb-6">
          Ajoutez une région de stock (ex. ville ou entrepôt).
        </p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!newRegionName.trim() || !onAddRegion) return;
            setAddRegionError(null);
            setAddRegionLoading(true);
            const { data, error } = await onAddRegion(newRegionName.trim());
            setAddRegionLoading(false);
            if (error) {
              setAddRegionError(error);
              return;
            }
            if (data) {
              setSelectedRegionId(data.id);
              setStockRegionFolderId(data.id);
              setIsAddRegionModalOpen(false);
              setNewRegionName("");
              refetchStockRegions?.();
            }
          }}
          className="space-y-5"
        >
          <div>
            <label className="block text-[13px] font-medium text-gray-600 dark:text-slate-400 mb-2 tracking-tight">
              Nom de la région
            </label>
            <input
              type="text"
              value={newRegionName}
              onChange={(e) => {
                setNewRegionName(e.target.value);
                setAddRegionError(null);
              }}
              placeholder="Paris, Lyon, Entrepôt Nord…"
              className="w-full px-4 py-3.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 outline-none transition-all"
            />
          </div>
          {addRegionError && (
            <p className="text-[13px] text-red-500 dark:text-red-400 bg-red-50/80 dark:bg-red-500/10 px-4 py-3 rounded-2xl">
              {addRegionError}
            </p>
          )}
          <button
            type="submit"
            disabled={!newRegionName.trim() || addRegionLoading}
            className="w-full py-3.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[15px] font-medium rounded-2xl hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
          >
            {addRegionLoading ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Création…
              </>
            ) : (
              "Créer la région"
            )}
          </button>
        </form>
      </Modal>

      {/* Modal Modifier une région — premium */}
      <Modal
        isOpen={!!editingRegion}
        onClose={() => setEditingRegion(null)}
        size="lg"
      >
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight mb-1">
          {t("admin.editRegion")}
        </h3>
        <p className="text-[15px] text-gray-500 dark:text-slate-400 mb-6">
          Renommez ou supprimez cette région.
        </p>
        {editingRegion && (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!editingRegion.name.trim() || !onUpdateRegion) return;
              await onUpdateRegion(editingRegion.id, editingRegion.name.trim());
              setEditingRegion(null);
            }}
            className="space-y-5"
          >
            <div>
              <label className="block text-[13px] font-medium text-gray-600 dark:text-slate-400 mb-2 tracking-tight">
                Nom
              </label>
              <input
                type="text"
                value={editingRegion.name}
                onChange={(e) => setEditingRegion({ ...editingRegion, name: e.target.value })}
                className="w-full px-4 py-3.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={async () => {
                  if (onRemoveRegion && window.confirm(t("admin.deleteRegionShortConfirm"))) {
                    await onRemoveRegion(editingRegion.id);
                    setEditingRegion(null);
                    setSelectedRegionId((prev) => (prev === editingRegion.id ? null : prev));
                    setStockRegionFolderId((prev) => (prev === editingRegion.id ? null : prev));
                    setSelectedRegionsTabRegionId((prev) => (prev === editingRegion.id ? null : prev));
                  }
                }}
                className="px-4 py-3 text-[15px] font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-2xl transition-colors"
              >
{t("admin.delete")}
                </button>
              <button
                type="submit"
                className="flex-1 py-3.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[15px] font-medium rounded-2xl hover:opacity-90 transition-opacity"
              >
                Enregistrer
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={!!editingJob} onClose={closeEditJobModal} size="xl">
        <div className="rounded-2xl md:rounded-3xl bg-slate-50/80 dark:bg-slate-900/40 p-4 md:p-6 mb-4 md:mb-5">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 tracking-tight">
            {editingJobReadOnly ? t("admin.viewJob") : t("admin.editJob")}
          </h3>
          <p className="text-[13px] text-gray-500 dark:text-slate-400 tracking-tight">
            {editingJobReadOnly ? t("admin.viewOnlyNoEdit") : t("admin.detailsAndPlanning")}
          </p>
        </div>
        {editingJob && (() => {
          return (
          <form onSubmit={handleSaveEdit} className="space-y-4 md:space-y-5 rounded-2xl md:rounded-3xl bg-slate-50/80 dark:bg-slate-900/40 p-4 md:p-6">
            <fieldset disabled={editingJobReadOnly} className="min-w-0 border-0 p-0 m-0 space-y-4 md:space-y-5 disabled:opacity-95">
            <section className="rounded-2xl bg-white dark:bg-slate-900/70 shadow-sm hover:shadow-md transition-shadow border border-slate-200 dark:border-white/10 overflow-hidden">
              <div className="px-4 md:px-6 py-3 md:py-4 border-b border-slate-100 dark:border-white/10">
                <span className="font-semibold text-gray-900 dark:text-white">Contexte</span>
              </div>
              <div className="p-4 md:p-6 pt-4 md:pt-5 space-y-3 md:space-y-4">
            <div className="space-y-3 pt-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 dark:text-slate-500 mb-1.5">{t("admin.jobTypeLabel")}</p>
              <select
                value={editingJob.dossierType ?? ""}
                onChange={(e) => setEditingJob({ ...editingJob, dossierType: e.target.value as Job["dossierType"] })}
                className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 outline-none transition-all"
              >
                {DOSSIER_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
              </div>
            </section>
            <section className="rounded-2xl bg-white dark:bg-slate-900/70 shadow-sm hover:shadow-md transition-shadow border border-slate-200 dark:border-white/10 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-white/10">
                <span className="font-semibold text-gray-900 dark:text-white">Planification</span>
              </div>
              <div className="p-4 pt-4 space-y-3">
            {stockRegions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                  {t("admin.region")}
                </label>
                <select
                  value={editingJob.stockRegionId ?? ""}
                  onChange={(e) => {
                    const newRegionId = e.target.value;
                    const currentInstaller = editingJob.installerId ? installers.find((i: User) => i.id === editingJob.installerId) : null;
                    const installerInNewRegion = !currentInstaller || currentInstaller?.preferences?.defaultStockRegionId === newRegionId;
                    const nextInstallerId = installerInNewRegion ? editingJob.installerId : "";
                    const hasDate = !!editingJob.date?.trim();
                    const keepStatus = editingJob.status === "completed" || editingJob.status === "cancelled" || editingJob.status === "in_progress";
                    setEditingJob({
                      ...editingJob,
                      stockRegionId: newRegionId,
                      installerId: nextInstallerId || undefined,
                      status: keepStatus
                        ? editingJob.status
                        : resolveJobStatusFromScheduling(editingJob.status, editingJob.date),
                    });
                  }}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 outline-none transition-all"
                >
                  <option value="">Sélectionner une région</option>
                  {stockRegions.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                {t("admin.assignInstaller")}
              </label>
              <select
                value={editingJob.installerId ?? ""}
                onChange={(e) => {
                  const newInstallerId = e.target.value;
                  const hasDate = !!(editingJob.date?.trim());
                  const keepStatus = editingJob.status === "completed" || editingJob.status === "cancelled" || editingJob.status === "in_progress";
                  setEditingJob({
                    ...editingJob,
                    installerId: newInstallerId?.trim() || undefined,
                    status: keepStatus
                      ? editingJob.status
                      : resolveJobStatusFromScheduling(editingJob.status, editingJob.date),
                  });
                }}
                className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 outline-none transition-all"
              >
                <option value="">{t("admin.assignToNobody")}</option>
                {(editingJob.stockRegionId
                  ? installers.filter((i: User) => i.preferences?.defaultStockRegionId === editingJob.stockRegionId)
                  : installers.filter((i: User) => !!i.preferences?.defaultStockRegionId)
                ).map((i: User) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
              {editingJob.stockRegionId && installers.filter((i: User) => i.preferences?.defaultStockRegionId === editingJob.stockRegionId).length === 0 && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Aucun poseur dans cette région. Assignez d’abord un poseur à cette région dans Gestion Accès.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                {t("admin.assignCommercial")}
              </label>
              <select
                value={editingJob.commercialId ?? ""}
                onChange={(e) => {
                  const newCommercialId = e.target.value;
                  setEditingJob({
                    ...editingJob,
                    commercialId: newCommercialId?.trim() || undefined,
                  });
                }}
                className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 outline-none transition-all"
              >
                <option value="">{t("admin.assignToNobody")}</option>
                {commercials.map((c: User) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {commercials.length === 0 && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  Aucun commercial. Créez un compte dans Gestion Accès.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[13px] font-medium text-gray-500 dark:text-slate-400 tracking-tight mb-1">
                  Date
                </label>
                <input
                  type="date"
                  lang={getCurrentLocale() === "fr" ? "fr-FR" : getCurrentLocale() === "es" ? "es-ES" : "en-US"}
                  value={editingJob.date || ""}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    const hasDate = !!newDate.trim();
                    const keepStatus = editingJob.status === "completed" || editingJob.status === "cancelled" || editingJob.status === "in_progress";
                    setEditingJob({
                      ...editingJob,
                      date: newDate,
                      status: keepStatus
                        ? editingJob.status
                        : resolveJobStatusFromScheduling(editingJob.status, newDate),
                    });
                  }}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/40 outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-gray-500 dark:text-slate-400 tracking-tight mb-1">
                  Heure
                </label>
                <input
                  type="time"
                  value={editingJob.time || ""}
                  onChange={(e) =>
                    setEditingJob({ ...editingJob, time: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/40 outline-none transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-gray-500 dark:text-slate-400 tracking-tight mb-1.5">
                Statut
              </label>
              <select
                value={editingJob.status}
                onChange={(e) => {
                  const nextStatus = e.target.value as Job["status"];
                  setEditingJob({
                    ...editingJob,
                    status: nextStatus,
                    date: nextStatus === "pending" ? undefined : editingJob.date,
                    time: nextStatus === "pending" ? undefined : editingJob.time,
                  });
                }}
                className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 outline-none transition-all"
              >
                {([
                  { value: "pending", label: t("admin.statusPending") },
                  { value: "scheduled", label: t("admin.statusScheduled") },
                  { value: "in_progress", label: t("admin.statusInProgress") },
                  { value: "cancelled", label: t("admin.statusCancelledClient") },
                ] as Array<{ value: Job["status"]; label: string }>).map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              {editingJob.status === "cancelled" && (
                <p className="mt-1 text-[12px] text-red-600 dark:text-red-400">{t("admin.jobCancelledByClient")}</p>
              )}
            </div>
              </div>
            </section>
            <section className="rounded-2xl bg-white dark:bg-slate-900/70 shadow-sm hover:shadow-md transition-shadow border border-slate-200 dark:border-white/10 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-white/10">
                <span className="font-semibold text-gray-900 dark:text-white">Technique</span>
              </div>
              <div className="p-4 pt-4 space-y-3">
            {!usesTechnicalQuestions(editingJob.dossierType) ? null : (
              <div>
                <label className="block text-[13px] font-medium text-gray-500 dark:text-slate-400 tracking-tight mb-1">
                  Surface (m²)
                </label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={editingJob.surface ?? ""}
                  onChange={(e) => setEditingJob({ ...editingJob, surface: e.target.value })}
                  placeholder="Ex. 120"
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 focus:ring-2 focus:ring-blue-500/40 outline-none transition-all"
                />
              </div>
            )}
            <div>
              <label className="block text-[13px] font-medium text-gray-500 dark:text-slate-400 tracking-tight mb-1">
                Type
              </label>
              {showLedTechnicalFields(editingJob.dossierType) ? (
                <>
                  <select
                    value={editingJob.lightingType ?? ""}
                    onChange={(e) => setEditingJob({ ...editingJob, lightingType: e.target.value })}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 outline-none transition-all"
                  >
                    <option value="">Sélectionner un type</option>
                    {[
                      { value: "visite technique", label: t("admin.technicalVisit") },
                      { value: "pose", label: t("admin.installation") },
                    ].map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <div className="mt-3 flex gap-2 items-center">
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="Ex: 24"
                      value={editingJob.estimatedLedCount ?? ""}
                      onChange={(e) => setEditingJob({ ...editingJob, estimatedLedCount: e.target.value })}
                      className="flex-1 min-w-0 px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 focus:ring-2 focus:ring-blue-500/40 outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => handleAiSuggestJob("led_estimate", "edit")}
                      disabled={!!aiSuggestLoading}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-blue-500/15 dark:bg-blue-400/15 text-blue-600 dark:text-blue-400 hover:bg-blue-500/25 dark:hover:bg-blue-400/25 transition-colors disabled:opacity-50 text-sm font-medium"
                    >
                      {aiSuggestLoading === "edit_led" ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                      Estimer
                    </button>
                  </div>
                </>
              ) : null}
              {showDehumidifierTechnicalFields(editingJob.dossierType) && (
                <div className="space-y-3">
                  <input
                    type="number"
                    min={0}
                    value={editingJob.dehumidifierCount ?? ""}
                    onChange={(e) => setEditingJob({ ...editingJob, dehumidifierCount: Number(e.target.value) })}
                    placeholder="Nombre de déshumidificateurs"
                    className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 focus:ring-2 focus:ring-blue-500/40 outline-none transition-all"
                  />
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
                      <input
                        type="checkbox"
                        checked={!!editingJob.needsCounter}
                        onChange={(e) => setEditingJob({ ...editingJob, needsCounter: e.target.checked })}
                      />
                      Compteur
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
                      <input
                        type="checkbox"
                        checked={!!editingJob.needsGenerator}
                        onChange={(e) => setEditingJob({ ...editingJob, needsGenerator: e.target.checked })}
                      />
                      Générateur
                    </label>
                  </div>
                </div>
              )}
              {showTimerTechnicalFields(editingJob.dossierType) && (
                <input
                  type="number"
                  min={0}
                  value={editingJob.timerCount ?? ""}
                  onChange={(e) => setEditingJob({ ...editingJob, timerCount: Number(e.target.value) })}
                  placeholder="Nombre de minuteries"
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 focus:ring-2 focus:ring-blue-500/40 outline-none transition-all"
                />
              )}
            </div>
            <div>
              <label className="block text-[13px] font-medium text-gray-500 dark:text-slate-400 tracking-tight mb-1">
                Notes
              </label>
              <textarea
                value={editingJob.notes || ""}
                onChange={(e) =>
                  setEditingJob({ ...editingJob, notes: e.target.value })
                }
                placeholder="Instructions ou remarques..."
                className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl h-24 text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 focus:ring-2 focus:ring-blue-500/40 outline-none transition-all resize-none"
              />
              <div className="mt-1.5 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleAiSuggestJob("notes", "edit")}
                  disabled={!!aiSuggestLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-blue-500/15 dark:bg-blue-400/15 text-blue-600 dark:text-blue-400 hover:bg-blue-500/25 dark:hover:bg-blue-400/25 transition-colors disabled:opacity-50 text-sm font-medium"
                >
                  {aiSuggestLoading === "edit_notes" ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Sparkles size={16} />
                  )}
                  Suggérer avec l&apos;IA
                </button>
                {aiSuggestError && (
                  <span className="text-[13px] text-red-500 dark:text-red-400">{aiSuggestError}</span>
                )}
              </div>
            </div>

              </div>
            </section>

            <section className="rounded-2xl bg-white dark:bg-slate-900/70 shadow-sm hover:shadow-md transition-shadow border border-slate-200 dark:border-white/10 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-white/10">
                <span className="font-semibold text-gray-900 dark:text-white">Client</span>
              </div>
              <div className="p-4 pt-4 space-y-3">
            <div>
              <label className="block text-[13px] text-gray-500 dark:text-slate-400 tracking-tight mb-1.5">{t("admin.nameOrFirstName")}</label>
              <input
                type="text"
                value={editingJob.clientName}
                onChange={(e) =>
                  setEditingJob({ ...editingJob, clientName: e.target.value })
                }
                placeholder={t("admin.clientNamePlaceholder")}
                className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 tracking-tight focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-[13px] text-gray-500 dark:text-slate-400 tracking-tight mb-1.5">{t("admin.address")}</label>
              <input
                type="text"
                value={editingJob.clientAddress}
                onChange={(e) =>
                  setEditingJob({ ...editingJob, clientAddress: e.target.value })
                }
                placeholder={t("admin.fullAddressPlaceholder")}
                className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 tracking-tight focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-[13px] text-gray-500 dark:text-slate-400 tracking-tight mb-1.5">{t("admin.phone")}</label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" size={16} strokeWidth={1.8} />
                <input
                  type="tel"
                  value={editingJob.clientPhone ?? ""}
                  onChange={(e) =>
                    setEditingJob({ ...editingJob, clientPhone: e.target.value })
                  }
                  placeholder={t("admin.clientPhonePlaceholder")}
                  className="w-full pl-10 pr-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 tracking-tight focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 outline-none transition-all"
                />
              </div>
            </div>
              </div>
            </section>

            <DossierNotes dossierId={editingJob.id} currentUser={currentUser} />

            <SignedQuoteUpload
              jobId={editingJob.id}
              existingPaths={editingJob.signedQuoteUrls}
              canDelete={!editingJobReadOnly}
              disabled={editingJobReadOnly}
              onChange={async (nextPaths) => {
                const updated = {
                  ...editingJob,
                  signedQuoteUrls: nextPaths.length ? nextPaths : undefined,
                };
                const result = await onUpdateJob(updated);
                if (result && "error" in result && result.error) {
                  throw new Error(result.error);
                }
                setEditingJob(updated);
              }}
            />

            {editingJob.status === "completed" && editingJob.stockUsed && (
              <div className="pt-4 border-t border-gray-200 dark:border-white/10">
                <h4 className="font-bold text-green-700 dark:text-green-400 mb-3 flex items-center gap-2">
                  <CheckCircle size={16} /> Rapport d'installation
                </h4>
                <div className="bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 p-4 rounded-xl text-sm text-gray-700 dark:text-slate-300 space-y-1">
                  {Object.entries(editingJob.stockUsed).map(([itemId, qty]) => {
                    // Clé possible: "uuid-type" (ex. 11747549-3721-4ce1-9521-fa581fb5bc17-leds) → n'afficher que le type
                    const materialId = itemId.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i, "") || itemId;
                    const itemName =
                      stock.find((s: StockItem) => s.id === materialId)?.name ||
                      materialId;
                    if (qty > 0)
                      return (
                        <div key={itemId} className="flex justify-between">
                          <span>{itemName}:</span> <strong className="text-white">{qty}</strong>
                        </div>
                      );
                    return null;
                  })}
                  {editingJob.installerNotes && (
                    <div className="mt-2 pt-2 border-t border-white/10 italic text-slate-300">
                      &quot;{editingJob.installerNotes}&quot;
                    </div>
                  )}
                  {editingJob.partnerInvoiceTotalCents != null && editingJob.partnerInvoiceTotalCents > 0 && (
                    <div className="mt-3 pt-3 border-t border-green-200/50 dark:border-green-500/20 font-semibold text-green-800 dark:text-green-300">
                      {t("admin.partnerInvoiceLabel")} {(editingJob.partnerInvoiceTotalCents / 100).toFixed(2)} €
                    </div>
                  )}
                </div>
                {onValidateJob && onModifyJob && !editingJobReadOnly && (
                  <button
                    type="button"
                    onClick={() => { setValidatingJob(editingJob); setEditingJob(null); setEditingJobReadOnly(false); }}
                    className="mt-3 w-full py-3 bg-amber-500/20 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 rounded-xl font-semibold text-[14px] border border-amber-300/50 dark:border-amber-500/30 hover:bg-amber-500/30 dark:hover:bg-amber-500/25 transition-colors flex items-center justify-center gap-2"
                  >
                    <Edit2 size={16} /> {t("admin.editReport")}
                  </button>
                )}
              </div>
            )}
            </fieldset>

            <div className="pt-4 border-t border-gray-200 dark:border-white/10 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setPdfJob(editingJob);
                  setPdfType("devis");
                  setPdfAmount(editingJob.partnerInvoiceTotalCents != null ? String(editingJob.partnerInvoiceTotalCents / 100) : "");
                  setPdfModalOpen(true);
                }}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-white/5 text-sm font-medium flex items-center justify-center gap-2"
              >
                <FileText size={16} /> Générer devis
              </button>
              <button
                type="button"
                onClick={() => {
                  setPdfJob(editingJob);
                  setPdfType("facture");
                  setPdfAmount(editingJob.partnerInvoiceTotalCents != null ? String(editingJob.partnerInvoiceTotalCents / 100) : "");
                  setPdfModalOpen(true);
                }}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-white/5 text-sm font-medium flex items-center justify-center gap-2"
              >
                <FileText size={16} /> Générer facture
              </button>
            </div>

            {editingJobReadOnly ? (
              <button
                type="button"
                onClick={closeEditJobModal}
                className="w-full py-3.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-semibold text-[15px] tracking-tight hover:opacity-90 active:scale-[0.99] transition-all"
              >
                Fermer
              </button>
            ) : (
              <>
            <button
              type="submit"
              disabled={!isEditingJobDirty}
              className="w-full py-3.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-semibold text-[15px] tracking-tight hover:opacity-90 active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("admin.save")}
            </button>

            {(editingJob.status === "scheduled" || editingJob.status === "in_progress") && onValidateJob && (
              <div className="pt-4 border-t border-gray-200 dark:border-white/10">
                <button
                  type="button"
                  onClick={() => { setValidatingJob(editingJob); setEditingJob(null); setEditingJobReadOnly(false); }}
                  className="w-full py-3.5 bg-green-600 dark:bg-green-500 text-white rounded-xl font-semibold text-[15px] tracking-tight hover:bg-green-700 dark:hover:bg-green-600 active:scale-[0.99] transition-all flex items-center justify-center gap-2"
                >
                  <CheckCircle size={18} /> Clôturer le chantier
                </button>
              </div>
            )}
              </>
            )}
          </form>
          );
        })()}
      </Modal>

      {/* Modal Générer PDF devis/facture */}
      <Modal
        isOpen={pdfModalOpen && !!pdfJob}
        onClose={() => { setPdfModalOpen(false); setPdfJob(null); setPdfAmount(""); }}
        size="lg"
      >
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
          Générer un {pdfType === "devis" ? "devis" : "facture"}
        </h3>
        <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
          Client : {pdfJob?.clientName}
        </p>
        {pdfType === "facture" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              {t("settings.matchWithQuote")}
            </label>
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
              {t("settings.matchWithQuoteHint")}
            </p>
            <select
              value=""
              onChange={(e) => {
                const id = e.target.value;
                if (!id) return;
                const job = jobs.find((j) => j.id === id);
                if (job) {
                  setPdfJob(job);
                  setPdfAmount(job.partnerInvoiceTotalCents != null ? String(job.partnerInvoiceTotalCents / 100) : "");
                }
              }}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white text-sm"
            >
              <option value="">— {t("settings.matchWithQuote")} —</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.clientName} {job.partnerInvoiceTotalCents != null ? `(${(job.partnerInvoiceTotalCents / 100).toFixed(2)} €)` : ""}
                </option>
              ))}
            </select>
          </div>
        )}
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!pdfJob) return;
            const amount = parseFloat(pdfAmount.replace(",", ".")) || 0;
            setPdfGenerating(true);
            try {
              const prefs = ownerProfile?.preferences;
              const tpl = prefs?.invoiceTemplate;
              const resolvedTemplate = tpl
                ? {
                    ...tpl,
                    logoUrl:
                      tpl.useProfilePhotoAsLogo && ownerProfile?.avatar_url
                        ? ownerProfile.avatar_url
                        : tpl.logoUrl,
                  }
                : undefined;
              const doc = (
                <DevisFactureDoc
                  type={pdfType}
                  job={pdfJob}
                  companyName={prefs?.companyName}
                  companySiret={prefs?.companySiret}
                  companyAddress={prefs?.companyAddress}
                  companyPhone={prefs?.companyPhone}
                  amountEur={amount}
                  date={new Date().toLocaleDateString("fr-FR")}
                  template={resolvedTemplate}
                />
              );
              const blob = await pdf(doc).toBlob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `${pdfType}-${pdfJob.clientName?.replace(/\s+/g, "-") ?? "client"}-${new Date().toISOString().slice(0, 10)}.pdf`;
              a.click();
              URL.revokeObjectURL(url);
              setPdfModalOpen(false);
              setPdfJob(null);
              setPdfAmount("");
            } catch (err) {
              console.error(err);
            } finally {
              setPdfGenerating(false);
            }
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Montant (€)
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={pdfAmount}
              onChange={(e) => setPdfAmount(e.target.value)}
              placeholder="0.00"
              className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setPdfModalOpen(false); setPdfJob(null); setPdfAmount(""); }}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={pdfGenerating}
              className="flex-1 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {pdfGenerating ? <Loader2 size={18} className="animate-spin" /> : "Télécharger le PDF"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!validatingJob} onClose={() => setValidatingJob(null)} size="lg">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            {validatingIsEditing ? t("admin.editReport") : t("admin.closeJob")}
          </h3>
          <p className="text-gray-500 dark:text-slate-400">{t("admin.quantitiesPlaced")}</p>
        </div>
        <form onSubmit={handleValidateSubmit} className="space-y-6">
          <div className="bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 p-4 rounded-2xl space-y-4 max-h-80 overflow-y-auto">
            {validatingJob && stockForValidatingJob.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Aucun article de stock pour ce partenaire dans cette région. Ajoutez du stock dans la région du chantier pour pouvoir clôturer.
              </p>
            ) : (
              stockForValidatingJob.map((item: StockItem) => {
                const qtyNeuf = validatingUsageNeuf[item.id] ?? 0;
                const qtyRem = validatingUsageRemplacement[item.id] ?? 0;
                const total = qtyNeuf + qtyRem;
                return (
                  <div key={item.id} className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-slate-300 shrink-0">
                        {item.name}
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col items-center">
                          <span className="text-xs text-gray-500 dark:text-slate-400">Neuf</span>
                          <input
                            type="number"
                            min={0}
                            value={qtyNeuf}
                            onChange={(e) =>
                              setValidatingUsageNeuf((u) => ({
                                ...u,
                                [item.id]: Math.max(0, parseInt(e.target.value, 10) || 0),
                              }))
                            }
                            className="w-14 px-2 py-1.5 border border-gray-200 dark:border-white/10 dark:bg-slate-800/80 rounded-lg text-center text-gray-900 dark:text-white"
                          />
                        </div>
                        <div className="flex flex-col items-center">
                          <span className="text-xs text-gray-500 dark:text-slate-400">Remplacement</span>
                          <input
                            type="number"
                            min={0}
                            value={qtyRem}
                            onChange={(e) =>
                              setValidatingUsageRemplacement((u) => ({
                                ...u,
                                [item.id]: Math.max(0, parseInt(e.target.value, 10) || 0),
                              }))
                            }
                            className="w-14 px-2 py-1.5 border border-gray-200 dark:border-white/10 dark:bg-slate-800/80 rounded-lg text-center text-gray-900 dark:text-white"
                          />
                        </div>
                        {total > 0 && (
                          <span className="text-xs text-gray-400 dark:text-slate-500 pt-2">
                            Total: {total}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 dark:text-slate-500 uppercase mb-2">
              Commentaire technique
            </label>
            <textarea
              value={validatingNotes}
              onChange={(e) => setValidatingNotes(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 dark:border-white/10 dark:bg-slate-800/80 rounded-xl h-24 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/40 outline-none transition-all resize-none"
              placeholder="RAS, tout s'est bien passé..."
            />
          </div>
          <button
            type="submit"
            disabled={validatingSubmitting || (validatingIsEditing ? !onModifyJob : !onValidateJob)}
            className="w-full py-3.5 bg-green-600 dark:bg-green-500 text-white rounded-xl font-semibold text-[15px] tracking-tight hover:bg-green-700 dark:hover:bg-green-600 active:scale-[0.99] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {validatingSubmitting ? "Envoi…" : validatingIsEditing ? "Mettre à jour le rapport" : "Valider et clôturer"}
          </button>
        </form>
      </Modal>

      <Modal
        isOpen={isAddJobModalOpen}
        onClose={handleCloseAddJobModal}
        size="xl"
      >
        <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-2xl bg-blue-500/15 dark:bg-blue-400/15 text-blue-600 dark:text-blue-400">
            <Briefcase size={22} strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{t("admin.newJobTitle")}</h3>
            <p className="text-sm md:text-[15px] text-gray-500 dark:text-slate-400 mt-0.5 md:mt-1">Créer un dossier et optionnellement l’assigner à une équipe</p>
          </div>
        </div>
        <form onSubmit={handleAddJob} className="space-y-4 md:space-y-5 rounded-2xl md:rounded-3xl bg-slate-50/80 dark:bg-slate-900/40 p-4 md:p-6">
          <section className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 shadow-sm hover:shadow-md transition-shadow overflow-visible">
            <div className="px-5 md:px-6 py-4 md:py-5 border-b border-slate-100 dark:border-white/10">
              <span className="font-semibold text-gray-900 dark:text-white">Contexte</span>
            </div>
            <div className="px-5 md:px-6 pb-5 md:pb-6 pt-4 md:pt-5 space-y-4 md:space-y-5">
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 mb-1.5">{t("admin.businessBringer")}</label>
                    <select
                      value={newJob.partnerId ?? ""}
                      onChange={(e) => setNewJob({ ...newJob, partnerId: e.target.value })}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 outline-none transition-all"
                    >
                      <option value="">{t("admin.noPartnerInternal")}</option>
                      {partners.map((p: User) => (
                        <option key={p.id} value={p.id}>
                          {p.company ?? p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {stockRegions.length > 0 && (
                    <div>
                      <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 mb-1.5">Région (maison de stock)</label>
                      <select
                        value={newJob.stockRegionId || selectedRegionId || ""}
                        onChange={(e) => {
                          const newRegionId = e.target.value;
                          const instId = assignImmediately ? immediateAssignment.installerId : null;
                          const instRegionId = instId ? installers.find((i: User) => i.id === instId)?.preferences?.defaultStockRegionId : null;
                          const mustClearPoseur = instId && instRegionId !== newRegionId;
                          setNewJob({ ...newJob, stockRegionId: newRegionId });
                          if (mustClearPoseur) setImmediateAssignment((prev) => ({ ...prev, installerId: "" }));
                        }}
                        className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 outline-none transition-all"
                      >
                        <option value="">Sélectionner une région</option>
                        {stockRegions.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 shadow-sm hover:shadow-md transition-shadow overflow-visible">
            <div className="px-5 md:px-6 py-4 md:py-5 border-b border-slate-100 dark:border-white/10">
              <span className="font-semibold text-gray-900 dark:text-white">Technique</span>
            </div>
            <div className="px-5 md:px-6 pb-5 md:pb-6 pt-4 md:pt-5 space-y-4 md:space-y-5">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2">Type de dossier <span className="text-red-500">*</span></p>
                    <div
                      className="relative"
                      onBlur={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                          setIsAddJobDossierTypeOpen(false);
                        }
                      }}
                    >
                      <button
                        type="button"
                        aria-haspopup="listbox"
                        aria-expanded={isAddJobDossierTypeOpen}
                        onClick={() => setIsAddJobDossierTypeOpen((open) => !open)}
                        className={`group flex w-full items-center justify-between gap-4 rounded-3xl border px-4 py-4 text-left shadow-lg shadow-blue-100/50 outline-none transition-all duration-200 dark:shadow-black/20 ${
                          selectedAddJobDossierType
                            ? "border-blue-300/80 bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:border-blue-400/30 dark:from-slate-800 dark:via-slate-900 dark:to-blue-950/30"
                            : "border-dashed border-slate-300 bg-white dark:border-white/15 dark:bg-slate-800/80"
                        } hover:-translate-y-0.5 hover:border-blue-400/80 hover:shadow-xl focus:ring-4 focus:ring-blue-500/15`}
                      >
                        <span className="flex min-w-0 items-center gap-3">
                          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-md shadow-blue-500/25">
                            <Briefcase size={19} />
                          </span>
                          <span className="min-w-0">
                            <span className={`block truncate text-[15px] font-bold ${selectedAddJobDossierType ? "text-gray-950 dark:text-white" : "text-gray-500 dark:text-slate-400"}`}>
                              {selectedAddJobDossierType?.label ?? "Choisir le type de dossier"}
                            </span>
                            <span className="mt-0.5 block text-xs font-medium text-blue-600/80 dark:text-blue-300/80">
                              {selectedAddJobDossierType
                                ? usesRenovationQuestions(newJob.dossierType)
                                  ? "Questionnaire simplifié nom / prénom / notes"
                                  : "Questionnaire technique complet"
                                : "Sélection obligatoire avant création"}
                            </span>
                          </span>
                        </span>
                        <ChevronDown className={`h-5 w-5 shrink-0 text-blue-500 transition-transform duration-200 dark:text-blue-300 ${isAddJobDossierTypeOpen ? "rotate-180" : ""}`} />
                      </button>
                      {isAddJobDossierTypeOpen && (
                        <div
                          role="listbox"
                          className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-3xl border border-blue-100 bg-white p-2 shadow-2xl shadow-slate-900/15 ring-1 ring-black/5 dark:border-white/10 dark:bg-slate-900 dark:shadow-black/40"
                        >
                          {DOSSIER_TYPE_OPTIONS.map((option) => {
                            const isSelected = newJob.dossierType === option.value;
                            return (
                              <button
                                key={option.value}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setNewJob({ ...newJob, dossierType: option.value });
                                  setIsAddJobDossierTypeOpen(false);
                                }}
                                className={`flex w-full items-center justify-between gap-3 rounded-2xl px-3 py-3 text-left transition-all ${
                                  isSelected
                                    ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                                    : "text-gray-700 hover:bg-blue-50 hover:text-blue-700 dark:text-slate-200 dark:hover:bg-blue-500/10 dark:hover:text-blue-200"
                                }`}
                              >
                                <span>
                                  <span className="block text-sm font-semibold">{option.label}</span>
                                  <span className={`block text-xs ${isSelected ? "text-blue-100" : "text-gray-400 dark:text-slate-500"}`}>
                                    {usesRenovationQuestions(option.value)
                                      ? "Questions type rénovation"
                                      : "Questions techniques"}
                                  </span>
                                </span>
                                {isSelected && <CheckCircle size={18} className="shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  {usesTechnicalQuestions(newJob.dossierType) && (
                  <div className="grid grid-cols-3 gap-3">
              <div className="col-span-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800/50 p-3 transition-all duration-300">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Surface</span>
                  <span className="text-sm font-semibold text-blue-600 dark:text-blue-300">{newJob.surface || "0"} m²</span>
                </div>
                <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
                  <input
                    type="range"
                    min={0}
                    max={2000}
                    step={5}
                    value={Number(newJob.surface) || 0}
                    onChange={(e) => setNewJob({ ...newJob, surface: e.target.value })}
                    className="w-full accent-blue-500"
                  />
                  <input
                    type="number"
                    min={0}
                    required={
                      newJob.dossierType === "deshumidificateur" ||
                      newJob.dossierType === "vmc_agricoles" ||
                      newJob.dossierType === "th_122"
                    }
                    value={newJob.surface}
                    onChange={(e) => setNewJob({ ...newJob, surface: e.target.value })}
                    className="w-24 px-3 py-2 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              {showLedTechnicalFields(newJob.dossierType) && (
                <>
                  <input
                    type="text"
                    placeholder="Haut. (m)"
                    value={newJob.ceilingHeight}
                    onChange={(e) => setNewJob({ ...newJob, ceilingHeight: e.target.value })}
                    className="w-full px-4 py-3.5 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                  />
                  <div className="col-span-2 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setNewJob({ ...newJob, lightingType: "visite technique" })}
                      className={`rounded-xl px-3 py-3 text-sm font-medium transition-all duration-300 ${
                        newJob.lightingType === "visite technique"
                          ? "bg-blue-500 text-white shadow-md shadow-blue-500/25"
                          : "bg-white dark:bg-slate-800/60 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-500/10"
                      }`}
                    >
                      {t("admin.technicalVisit")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewJob({ ...newJob, lightingType: "pose" })}
                      className={`rounded-xl px-3 py-3 text-sm font-medium transition-all duration-300 ${
                        newJob.lightingType === "pose"
                          ? "bg-blue-500 text-white shadow-md shadow-blue-500/25"
                          : "bg-white dark:bg-slate-800/60 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-500/10"
                      }`}
                    >
                      {t("admin.installation")}
                    </button>
                  </div>
                  <div className="col-span-3 flex gap-2 items-center">
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="LED estimées"
                      value={newJob.estimatedLedCount}
                      onChange={(e) => setNewJob({ ...newJob, estimatedLedCount: e.target.value })}
                      className="flex-1 min-w-0 px-4 py-3.5 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => handleAiSuggestJob("led_estimate", "create")}
                      disabled={!!aiSuggestLoading}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-blue-500/15 dark:bg-blue-400/15 text-blue-600 dark:text-blue-400 hover:bg-blue-500/25 dark:hover:bg-blue-400/25 transition-colors disabled:opacity-50 text-sm font-medium"
                    >
                      {aiSuggestLoading === "create_led" ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                      Estimer
                    </button>
                  </div>
                </>
              )}
              {showDehumidifierTechnicalFields(newJob.dossierType) && (
                <>
                  <div className="col-span-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800/50 p-3 transition-all duration-300">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Nombre de déshumidificateurs</span>
                      <span className="text-sm font-semibold text-blue-600 dark:text-blue-300">{newJob.dehumidifierCount || "0"}</span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
                      <input
                        type="range"
                        min={0}
                        max={50}
                        step={1}
                        required
                        value={Number(newJob.dehumidifierCount) || 0}
                        onChange={(e) => setNewJob({ ...newJob, dehumidifierCount: e.target.value })}
                        className="w-full accent-blue-500"
                      />
                      <input
                        type="number"
                        min={0}
                        max={50}
                        required
                        value={newJob.dehumidifierCount}
                        onChange={(e) => setNewJob({ ...newJob, dehumidifierCount: e.target.value })}
                        className="w-24 px-3 py-2 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                  <div className="col-span-3 flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
                      <input
                        type="checkbox"
                        checked={newJob.needsCounter}
                        onChange={(e) => setNewJob({ ...newJob, needsCounter: e.target.checked })}
                      />
                      Compteur
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
                      <input
                        type="checkbox"
                        checked={newJob.needsGenerator}
                        onChange={(e) => setNewJob({ ...newJob, needsGenerator: e.target.checked })}
                      />
                      Générateur
                    </label>
                  </div>
                </>
              )}
              {showTimerTechnicalFields(newJob.dossierType) && (
                <div className="col-span-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800/50 p-3 transition-all duration-300">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Nombre de minuteries</span>
                    <span className="text-sm font-semibold text-blue-600 dark:text-blue-300">{newJob.timerCount || "0"}</span>
                  </div>
                  <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      required
                      value={Number(newJob.timerCount) || 0}
                      onChange={(e) => setNewJob({ ...newJob, timerCount: e.target.value })}
                      className="w-full accent-blue-500"
                    />
                    <input
                      type="number"
                      min={0}
                      max={100}
                      required
                      value={newJob.timerCount}
                      onChange={(e) => setNewJob({ ...newJob, timerCount: e.target.value })}
                      className="w-24 px-3 py-2 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              )}
            </div>
                  )}
          </div>
          </section>

          <section className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
            <div className="px-5 md:px-6 py-4 md:py-5 border-b border-slate-100 dark:border-white/10">
              <span className="font-semibold text-gray-900 dark:text-white">Client</span>
            </div>
            <div className="px-5 md:px-6 pb-5 md:pb-6 pt-4 md:pt-5 space-y-3 md:space-y-4">
                  {usesRenovationQuestions(newJob.dossierType) ? (
                    <>
                      <input
                        required
                        type="text"
                        placeholder="Quel est le nom du client ?"
                        value={newJob.clientLastName}
                        onChange={(e) => setNewJob({ ...newJob, clientLastName: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 outline-none"
                      />
                      <input
                        required
                        type="text"
                        placeholder="Quel est le prénom du client ?"
                        value={newJob.clientFirstName}
                        onChange={(e) => setNewJob({ ...newJob, clientFirstName: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 outline-none"
                      />
                    </>
                  ) : (
                    <>
                      <input
                        required={!isPacDossierType(newJob.dossierType)}
                        type="text"
                        placeholder="Nom client"
                        value={newJob.clientName}
                        onChange={(e) => setNewJob({ ...newJob, clientName: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 outline-none"
                      />
                      {isPacDossierType(newJob.dossierType) && (
                        <>
                          <input
                            type="text"
                            placeholder="Quel est le nom du client ?"
                            value={newJob.clientLastName}
                            onChange={(e) => setNewJob({ ...newJob, clientLastName: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 outline-none"
                          />
                          <input
                            type="text"
                            placeholder="Quel est le prénom du client ?"
                            value={newJob.clientFirstName}
                            onChange={(e) => setNewJob({ ...newJob, clientFirstName: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 outline-none"
                          />
                        </>
                      )}
                    </>
                  )}
                  <input
                    required
                    type="tel"
                    placeholder={
                      usesRenovationQuestions(newJob.dossierType) || isPacDossierType(newJob.dossierType)
                        ? "Quel est le téléphone du client ?"
                        : "Téléphone"
                    }
                    value={newJob.clientPhone}
                    onChange={(e) => setNewJob({ ...newJob, clientPhone: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 outline-none"
                  />
                  <AddressInput ref={addJobAddressRef} />
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200/80 dark:border-white/10 bg-gray-50/70 dark:bg-white/[0.04] p-4 md:p-5 space-y-3 md:space-y-4">
            <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">{t("admin.notesLabel")}</p>
            <textarea
              placeholder={
                usesRenovationQuestions(newJob.dossierType) || isPacDossierType(newJob.dossierType)
                  ? "Quelles notes souhaitez-vous ajouter ?"
                  : "Instructions spéciales..."
              }
              value={newJob.notes}
              onChange={(e) => setNewJob({ ...newJob, notes: e.target.value })}
              className="w-full px-4 py-3.5 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/30 outline-none transition-all min-h-[88px] resize-y"
            />
            <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => handleAiSuggestJob("notes", "create")}
                disabled={!!aiSuggestLoading}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-blue-500/15 dark:bg-blue-400/15 text-blue-600 dark:text-blue-400 hover:bg-blue-500/25 dark:hover:bg-blue-400/25 transition-colors disabled:opacity-50 text-sm font-medium"
              >
                {aiSuggestLoading === "create_notes" ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Sparkles size={16} />
                )}
                Suggérer avec l&apos;IA
              </button>
              {aiSuggestError && (
                <span className="text-[13px] text-red-500 dark:text-red-400">{aiSuggestError}</span>
              )}
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200/80 dark:border-white/10 bg-gray-50/70 dark:bg-white/[0.04] p-4 md:p-5 space-y-4 md:space-y-5">
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={assignImmediately}
                onChange={(e) => setAssignImmediately(e.target.checked)}
                className="w-5 h-5 text-blue-500 rounded border-gray-300 dark:border-white/20 focus:ring-2 focus:ring-blue-500/30"
              />
              <span className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                Assigner immédiatement ?
              </span>
            </label>
            {assignImmediately && (
              <div className="space-y-3 animate-in fade-in slide-in-from-top-2 pt-2 border-t border-gray-200/80 dark:border-white/10">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="date"
                    lang={getCurrentLocale() === "fr" ? "fr-FR" : getCurrentLocale() === "es" ? "es-ES" : "en-US"}
                    required
                    value={immediateAssignment.date}
                    onChange={(e) =>
                      setImmediateAssignment({
                        ...immediateAssignment,
                        date: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3.5 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                  />
                  <input
                    type="time"
                    required
                    value={immediateAssignment.time}
                    onChange={(e) =>
                      setImmediateAssignment({
                        ...immediateAssignment,
                        time: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3.5 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-gray-500 dark:text-slate-400 tracking-tight mb-1.5">
                    {t("admin.assignInstaller")}
                  </label>
                  <select
                    value={immediateAssignment.installerId ?? ""}
                    onChange={(e) => setImmediateAssignment({ ...immediateAssignment, installerId: e.target.value })}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 rounded-xl text-[15px] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 outline-none transition-all"
                  >
                    <option value="">{t("admin.assignToNobody")}</option>
                    {(newJob.stockRegionId || selectedRegionId
                      ? installers.filter((i: User) => i.preferences?.defaultStockRegionId === (newJob.stockRegionId || selectedRegionId))
                      : installers.filter((i: User) => !!i.preferences?.defaultStockRegionId)
                    ).map((i: User) => (
                      <option key={i.id} value={i.id}>
                        {i.name}
                      </option>
                    ))}
                  </select>
                </div>
                {((newJob.stockRegionId || selectedRegionId) && installers.filter((i: User) => i.preferences?.defaultStockRegionId === (newJob.stockRegionId || selectedRegionId)).length === 0) && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">Aucun poseur dans cette région. Choisissez une autre région ou ajoutez un poseur dans Gestion Accès.</p>
                )}
              </div>
            )}
          </section>

          <button
            type="submit"
            disabled={addJobSubmitting || !isNewJobCoreComplete}
            className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-2xl font-semibold text-[15px] shadow-lg shadow-blue-500/25 hover:shadow-blue-500/30 transition-all flex items-center justify-center gap-2 active:scale-[0.99]"
          >
            {addJobSubmitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Envoi…
              </>
            ) : assignImmediately ? (
              "Valider et envoyer l'ordre"
            ) : (
              "Enregistrer le dossier"
            )}
          </button>
        </form>
      </Modal>
    </div>
  );
};
